import React from "react";
import type { Theme } from "../lib/theme";
import type { SiteLogistics, SiteItemType, SiteLineType, SiteIcon, SiteLine, SiteArea, SiteCircle, SiteText, SitePlan, SiteDimension, BlockKind } from "../lib/logistics";
import { SITE_ICON_CONFIG, SITE_LINE_CONFIG, SITE_AREA_CONFIG, SCALE_PRESETS } from "../lib/logistics";
import SiteMap from "../components/SiteMap";
import MapLabel from "../components/MapLabel";
import * as Icon from "../components/OEIcons";
import {
  plansOf, activePlan, withPlan, duplicatePlan, addPlan, renamePlan, setPhase,
  removePlan, reorderPlans,
} from "../lib/logisticsPlans";
import { pickSaveTarget, canvasToBlob, jpegsToPdf } from "../lib/exportImage";
import { legendRows, rowText } from "../lib/logisticsLegend";
import { FIT, zoomAt, panBy, isFitted, toCss, formatZoom, wheelFactor, isDoublePress,
  type Press, type ViewTransform } from "../lib/planView";
import { readableOn, inkOn } from "../lib/contrast";
import {
  footprintFt, footprintPx, rectAround, centreOf, resizeRect, handlePoint,
  handleCursor, HANDLES, formatFt, describeFootprint, clampFootprint,
  SIZE_PRESETS, matchesPreset, MIN_FT, MAX_FT, type Handle,
} from "../lib/iconFootprint";
import { leaderLine, edgePoint, clampOffset, isOffset, unrotateOffset } from "../lib/leader";
import { lockedById, setLock, countLocked, unlockAll,
  clearUnlocked } from "../lib/logisticsLock";
import { phaseOf, phaseRange, phaseLength, phaseIssue, sequenceSummary,
  phaseFromSchedule, weeksSpan } from "../lib/logisticsPhases";
import { niceBar, barLabel, divisionOffsets, barCells, barTicks } from "../lib/scaleBar";
import { ticksFor, tickLabel, describeSheet } from "../lib/ruler";
import { parseFeet, FEET_HINT } from "../lib/parseFeet";
import { SHEET_TYPE, legendLayout, legendSlot, wrapText, clampSize,
  LEGEND_COL_IN, widthForHeight, blockSelId, isBlockSel, unitsPerInch, pt } from "../lib/sheetBlocks";
import { dimGeometry, extensionLines, dimensionText } from "../lib/dimensions";
import { blockRows, shortDate, isShown, toggleField, BLOCK_FIELDS,
  type BlockField, type TitleBlock } from "../lib/titleBlock";
import { bringToFront, sendToBack, bringForward, sendBackward, canRaise, canLower } from "../lib/zorder";
import { rotatePoint, rotatePoints, angleFromPointer, snapAngle, normalizeAngle, aspectOf,
  handleAt, centroid, formatAngle, isRotated, scalePoints, boundsOf, cornerPoint,
  oppositeCorner, type Corner } from "../lib/rotate";
import { mapScaleNote } from "../lib/mapScale";
import { offsetPath, offsetLabel } from "../lib/offset";
import type { ShowToast } from "../types";
import { normalizeScale, feetPerPixel, pctDistance, calibrate, presetToFeetPerPct,
  sheetInchesFor } from "../lib/planScale";
import { pathFromPoints, handlesOf, withHandle, withLegacyCurve, toggleVertexCurve, setAllCurve, curvedCount,
  spanMidpoints, nearestSegment, insertVertexAfter, removeVertex, canRemoveVertex } from "../lib/curves";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

// Stroke patterns offered in the draw-style bar. The label is the tooltip and
// the accessible name; the swatch itself carries the meaning.
const LINE_DASH_OPTIONS = [
  { dash: "",          label: "Solid" },
  { dash: "8,5",       label: "Dashed" },
  { dash: "2,5",       label: "Dotted" },
  { dash: "12,4,2,4",  label: "Dash-dot" },
  { dash: "18,5",      label: "Long dash" },
] as const;

// Stroke-pattern picker. Each option is drawn in the line's own colour and
// weight, so the selected swatch is also the preview -- the two used to be
// separate controls sitting next to each other.
function DashSwatches({options,value,onChange,color,width,swatchW,T}:{
  options:readonly {dash:string;label:string}[]; value:string;
  onChange:(dash:string)=>void; color:string; width:number; swatchW:number; T:Theme;
}){
  const h=Math.max(12,width+4);
  return (
    <div style={{display:"flex",gap:3,flexWrap:"wrap" as const}}>
      {options.map(opt=>{
        const on=value===opt.dash;
        return (
          <button key={opt.label} title={opt.label} aria-label={opt.label} aria-pressed={on}
            onClick={()=>onChange(opt.dash)}
            style={{display:"flex",alignItems:"center",justifyContent:"center",
              padding:"3px 5px",borderRadius:3,cursor:"pointer",
              background:on?T.bgAccentFaint:T.bgInput,
              border:`1px solid ${on?T.borderAccent:T.borderSecondary}`}}>
            <svg width={swatchW} height={h} style={{display:"block"}}>
              <polyline points={`1,${h/2} ${swatchW-1},${h/2}`} fill="none"
                stroke={color} strokeWidth={width} strokeDasharray={opt.dash} strokeLinecap="round"/>
            </svg>
          </button>
        );
      })}
    </div>
  );
}

interface LogisticsTabProps {
  T: Theme;
  siteLogistics: SiteLogistics;
  setSiteLogistics: React.Dispatch<React.SetStateAction<SiteLogistics>>;
  logisticsTool: string;
  setLogisticsTool: React.Dispatch<React.SetStateAction<string>>;
  selectedLogId: string | null;
  setSelectedLogId: React.Dispatch<React.SetStateAction<string | null>>;
  lineInProgress: {x: number; y: number}[];
  setLineInProgress: React.Dispatch<React.SetStateAction<{x: number; y: number}[]>>;
  linePreview: {x: number; y: number} | null;
  setLinePreview: React.Dispatch<React.SetStateAction<{x: number; y: number} | null>>;
  logSvgRef: React.RefObject<SVGSVGElement>;
  logDragRef: React.MutableRefObject<{kind: "icon" | "area" | "circle" | "text" | "line" | "dimension"; id: string; ox: number; oy: number; startX: number; startY: number} | null>;
  logVertexDragRef: React.MutableRefObject<{kind: "line" | "area"; id: string; vertIdx: number} | null>;
  logResizeRef: React.MutableRefObject<{id: string; handle: Handle} | null>;
  logRotateRef: React.MutableRefObject<{kind: "icon"|"text"|"area"|"line"; id: string;
    start: number; base: number; points?: {x: number; y: number}[]} | null>;
  logLabelRef: React.MutableRefObject<{id: string; ox: number; oy: number} | null>;
  /** Optional: the tab is rendered without one in some harnesses. */
  showToast?: ShowToast;
  areaInProgress: {x: number; y: number}[];
  setAreaInProgress: React.Dispatch<React.SetStateAction<{x: number; y: number}[]>>;
  areaPreview: {x: number; y: number} | null;
  setAreaPreview: React.Dispatch<React.SetStateAction<{x: number; y: number} | null>>;
  circleCenter: {x: number; y: number} | null;
  setCircleCenter: React.Dispatch<React.SetStateAction<{x: number; y: number} | null>>;
  circlePreviewR: number;
  setCirclePreviewR: React.Dispatch<React.SetStateAction<number>>;
  calibPoints: {x: number; y: number}[];
  setCalibPoints: React.Dispatch<React.SetStateAction<{x: number; y: number}[]>>;
  calibInput: string;
  setCalibInput: React.Dispatch<React.SetStateAction<string>>;
  logDrawStyle: {strokeColor?: string; strokeWidth?: number; strokeDash?: string};
  setLogDrawStyle: React.Dispatch<React.SetStateAction<{strokeColor?: string; strokeWidth?: number; strokeDash?: string}>>;
  logPanelCollapsed: {[k: string]: boolean};
  setLogPanelCollapsed: React.Dispatch<React.SetStateAction<{[k: string]: boolean}>>;
  logSvgDims: {w: number; h: number};
  logisticsView: "plan" | "map";
  setLogisticsView: React.Dispatch<React.SetStateAction<"plan" | "map">>;
  logisticsSnapshot: () => void;
  uid: () => string;
  address: string;
  geoQuery: string;
  mapLocLabel: string;
  setMapLocLabel: React.Dispatch<React.SetStateAction<string>>;
  mapViewMode: "aerial" | "street";
  setMapViewMode: React.Dispatch<React.SetStateAction<"aerial" | "street">>;
  mapLocked: boolean;
  setMapLocked: React.Dispatch<React.SetStateAction<boolean>>;
  /** The job's own name, for the title block. The sheet says what project it
   *  belongs to without anyone typing it a second time. */
  projectName?: string;
  /** The gantt, for borrowing a phase's dates. Optional: the tab is rendered
   *  without one in some harnesses, and a logistics plan has never needed a
   *  schedule to exist. */
  schedule?: {
    projectStartDate: string;
    phases: Array<{ id: string; name: string; startWeek: number; durationWeeks: number;
      segments?: Array<{ startWeek: number; durationWeeks: number }> }>;
  };
}

/**
 * A labelled row in the properties panel.
 *
 * Declared here rather than inside the render. A component defined during
 * render is a new type on every render, so React throws the old subtree away
 * and builds a fresh one -- which took the focus out of whatever field you
 * were typing in, after every single keystroke, and would have killed a slider
 * drag on its first pixel.
 */
function LabelRow({label,children,T}:{label:string;children:React.ReactNode;T:Theme}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
      <span style={{fontSize:10,color:T.textWeak,width:44,flexShrink:0,textAlign:"right" as const}}>{label}</span>
      {children}
    </div>
  );
}

/** A labelled row in the sheet panel. Module scope for the same reason as
 *  LabelRow: declared inside the render it would be a new component type on
 *  every keystroke, and the field being typed into would lose focus. */
function SheetRow({label,children,T}:{label:string;children:React.ReactNode;T:Theme}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:5}}>
      <span style={{fontSize:10,color:T.textWeak,width:52,flexShrink:0,textAlign:"right" as const}}>{label}</span>
      {children}
    </div>
  );
}

/**
 * Asking how far, for an offset.
 *
 * Hoisted out of the tab rather than written inline, because a component
 * declared during a render is a new component every render: React unmounts
 * the old one, mounts the new, and the field loses focus after the first
 * keystroke.
 */
/**
 * The name, asked for at the moment the shape lands.
 *
 * A custom area arrived called "Custom Area" and the legend said so, which
 * meant the first thing anybody did after drawing one was open it and rename
 * it. Asking here costs the same keystrokes and skips the detour -- and
 * because the shape is already committed, dismissing this leaves a drawn
 * area rather than losing the outline.
 */
function NamePrompt({what,value,T,onValue,onApply,onCancel}:{
  what:string; value:string; T:Theme;
  onValue:(v:string)=>void; onApply:()=>void; onCancel:()=>void;
}){
  return(
    <div style={{position:"absolute",left:"50%",top:18,transform:"translateX(-50%)",zIndex:7,
      background:T.bgPanel,border:`1px solid ${T.borderAccent}`,borderRadius:6,
      boxShadow:"0 8px 24px rgba(0,0,0,.45)",padding:"10px 12px",fontFamily:FONT,
      display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:11,color:T.textSecondary,whiteSpace:"nowrap" as const}}>
        Name this {what}
      </span>
      <input autoFocus value={value} placeholder="Laydown yard, crane pad, staging..."
        onChange={e=>onValue(e.target.value)}
        onKeyDown={e=>{
          if(e.key==="Enter"){e.preventDefault();onApply();}
          if(e.key==="Escape"){e.preventDefault();onCancel();}
        }}
        style={{width:240,fontSize:11,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
          color:T.textPrimary,borderRadius:3,padding:"4px 7px",fontFamily:FONT,outline:"none"}}/>
      <button onClick={onApply}
        style={{fontSize:11,padding:"4px 11px",background:T.borderAccent,border:"none",
          color:T.textOnGold,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,
          whiteSpace:"nowrap" as const}}>
        Name it
      </button>
      <button onClick={onCancel}
        style={{fontSize:11,padding:"4px 8px",background:"none",border:"none",
          color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Skip</button>
    </div>
  );
}

function OffsetPrompt({name,closed,value,side,T,onValue,onSide,onApply,onCancel}:{
  name:string; closed:boolean; value:string; side:-1|1; T:Theme;
  onValue:(v:string)=>void; onSide:(s:-1|1)=>void; onApply:()=>void; onCancel:()=>void;
}){
  const sides:Array<[-1|1,string]> = closed
    ? [[-1,"inside"],[1,"outside"]]
    : [[-1,"one side"],[1,"the other"]];
  return(
    <div style={{position:"absolute",left:"50%",top:18,transform:"translateX(-50%)",zIndex:7,
      background:T.bgPanel,border:`1px solid ${T.borderAccent}`,borderRadius:6,
      boxShadow:"0 8px 24px rgba(0,0,0,.45)",padding:"10px 12px",fontFamily:FONT,
      display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:11,color:T.textSecondary,whiteSpace:"nowrap" as const}}>
        Offset {name||"this"} by
      </span>
      <input autoFocus value={value} placeholder={FEET_HINT}
        onChange={e=>onValue(e.target.value)}
        onKeyDown={e=>{
          if(e.key==="Enter"){e.preventDefault();onApply();}
          if(e.key==="Escape"){e.preventDefault();onCancel();}
        }}
        style={{width:120,fontSize:11,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
          color:T.textPrimary,borderRadius:3,padding:"4px 7px",fontFamily:FONT,outline:"none"}}/>
      <div style={{display:"flex",border:`1px solid ${T.borderSecondary}`,borderRadius:3,overflow:"hidden"}}>
        {sides.map(([v,lbl])=>(
          <button key={lbl} onClick={()=>onSide(v)}
            style={{padding:"3px 9px",border:"none",cursor:"pointer",fontSize:11,fontFamily:FONT,
              fontWeight:side===v?700:400,
              background:side===v?T.borderAccent:"transparent",
              color:side===v?T.textOnGold:T.textSecondary}}>{lbl}</button>
        ))}
      </div>
      <button onClick={onApply}
        style={{fontSize:11,padding:"4px 11px",background:T.borderAccent,border:"none",
          color:T.textOnGold,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
        Offset
      </button>
      <button onClick={onCancel}
        style={{fontSize:11,padding:"4px 8px",background:"none",border:"none",
          color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
    </div>
  );
}

/**
 * A distance drawn around something, and whether it is being drawn.
 *
 * The same control for an item's radius and a run's clearance, because it is
 * the same question: how far does this thing reach, or have to be kept clear
 * of. The switch matters as much as the number -- a swing radius is wanted
 * while the crane is being placed and in the way once it is, and deleting it
 * to get it off the sheet loses the measurement.
 */
function OffsetRow({label,hint,feet,hidden,fpp,T,inputS,onFeet,onToggle,onSnapshot,
  side,onCycleSide}:{
  label:string; hint:string; feet?:number; hidden:boolean; fpp:number|null;
  T:Theme; inputS:React.CSSProperties;
  onFeet:(v:number|undefined)=>void; onToggle:()=>void; onSnapshot:()=>void;
  /** Only for something with sides. A radius has none. */
  side?:"both"|"left"|"right"; onCycleSide?:()=>void;
}){
  const sideLabel=side==="left"?"One side":side==="right"?"The other":"Both sides";
  const name=(
    <span style={{fontSize:10,color:T.textWeak,width:44,flexShrink:0,textAlign:"right" as const}}>{label}</span>
  );
  if(!fpp){
    return(
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
        {name}
        <span style={{fontSize:10,color:T.textWeak,opacity:.8}}>Set a drawing scale to use this</span>
      </div>
    );
  }
  const on=!hidden&&!!feet;
  return(
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}} title={hint}>
      {name}
      <input type="number" min={0} step={5} value={feet??""} placeholder="0"
        onChange={e=>{const n=parseFloat(e.target.value);onFeet(Number.isFinite(n)&&n>0?n:undefined);}}
        onFocus={()=>onSnapshot()}
        style={{...inputS,flex:"none",width:58,textAlign:"right" as const}}/>
      <span style={{fontSize:10,color:T.textWeak}}>ft</span>
      <div style={{flex:1}}/>
      {onCycleSide&&(
        <button onClick={()=>{if(!feet)return;onSnapshot();onCycleSide();}}
          title="Both sides, one side, or the other. Which is which is easier to see than to say."
          style={{background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,
            color:feet?T.textMuted:T.textWeak,cursor:feet?"pointer":"default",
            fontSize:10,fontFamily:FONT,padding:"1px 6px",whiteSpace:"nowrap" as const,
            opacity:feet?1:.5}}>
          {sideLabel}
        </button>
      )}
      <ShowToggle T={T} on={on} title={on?`Hide the ${label.toLowerCase()}`:`Draw the ${label.toLowerCase()}`}
        onClick={()=>{if(!feet)return;onSnapshot();onToggle();}}/>
    </div>
  );
}

/**
 * The switch beside a line of the title block.
 *
 * An eye rather than a checkbox, because what it controls is whether the line
 * is printed, not whether it is true: the project still has an address when
 * the block does not say so.
 */
function ShowToggle({on,onClick,title,T}:{on:boolean;onClick:()=>void;title:string;T:Theme}){
  return(
    <button onClick={onClick} title={title} aria-pressed={on} aria-label={title}
      style={{background:"none",border:"none",cursor:"pointer",padding:"0 2px",lineHeight:1,
        fontSize:11,fontFamily:FONT,flexShrink:0,
        color:on?T.borderAccent:T.textWeak,opacity:on?.9:.45}}>
      {on?"◉":"○"}
    </button>
  );
}

/** The bearing: drag it, read it, or type it. The same control for every
 *  turnable thing, because it is the same question in each case. */
function RotationRow({deg,onSet,tint,T,inputS,onSnapshot}:{
  deg:number;onSet:(d:number)=>void;tint:string;T:Theme;
  inputS:React.CSSProperties;onSnapshot:()=>void;
}){
  const shown=Math.round(normalizeAngle(deg));
  return(
    <>
      <LabelRow label="Rotation" T={T}>
        {/* Same shape as Border and Fill above: drag for the angle, read it
            off the box, type it when you know the number you want. */}
        <input type="range" min={0} max={359} step={1} value={shown}
          onChange={e=>onSet(normalizeAngle(+e.target.value))}
          onMouseDown={onSnapshot}
          className="oe-range"
          style={{flex:1,minWidth:0,["--oe-accent" as any]:tint}}/>
        <input type="number" min={0} max={359} step={1} value={shown}
          onChange={e=>onSet(normalizeAngle(+e.target.value))}
          onFocus={onSnapshot}
          style={{...inputS,flex:"none",width:44,textAlign:"right" as const,padding:"3px 4px"}}/>
        <span style={{fontSize:10,color:T.textMuted,minWidth:8}}>&deg;</span>
      </LabelRow>
      <div style={{display:"flex",alignItems:"center",gap:6,margin:"-2px 0 7px 50px"}}>
        {[-90,90].map(d=>(
          <button key={d} onClick={()=>{onSnapshot();onSet(normalizeAngle(deg+d));}}
            title={d<0?"Turn 90° anticlockwise":"Turn 90° clockwise"}
            style={{padding:"1px 7px",fontSize:10,fontFamily:FONT,cursor:"pointer",
              borderRadius:3,background:"transparent",color:T.textMuted,
              border:`1px solid ${T.borderSecondary}`}}>
            {d<0?"↺":"↻"}
          </button>
        ))}
        {isRotated(deg)&&
          <button onClick={()=>{onSnapshot();onSet(0);}} title="Square to the sheet"
            style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",
              fontSize:10,fontFamily:FONT,padding:"0 2px"}}>&#8634;</button>}
        <span style={{fontSize:10,color:T.textWeak,lineHeight:1.5,opacity:.8}}>
          or drag the grip &middot; Shift snaps to 15{'°'}
        </span>
      </div>
    </>
  );
}

export default function LogisticsTab({
  T, siteLogistics: siteLogisticsProp, setSiteLogistics: setSiteLogisticsProp, logisticsTool, setLogisticsTool,
  selectedLogId, setSelectedLogId, lineInProgress, setLineInProgress,
  linePreview, setLinePreview, logSvgRef, logDragRef, logVertexDragRef, logResizeRef, logRotateRef, logLabelRef, showToast,
  areaInProgress, setAreaInProgress, areaPreview, setAreaPreview,
  circleCenter, setCircleCenter, circlePreviewR, setCirclePreviewR,
  calibPoints, setCalibPoints, calibInput, setCalibInput,
  logDrawStyle, setLogDrawStyle, logPanelCollapsed, setLogPanelCollapsed,
  logSvgDims, logisticsView, setLogisticsView, logisticsSnapshot, uid,
  address, geoQuery, mapLocLabel, setMapLocLabel, mapViewMode, setMapViewMode, mapLocked, setMapLocked,
  schedule, projectName,
}: LogisticsTabProps) {
const svgToPercent=(e:React.MouseEvent|MouseEvent)=>{
  const svg=logSvgRef.current; if(!svg)return{x:0,y:0};
  const r=svg.getBoundingClientRect();
  return{x:((e.clientX-r.left)/r.width)*100,y:((e.clientY-r.top)/r.height)*100};
};
const tool=logisticsTool;
const isPlaceTool=tool.startsWith("place:");
const isLineTool=tool.startsWith("line:");
const placingType=isPlaceTool?tool.slice(6) as SiteItemType:null;
const drawingLineType=isLineTool?tool.slice(5) as SiteLineType:null;

const handleSvgClick=(e:React.MouseEvent<SVGSVGElement>)=>{
  if(e.target!==logSvgRef.current&&!isPlaceTool&&!isLineTool&&!isAreaTool&&!isCircleTool&&!isCalibTool&&!isTextTool&&!isDimTool)return;
  const pos=svgToPercent(e);
  if(isPlaceTool&&placingType){
    const cfg=SITE_ICON_CONFIG[placingType];
    const newIcon:SiteIcon={id:uid(),type:placingType,x:pos.x,y:pos.y,label:cfg.label};
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,icons:[...prev.icons,newIcon]}));
  } else if(isLineTool){
    setLineInProgress(prev=>[...prev,pos]);
  } else if(isAreaTool){
    handleAreaClick(e);
  } else if(isCircleTool){
    handleCircleClick(e,pos);
  } else if(isDimTool){
    handleDimClick(pos);
  } else if(isCalibTool){
    handleCalibClick(pos);
  } else if(tool==="select"&&e.target===logSvgRef.current){
    // Clicking bare canvas lets go, which is the other half of how every
    // drawing tool deselects.
    setSelectedLogId(null);
  } else if(isTextTool){
    // Created empty and focused: a box that arrives pre-filled with "Text" is
    // a box you have to clear before you can use it.
    const id=uid();
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,texts:[...(prev.texts||[]),
      {id,x:pos.x,y:pos.y,text:"",size:13,boxed:true,align:"left" as const}]}));
    setSelectedLogId(id);
    setEditingTextId(id);
    setLogisticsTool("select");
  }
};

const handleSvgDblClick=(e:React.MouseEvent<SVGSVGElement>)=>{
  if(isLineTool&&drawingLineType&&lineInProgress.length>=2){
    e.preventDefault();
    const cfg=SITE_LINE_CONFIG[drawingLineType];
    const named=drawingLineType==="custom"?customName.trim():"";
    const newLine:SiteLine={id:uid(),lineType:drawingLineType,points:[...lineInProgress],
      label:named||cfg.label,...logDrawStyle};
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,lines:[...prev.lines,newLine]}));
    setLineInProgress([]);
    setLinePreview(null);
  } else if(isAreaTool){
    handleAreaDblClick(e);
  }
};

/** The canvas proportions, which every rotation has to be corrected by or it
 *  shears the shape instead of turning it. */
const logAspect=()=>aspectOf({
  w:(logSvgRef.current?.clientWidth||0)||logSvgDims.w,
  h:(logSvgRef.current?.clientHeight||0)||logSvgDims.h,
});

/** How far out the rotation grip sits from a shape, in percent of width. */
const ROTATE_ARM=7;

const handleSvgMouseMove=(e:React.MouseEvent<SVGSVGElement>)=>{
  const pos=svgToPercent(e);
  if(logBezierRef.current){
    const{kind,id,index,which,mirror}=logBezierRef.current;
    const list=kind==="line"?siteLogistics.lines:siteLogistics.areas;
    const shape:any=list.find((x:any)=>x.id===id);
    if(shape?.points?.[index]){
      // The handle is an offset from its own vertex, in percent, so it
      // travels with the shape rather than being pinned to the sheet.
      const v=shape.points[index];
      const off={x:pos.x-v.x,y:pos.y-v.y};
      const next=withHandle(shape.points,index,which,off,mirror);
      const put=<S extends {id:string}>(l:S[])=>l.map(sh=>sh.id!==id?sh:{...sh,points:next,curved:undefined});
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:put(prev.lines)}
        :{...prev,areas:put(prev.areas)});
    }
  } else if(logScaleRef.current){
    const{kind,id,origin,startDist,points}=logScaleRef.current;
    const next=scalePoints(points,pctDistance(origin,pos,liveCanvas)/startDist,origin);
    const put=<S extends {id:string}>(list:S[])=>list.map(sh=>
      sh.id!==id?sh:{...sh,points:next});
    setSiteLogistics(prev=>kind==="line"
      ?{...prev,lines:put(prev.lines)}
      :{...prev,areas:put(prev.areas)});
  } else if(logBlockResizeRef.current){
    const{kind,corner,startX,startY}=logBlockResizeRef.current;
    // Both axes do something, so neither gesture is a dead end: drag it
    // wider and it reflows into columns, drag it shorter and it reflows to
    // fit -- the legend has one size, worked out from whichever the hand
    // moved further.
    const dx=Math.abs(pos.x-startX), dy=Math.abs(pos.y-startY);
    const widthPx=Math.max(20,dx/100*liveCanvas.w);
    const heightPx=Math.max(20,dy/100*liveCanvas.h);
    const rowCount=legendRows(siteLogistics as any).length;
    const k=(siteLogistics.blocks||{})[kind]?.scale||1;
    const inPerUnit=unitsPerInch(liveCanvas.w,sheetInches?.w);
    const w=kind==="legend"&&dy>dx
      ?widthForHeight(rowCount,heightPx,k,inPerUnit)
      :widthPx;
    setSiteLogistics(prev=>({...prev,blocks:{...(prev.blocks||{}),
      // A corner on the left edge moves the block as well, so the edge the
      // hand is not holding stays where it is.
      [kind]:{...((prev.blocks||{})[kind]||{}),w,
        ...(corner==="nw"||corner==="sw"?{x:Math.min(pos.x,startX)}:{})}}}));
  } else if(logBlockRef.current){
    const{kind,ox,oy}=logBlockRef.current;
    setSiteLogistics(prev=>({...prev,blocks:{...(prev.blocks||{}),
      [kind]:{...((prev.blocks||{})[kind]||{}),x:pos.x-ox,y:pos.y-oy}}}));
  } else if(logDimEndRef.current){
    const{id,end}=logDimEndRef.current;
    setSiteLogistics(prev=>({...prev,dimensions:(prev.dimensions||[]).map(d=>
      d.id!==id?d:{...d,[end]:{x:pos.x,y:pos.y}})}));
  } else if(logLabelRef.current){
    // Only the label moves; the thing it names stays where it is, and the
    // leader keeps the two tied together.
    const{id,ox,oy}=logLabelRef.current;
    const off=clampOffset({x:pos.x-ox,y:pos.y-oy});
    const put=<S extends {id:string}>(list:S[])=>list.map(el=>el.id!==id?el:{...el,labelOffset:off});
    setSiteLogistics(prev=>({...prev,
      icons:put(prev.icons),lines:put(prev.lines),
      areas:put(prev.areas),circles:put(prev.circles)}));
  } else if(logRotateRef.current){
    // Shift snaps to 15°, which is what a site plan is dimensioned in.
    const{kind,id,start,base,points}=logRotateRef.current;
    const pivot=kind==="icon"||kind==="text"
      ?(()=>{const el:any=kind==="icon"
          ?siteLogistics.icons.find(i=>i.id===id)
          :(siteLogistics.texts||[]).find(t=>t.id===id);
        return el?{x:el.x,y:el.y}:null;})()
      :(points?centroid(points):null);
    if(pivot){
      const aspect=logAspect();
      const raw=base+(angleFromPointer(pivot,pos,aspect)-start);
      const deg=e.shiftKey?snapAngle(raw):normalizeAngle(raw);
      if(kind==="icon"){
        setSiteLogistics(prev=>({...prev,icons:prev.icons.map(ic=>ic.id===id?{...ic,rotation:deg}:ic)}));
      }else if(kind==="text"){
        setSiteLogistics(prev=>({...prev,texts:(prev.texts||[]).map(t=>t.id===id?{...t,rotation:deg}:t)}));
      }else if(points){
        // A polygon has no rotation of its own: the vertices are the drawing,
        // so the turn is applied to them and the shape stays a plain shape.
        const turned=rotatePoints(points,deg-base,aspect);
        const put=<S extends {id:string}>(list:S[])=>list.map(sh=>
          sh.id!==id?sh:{...sh,points:turned,rotation:deg});
        setSiteLogistics(prev=>kind==="area"
          ?{...prev,areas:put(prev.areas)}
          :{...prev,lines:put(prev.lines)});
      }
    }
  } else if(logResizeRef.current){
    // Resize works in drawn units and converts back to feet, so what the
    // corner lands on is what the item measures.
    const{id,handle}=logResizeRef.current;
    const W=(logSvgRef.current?.clientWidth||0)||logSvgDims.w;
    const H=(logSvgRef.current?.clientHeight||0)||logSvgDims.h;
    if(fpp&&W&&H){
      const ptr={x:pos.x/100*W, y:pos.y/100*H};
      setSiteLogistics(prev=>({...prev,icons:prev.icons.map(ic=>{
        if(ic.id!==id)return ic;
        const cur=footprintPx(footprintFt(ic),fpp);
        if(!cur)return ic;
        // A turned item is resized in its own frame: the pointer is brought
        // back square to the item, the corner is moved there, and the new
        // centre is turned out again. Without this the corners fight the
        // pointer the moment anything is off-axis.
        const centrePx={x:ic.x/100*W, y:ic.y/100*H};
        const deg=normalizeAngle(ic.rotation||0);
        const local=deg?rotatePoint(ptr,centrePx,-deg,1):ptr;
        const next=resizeRect(rectAround(centrePx.x,centrePx.y,cur), handle, local, MIN_FT/fpp);
        const lc=centreOf(next);
        const c=deg?rotatePoint(lc,centrePx,deg,1):lc;
        return {...ic, x:c.x/W*100, y:c.y/H*100,
          sizeFt:clampFootprint({w:next.w*fpp, h:next.h*fpp})};
      })}));
    }
  } else if(logVertexDragRef.current){
    const{kind,id,vertIdx}=logVertexDragRef.current;
    const movePt=<S extends {id:string;points:{x:number;y:number}[]}>(list:S[])=>list.map(sh=>
      sh.id!==id?sh:{...sh,points:sh.points.map((p,i)=>i===vertIdx?pos:p)});
    setSiteLogistics(prev=>kind==="line"
      ?{...prev,lines:movePt(prev.lines)}
      :{...prev,areas:movePt(prev.areas)});
  } else if(logDragRef.current){
    const{kind,id,ox,oy}=logDragRef.current;
    if(kind==="icon"){
      setSiteLogistics(prev=>({...prev,icons:prev.icons.map(ic=>ic.id===id?{...ic,x:pos.x-ox,y:pos.y-oy}:ic)}));
    }else if(kind==="text"){
      setSiteLogistics(prev=>({...prev,texts:(prev.texts||[]).map(tx=>tx.id===id?{...tx,x:pos.x-ox,y:pos.y-oy}:tx)}));
    }else if(kind==="circle"){
      setSiteLogistics(prev=>({...prev,circles:prev.circles.map(ci=>ci.id===id?{...ci,cx:pos.x-ox,cy:pos.y-oy}:ci)}));
    }else if(kind==="dimension"){
      // Both ends carry the same delta: a dimension dragged by its line is
      // being repositioned, not re-measured.
      setSiteLogistics(prev=>({...prev,dimensions:(prev.dimensions||[]).map(d=>{
        if(d.id!==id)return d;
        const dx=pos.x-ox-d.a.x, dy=pos.y-oy-d.a.y;
        return {...d,a:{x:d.a.x+dx,y:d.a.y+dy},b:{x:d.b.x+dx,y:d.b.y+dy}};
      })}));
    }else{
      // A run of points moves as a body: every vertex shifts by the same
      // delta, so the shape is carried rather than redrawn. Lines were left
      // out of this and could only be moved a vertex at a time, which is not
      // moving a fence, it is redrawing one.
      const carry=<S extends {id:string;points:{x:number;y:number}[]}>(list:S[])=>list.map(sh=>{
        if(sh.id!==id)return sh;
        const dx=pos.x-ox-sh.points[0].x, dy=pos.y-oy-sh.points[0].y;
        return {...sh,points:sh.points.map(p=>({...p,x:p.x+dx,y:p.y+dy}))};
      });
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:carry(prev.lines)}
        :{...prev,areas:carry(prev.areas)});
    }
  }
  if(isLineTool&&lineInProgress.length>0) setLinePreview(pos);
  if(isAreaTool&&areaInProgress.length>0) setAreaPreview(pos);
  if(isCircleTool&&circleCenter) setCirclePreviewR(logDist(circleCenter,pos));
  if((isCalibTool||isDimTool)&&calibPoints.length>0) setAreaPreview(pos);
};

const handleIconMouseDown=(e:React.MouseEvent,ic:SiteIcon)=>{
  e.stopPropagation();
  if(lockedById(siteLogistics,ic.id)){setSelectedLogId(ic.id);return;}
  // Dragging is ours, so the browser is told not to start one of its own:
  // left to itself it begins a text selection and answers the drag with a
  // washed-out picture of every label it caught, which reads as the drawing
  // coming apart.
  e.preventDefault();
  if(tool==="select"){
    const pos=svgToPercent(e);
    setSelectedLogId(ic.id);
    logisticsSnapshot();
    logDragRef.current={kind:"icon",id:ic.id,ox:pos.x-ic.x,oy:pos.y-ic.y,startX:pos.x,startY:pos.y};
  }
};

/** Picks up the legend, the title block or the scale bar. */
/**
 * Hands a click to whatever is underneath a block.
 *
 * A block covers part of the drawing and is opaque to the pointer as well
 * as to the eye, so a markup beneath one could not be grabbed at all --
 * which reads as a broken tool rather than as something being in the way.
 * Alt is the way past it, and it is in the block's own tooltip.
 */
const clickThroughBlock=(e:React.MouseEvent)=>{
  const here=e.currentTarget as Element;
  const under=document.elementsFromPoint(e.clientX,e.clientY)
    .find(n=>n!==here&&!n.closest("[data-block]"));
  if(!under)return;
  under.dispatchEvent(new MouseEvent("mousedown",{bubbles:true,cancelable:true,
    clientX:e.clientX,clientY:e.clientY,buttons:1,
    shiftKey:e.shiftKey,ctrlKey:e.ctrlKey,metaKey:e.metaKey}));
};

const handleBlockMouseDown=(e:React.MouseEvent,kind:BlockKind)=>{
  e.stopPropagation(); e.preventDefault();
  if(tool!=="select")return;
  if(e.altKey){clickThroughBlock(e);return;}
  setSelectedLogId(blockSelId(kind));
  const at=blockPosRef.current[kind]||(siteLogistics.blocks||{})[kind]||{x:0,y:0};
  const pos=svgToPercent(e);
  logisticsSnapshot();
  logBlockRef.current={kind,ox:pos.x-(at.x||0),oy:pos.y-(at.y||0)};
};

/** Grabs a block's corner. Width is the control: the legend works out its
 *  own columns from it, so there is no second number to disagree with. */
const handleBlockResizeMouseDown=(e:React.MouseEvent,kind:BlockKind,
  corner:"nw"|"ne"|"sw"|"se")=>{
  e.stopPropagation(); e.preventDefault();
  if(tool!=="select")return;
  setSelectedLogId(blockSelId(kind));
  logisticsSnapshot();
  const at=blockPosRef.current[kind];
  logBlockResizeRef.current={kind,corner,startX:at?.x??0,startY:at?.y??0};
};

/** What can be done to a block: put it back, or take it off the sheet. */
const openBlockMenu=(e:React.MouseEvent,kind:BlockKind)=>{
  e.preventDefault(); e.stopPropagation();
  if(tool!=="select")return;
  setSelectedLogId(blockSelId(kind));
  const name=kind==="legend"?"legend":kind==="title"?"sheet block":"scale bar";
  const put=(patch:{x?:number;y?:number;w?:number;hidden?:boolean}|null)=>{
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,blocks:{...(prev.blocks||{}),
      [kind]:patch===null?undefined:{...((prev.blocks||{})[kind]||{}),...patch}}}));
  };
  setCtxMenu({x:e.clientX,y:e.clientY,items:[
    // Cleared rather than set, so it falls back to wherever its own size
    // says it belongs today.
    {label:`Put the ${name} back`,onClick:()=>put({x:undefined,y:undefined,w:undefined,hidden:false})},
    {label:`Hide the ${name}`,danger:true,onClick:()=>{
      put({hidden:true});
      // Nothing on the sheet hints where it went, so the way back is said
      // at the moment it disappears.
      say(`${name[0].toUpperCase()}${name.slice(1)} hidden — Setup ▾ to put it back`);
    }},
  ]});
};

/**
 * Grabs a bezier handle.
 *
 * Alt breaks the mirror, which is how a deliberate kink gets made at a
 * vertex that is otherwise smooth.
 */
const handleBezierMouseDown=(e:React.MouseEvent,kind:"line"|"area",id:string,
  index:number,which:"in"|"out")=>{
  e.stopPropagation(); e.preventDefault();
  if(tool!=="select")return;
  if(lockedById(siteLogistics,id))return;
  setSelectedLogId(id);
  logisticsSnapshot();
  logBezierRef.current={kind,id,index,which,mirror:!e.altKey};
};

/** Grabs one end of a dimension, so it can be put exactly on the corner it
 *  is checking. */
const handleDimEndMouseDown=(e:React.MouseEvent,id:string,end:"a"|"b")=>{
  e.stopPropagation(); e.preventDefault();
  setSelectedLogId(id);
  if(lockedById(siteLogistics,id))return;
  logisticsSnapshot();
  logDimEndRef.current={id,end};
};

const handleVertexMouseDown=(e:React.MouseEvent,id:string,vertIdx:number,kind:"line"|"area"="line")=>{
  e.stopPropagation();
  e.preventDefault();
  setSelectedLogId(id);
  if(lockedById(siteLogistics,id))return;
  logisticsSnapshot();
  logVertexDragRef.current={kind,id,vertIdx};
};

/** Points of a shape in drawn units, so hit-testing is not skewed by a
 *  canvas that is wider than it is tall. */
const inPx=(pts:{x:number;y:number}[])=>{
  const W=(logSvgRef.current?.clientWidth||0)||logSvgDims.w||1;
  const H=(logSvgRef.current?.clientHeight||0)||logSvgDims.h||1;
  return {W,H,pts:pts.map(p=>({x:p.x/100*W,y:p.y/100*H}))};
};

/**
 * Adds a vertex where the shape was double-clicked.
 *
 * On the span that was actually clicked and at the point under the pointer,
 * because a vertex that appears somewhere else is worse than no vertex: the
 * person then has to find it and drag it to where they already clicked.
 */
const addVertexAt=(kind:"line"|"area",id:string,pos:{x:number;y:number})=>{
  const shape=kind==="line"
    ?siteLogistics.lines.find(l=>l.id===id)
    :siteLogistics.areas.find(a=>a.id===id);
  if(!shape)return;
  const{W,H,pts}=inPx(shape.points);
  const hit=nearestSegment(pts,{x:pos.x/100*W,y:pos.y/100*H},kind==="area");
  if(!hit)return;
  logisticsSnapshot();
  setSelectedLogId(id);
  const add=<S extends {id:string;points:{x:number;y:number}[]}>(list:S[])=>list.map(sh=>
    sh.id!==id?sh:{...sh,points:insertVertexAfter(sh.points,hit.index,pos)});
  setSiteLogistics(prev=>kind==="line"
    ?{...prev,lines:add(prev.lines)}
    :{...prev,areas:add(prev.areas)});
};

/** Takes a vertex out, refusing when that would stop the shape being one. */
const handleDeleteVertex=(kind:"line"|"area",id:string,vertIdx:number)=>{
  const closed=kind==="area";
  const shape=kind==="line"
    ?siteLogistics.lines.find(l=>l.id===id)
    :siteLogistics.areas.find(a=>a.id===id);
  if(!shape||!canRemoveVertex(shape.points,closed))return;
  logisticsSnapshot();
  const cut=<S extends {id:string;points:{x:number;y:number}[]}>(list:S[])=>list.map(sh=>
    sh.id!==id?sh:{...sh,points:removeVertex(sh.points,vertIdx,closed)});
  setSiteLogistics(prev=>kind==="line"
    ?{...prev,lines:cut(prev.lines)}
    :{...prev,areas:cut(prev.areas)});
};

/** Right-clicking the shape itself: what can be done to the run as a whole. */
const openShapeMenu=(e:React.MouseEvent,kind:"line"|"area",id:string)=>{
  e.preventDefault(); e.stopPropagation();
  if(tool!=="select")return;
  setSelectedLogId(id);
  const closed=kind==="area";
  const shape=kind==="line"
    ?siteLogistics.lines.find(l=>l.id===id)
    :siteLogistics.areas.find(a=>a.id===id);
  if(!shape)return;
  // Locked: the only thing on offer is the way out of it. Leaving the rest of
  // the menu greyed out would be a list of things that cannot be done.
  if(lockedById(siteLogistics,id)){
    setCtxMenu({x:e.clientX,y:e.clientY,items:[{label:"Unlock",onClick:()=>{
      logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,id,false));}}]});
    return;
  }
  const pts=withLegacyCurve(shape.points,(shape as any).curved);
  const curved=curvedCount(pts);
  const setPts=(next:any[])=>{
    logisticsSnapshot();
    const put=<S extends {id:string}>(list:S[])=>list.map(sh=>
      sh.id!==id?sh:{...sh,points:next,curved:undefined});
    setSiteLogistics(prev=>kind==="line"
      ?{...prev,lines:put(prev.lines)}
      :{...prev,areas:put(prev.areas)});
  };
  setCtxMenu({x:e.clientX,y:e.clientY,items:[
    {label:kind==="area"?"Offset… (setback, clearance)":"Offset… (parallel line)",
      onClick:()=>{
        // Greyed out, it looked broken. An offset is a measured distance and
        // there is nothing to measure against without a scale, so say that
        // rather than leaving a dead line in the menu.
        if(!fpp){ say("Set a drawing scale first — an offset is a measured distance"); return; }
        setOffsetAsk({kind,id,name:String(shape.label||"").trim()});
        setOffsetInput(""); setOffsetSide(-1);
      }},
    {label:"Add vertex here",onClick:()=>addVertexAt(kind,id,svgToPercent(e))},
    {label:"Curve all corners",disabled:curved===pts.length,onClick:()=>setPts(setAllCurve(pts,true))},
    {label:"Straighten all corners",disabled:curved===0,onClick:()=>setPts(setAllCurve(pts,false))},
    {label:"Turn 90° clockwise",onClick:()=>{
      logisticsSnapshot();
      const put=<S extends {id:string;rotation?:number}>(list:S[])=>list.map(sh=>
        sh.id!==id?sh:{...sh,points:rotatePoints(shape.points,90,logAspect()),
          rotation:normalizeAngle((shape.rotation||0)+(90))});
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:put(prev.lines)}
        :{...prev,areas:put(prev.areas)});
    }},
    {label:"Turn 90° anticlockwise",onClick:()=>{
      logisticsSnapshot();
      const put=<S extends {id:string;rotation?:number}>(list:S[])=>list.map(sh=>
        sh.id!==id?sh:{...sh,points:rotatePoints(shape.points,-90,logAspect()),
          rotation:normalizeAngle((shape.rotation||0)+(-90))});
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:put(prev.lines)}
        :{...prev,areas:put(prev.areas)});
    }},
    ...stackingItems(kind==="line"?siteLogistics.lines:siteLogistics.areas,id,
      next=>setSiteLogistics(prev=>kind==="line"?{...prev,lines:next}:{...prev,areas:next})),
    {label:"Put the label back",disabled:!isOffset(shape.labelOffset),onClick:()=>{
      logisticsSnapshot();
      const put=<S extends {id:string}>(list:S[])=>list.map(sh=>
        sh.id!==id?sh:{...sh,labelOffset:undefined});
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:put(prev.lines)}
        :{...prev,areas:put(prev.areas)});
    }},
    {label:"Lock",onClick:()=>{
      logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,id,true));}},
    {label:kind==="line"?"Delete line":"Delete area",danger:true,onClick:()=>{
      logisticsSnapshot();
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:prev.lines.filter(l=>l.id!==id)}
        :{...prev,areas:prev.areas.filter(a=>a.id!==id)});
      setSelectedLogId(null);
    }},
  ]});
};

/** Right-clicking one vertex: what can be done to that corner alone. */
const openVertexMenu=(e:React.MouseEvent,kind:"line"|"area",id:string,vertIdx:number)=>{
  e.preventDefault(); e.stopPropagation();
  if(tool!=="select")return;
  setSelectedLogId(id);
  const closed=kind==="area";
  const shape=kind==="line"
    ?siteLogistics.lines.find(l=>l.id===id)
    :siteLogistics.areas.find(a=>a.id===id);
  if(!shape)return;
  // Locked: the only thing on offer is the way out of it. Leaving the rest of
  // the menu greyed out would be a list of things that cannot be done.
  if(lockedById(siteLogistics,id)){
    setCtxMenu({x:e.clientX,y:e.clientY,items:[{label:"Unlock",onClick:()=>{
      logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,id,false));}}]});
    return;
  }
  const pts=withLegacyCurve(shape.points,(shape as any).curved);
  const isCurved=!!pts[vertIdx]?.curve;
  setCtxMenu({x:e.clientX,y:e.clientY,items:[
    {label:isCurved?"Make straight":"Convert to curve",onClick:()=>{
      logisticsSnapshot();
      const next=toggleVertexCurve(pts,vertIdx);
      const put=<S extends {id:string}>(list:S[])=>list.map(sh=>
        sh.id!==id?sh:{...sh,points:next,curved:undefined});
      setSiteLogistics(prev=>kind==="line"
        ?{...prev,lines:put(prev.lines)}
        :{...prev,areas:put(prev.areas)});
    }},
    {label:"Delete vertex",danger:true,disabled:!canRemoveVertex(pts,closed),
      onClick:()=>handleDeleteVertex(kind,id,vertIdx)},
  ]});
};

/**
 * The four stacking moves, for whichever list the element lives in.
 *
 * Order within a list is what decides which of two overlapping shapes you can
 * see and click; the kinds themselves are drawn in a fixed order, so this
 * moves an area past other areas rather than past a line.
 */
const stackingItems=(list:any[],id:string,apply:(next:any[])=>void)=>([
  {label:"Bring to front",disabled:!canRaise(list,id),
    onClick:()=>{logisticsSnapshot();apply(bringToFront(list,id));}},
  {label:"Bring forward",disabled:!canRaise(list,id),
    onClick:()=>{logisticsSnapshot();apply(bringForward(list,id));}},
  {label:"Send backward",disabled:!canLower(list,id),
    onClick:()=>{logisticsSnapshot();apply(sendBackward(list,id));}},
  {label:"Send to back",disabled:!canLower(list,id),
    onClick:()=>{logisticsSnapshot();apply(sendToBack(list,id));}},
]);

/**
 * Right-clicking anything that is not a shape with vertices.
 *
 * Delete lives here rather than in an eraser tool: a mode you have to enter,
 * use and then remember to leave is a mode that deletes something you meant
 * to keep.
 */
const openElementMenu=(e:React.MouseEvent,kind:"icon"|"circle"|"text"|"dimension",id:string)=>{
  e.preventDefault(); e.stopPropagation();
  if(tool!=="select")return;
  setSelectedLogId(id);
  const items:Array<{label:string;onClick:()=>void;danger?:boolean;disabled?:boolean}>=[];
  // Locked: the only thing on offer is the way out of it. Leaving the rest of
  // the menu greyed out would be a list of things that cannot be done.
  if(lockedById(siteLogistics,id)){
    setCtxMenu({x:e.clientX,y:e.clientY,items:[{label:"Unlock",onClick:()=>{
      logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,id,false));}}]});
    return;
  }

  if(kind==="text"){
    items.push({label:"Edit text",onClick:()=>setEditingTextId(id)});
    items.push({label:"Duplicate",onClick:()=>{
      logisticsSnapshot();
      setSiteLogistics(prev=>{
        const src=(prev.texts||[]).find(t=>t.id===id);
        if(!src)return prev;
        return {...prev,texts:[...(prev.texts||[]),{...src,id:uid(),x:src.x+3,y:src.y+4}]};
      });
    }});
  }
  // A dimension carries its own reading; the only thing worth doing to it
  // from here is getting the line out of the way of what it measures.
  // Checking one against a figure printed on the sheet is something you do
  // once, by looking, when you first set the scale -- not a property of
  // every dimension forever.
  if(kind==="dimension"){
    const dm=(siteLogistics.dimensions||[]).find(d=>d.id===id);
    if(dm){
      items.push({label:(dm.offset||0)===0?"Offset the dimension line":"Put the dimension line back",
        onClick:()=>{
          logisticsSnapshot();
          setSiteLogistics(prev=>({...prev,dimensions:(prev.dimensions||[]).map(d=>
            d.id!==id?d:{...d,offset:(d.offset||0)===0?3:0})}));
        }});
    }
  }

  // Turning is offered here as well as on the grip. The grip is quicker once
  // you know it is there; the menu is how you find out, and a quarter turn is
  // the one people reach for most -- a trailer along the far property line.
  if(kind==="icon"||kind==="text"){
    const cur=kind==="icon"
      ?siteLogistics.icons.find(i=>i.id===id)?.rotation
      :(siteLogistics.texts||[]).find(t=>t.id===id)?.rotation;
    const turn=(deg:number)=>{
      logisticsSnapshot();
      setSiteLogistics(prev=>kind==="icon"
        ?{...prev,icons:prev.icons.map(i=>i.id===id?{...i,rotation:normalizeAngle(deg)}:i)}
        :{...prev,texts:(prev.texts||[]).map(t=>t.id===id?{...t,rotation:normalizeAngle(deg)}:t)});
    };
    items.push({label:"Turn 90° clockwise",onClick:()=>turn((cur||0)+90)});
    items.push({label:"Turn 90° anticlockwise",onClick:()=>turn((cur||0)-90)});
    items.push({label:"Square to the sheet",disabled:!isRotated(cur),onClick:()=>turn(0)});
  }

  if(kind==="icon"||kind==="circle"){
    const el:any=kind==="icon"
      ?siteLogistics.icons.find(i=>i.id===id)
      :siteLogistics.circles.find(c=>c.id===id);
    if(isOffset(el?.labelOffset))items.push({label:"Put the label back",onClick:()=>{
      logisticsSnapshot();
      const put=<S extends {id:string}>(list:S[])=>list.map(x=>
        x.id!==id?x:{...x,labelOffset:undefined});
      setSiteLogistics(prev=>kind==="icon"
        ?{...prev,icons:put(prev.icons)}
        :{...prev,circles:put(prev.circles)});
    }});
  }

  {
    const list=kind==="icon"?siteLogistics.icons
      :kind==="circle"?siteLogistics.circles
      :kind==="dimension"?(siteLogistics.dimensions||[])
      :(siteLogistics.texts||[]);
    items.push(...stackingItems(list,id,next=>setSiteLogistics(prev=>
      kind==="icon"?{...prev,icons:next}
      :kind==="circle"?{...prev,circles:next}
      :kind==="dimension"?{...prev,dimensions:next}:{...prev,texts:next})));
  }

  items.push({label:"Lock",onClick:()=>{
    logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,id,true));}});

  items.push({label:kind==="icon"?"Delete item":kind==="circle"?"Delete radius"
      :kind==="dimension"?"Delete dimension":"Delete text",
    danger:true,onClick:()=>{
      logisticsSnapshot();
      setSiteLogistics(prev=>
        kind==="icon"?{...prev,icons:prev.icons.filter(x=>x.id!==id)}
        :kind==="circle"?{...prev,circles:prev.circles.filter(x=>x.id!==id)}
        :kind==="dimension"?{...prev,dimensions:(prev.dimensions||[]).filter(x=>x.id!==id)}
        :{...prev,texts:(prev.texts||[]).filter(x=>x.id!==id)});
      setSelectedLogId(null);
    }});
  setCtxMenu({x:e.clientX,y:e.clientY,items});
};

/** Picks up a whole area or circle so it can be carried to a new spot. */
const handleShapeMouseDown=(e:React.MouseEvent,kind:"area"|"circle"|"text"|"line"|"dimension",id:string,anchor:{x:number;y:number})=>{
  e.stopPropagation();
  e.preventDefault();
  if(tool!=="select")return;
  if(lockedById(siteLogistics,id)){setSelectedLogId(id);return;}

  const pos=svgToPercent(e);

  setSelectedLogId(id);
  logisticsSnapshot();
  logDragRef.current={kind,id,ox:pos.x-anchor.x,oy:pos.y-anchor.y,startX:pos.x,startY:pos.y};
};

/**
 * Shift-drag on a label pulls it clear of whatever it is sitting on top of.
 *
 * The offset is from the label's home position rather than an absolute point,
 * so a label moved out of a clash keeps its relationship to the thing it names
 * when that thing is dragged somewhere else.
 */
const handleLabelMouseDown=(e:React.MouseEvent,id:string,cur?:{x:number;y:number})=>{
  if(!e.shiftKey||tool!=="select")return;
  if(lockedById(siteLogistics,id)){setSelectedLogId(id);return;}
  e.stopPropagation(); e.preventDefault();
  setSelectedLogId(id);
  logisticsSnapshot();
  const pos=svgToPercent(e);
  logLabelRef.current={id,ox:pos.x-(cur?.x||0),oy:pos.y-(cur?.y||0)};
};

/**
 * Grabs the rotation grip.
 *
 * The angle is tracked as an offset from wherever the pointer started, so the
 * shape does not jump to meet the cursor the instant it is grabbed.
 */
const handleRotateMouseDown=(e:React.MouseEvent,kind:"icon"|"text"|"area"|"line",id:string,
  pivot:{x:number;y:number},base:number,points?:{x:number;y:number}[])=>{
  e.stopPropagation();
  if(tool!=="select")return;
  if(lockedById(siteLogistics,id)){setSelectedLogId(id);return;}

  setSelectedLogId(id);
  logisticsSnapshot();
  logRotateRef.current={kind,id,base,
    start:angleFromPointer(pivot,svgToPercent(e),logAspect()),
    points:points?points.map(p=>({...p})):undefined};
};

/** Grabs a corner of an item to set how much ground it takes. */
const handleResizeMouseDown=(e:React.MouseEvent,id:string,handle:Handle)=>{
  e.stopPropagation();
  if(tool!=="select")return;
  if(lockedById(siteLogistics,id)){setSelectedLogId(id);return;}

  setSelectedLogId(id);
  logisticsSnapshot();
  logResizeRef.current={id,handle};
};

const handleMouseUp=React.useCallback(()=>{
  logDragRef.current=null; logVertexDragRef.current=null; logResizeRef.current=null;
  logDimEndRef.current=null; logBlockRef.current=null; logBlockResizeRef.current=null;
  logScaleRef.current=null; logBezierRef.current=null;
  logRotateRef.current=null; logLabelRef.current=null;
},[logDragRef,logVertexDragRef,logResizeRef,logRotateRef,logLabelRef]);

// A drag released off the canvas has to end too. Without this the element
// stays stuck to the pointer, and the next click drops it somewhere nobody
// chose -- which looks exactly like the drawing corrupting itself.
React.useEffect(()=>{
  window.addEventListener("mouseup",handleMouseUp);
  return()=>window.removeEventListener("mouseup",handleMouseUp);
},[handleMouseUp]);

const isAreaTool=tool.startsWith("area:");
const isCircleTool=tool==="circle";
const isTextTool=tool==="text";
const isCalibTool=tool==="calibrate";
const isDimTool=tool==="dimension";
const drawingAreaType=isAreaTool?tool.slice(5):null;

/** The canvas as it is right now.
 *
 *  Read live rather than from state. Measurements used to come off
 *  `logSvgDims`, which a resize observer writes, while the drawing and the
 *  feet-per-pixel came off `clientWidth`. Between a resize and that state
 *  landing the two disagreed, and every length and area on the plan was
 *  computed against a canvas of one size and scaled by a canvas of another --
 *  silently, and with a confident number on the drawing to show for it. */
const liveCanvas={
  w:(logSvgRef.current?.clientWidth||0)||logSvgDims.w,
  h:(logSvgRef.current?.clientHeight||0)||logSvgDims.h,
};

// Measurement helpers
const W=liveCanvas.w, H=liveCanvas.h;
const logDist=(p1:{x:number;y:number},p2:{x:number;y:number})=>{
  const dx=(p2.x-p1.x)/100*W; const dy=(p2.y-p1.y)/100*H;
  return Math.sqrt(dx*dx+dy*dy);
};
const polyLen=(pts:{x:number;y:number}[])=>pts.reduce((s,p,i)=>i===0?s:s+logDist(pts[i-1],p),0);
const polyArea=(pts:{x:number;y:number}[])=>{
  if(pts.length<3)return 0;
  let s=0;
  for(let i=0;i<pts.length;i++){const j=(i+1)%pts.length;s+=(pts[i].x/100*W)*(pts[j].y/100*H);s-=(pts[j].x/100*W)*(pts[i].y/100*H);}
  return Math.abs(s/2);
};
// ── Plans ───────────────────────────────────────────────────────────────
// A job has as many logistics plans as it needs, each a free canvas. The rest
// of this component is written against one plan, so the active one is shadowed
// over the props: every existing read and write lands on it without changing a
// line of the drawing code.
const allPlans=plansOf(siteLogisticsProp);
const current=activePlan(siteLogisticsProp);
const siteLogistics=current as unknown as typeof siteLogisticsProp;
const setSiteLogistics:typeof setSiteLogisticsProp=(update)=>{
  setSiteLogisticsProp(prev=>{
    const plan=activePlan(prev);
    const next=typeof update==="function"
      ?(update as (p:any)=>any)(plan)
      :update;
    return withPlan(prev,plan.id,{...plan,...next});
  });
};

// Governs ICONS and LINES only, and is off by default: both are typed, the
// legend decodes them, and a dozen icons each carrying their own name is what
// made the drawing unreadable.
//
// Areas and circles always keep their names. They are free shapes the user
// names per instance -- two staging areas are told apart only by what is
// written on them -- so nothing else in the drawing carries that, and hiding
// it would leave anonymous coloured blobs.
const showLabels=siteLogisticsProp.showLabels===true;

/**
 * What the labels are actually sitting on.
 *
 * Not the panel: a label is painted over the uploaded sheet or the aerial, and
 * both are light. A white halo disappears into white paper and reads as a
 * bright outline over a photograph, so one light surface covers both. Only the
 * empty canvas, which is the app's own dark grey, is different.
 */
const labelSurface=(siteLogistics.imageDataUrl||logisticsView==="map")?"#ffffff":T.bgPanel;

/**
 * Selection chrome, coloured against the drawing rather than against the app.
 *
 * Handles, grips and selection outlines were drawn with a white stroke and the
 * app panel's fill, which assumed the canvas underneath was dark. Over an
 * uploaded sheet it is white, so a white outline vanished and a pale handle
 * with a pale-grey element colour on it could not be found at all -- and the
 * element colour is whatever the user picked, so it can be any colour at all,
 * including one that matches the paper.
 */
const chromeInk=readableOn(labelSurface);
/** The grab handles read as a hole in the drawing with a coloured ring. */
const handleFill=labelSurface;
/** An element's own colour, pushed until it reads on the drawing. */
const chromeOn=(c:string)=>inkOn(c,labelSurface);
const [renamingId,setRenamingId]=React.useState<string|null>(null);
const [dragIdx,setDragIdx]=React.useState<number|null>(null);
const [dragOverIdx,setDragOverIdx]=React.useState<number|null>(null);
/**
 * Whether Shift is down.
 *
 * Labels ignore the pointer the rest of the time -- they sit over the shapes
 * they name, and catching clicks meant for those would be worse than the
 * crowding. Holding Shift is what hands them the pointer, which is also the
 * hint that they can be moved.
 */
/** Which end of which dimension is being dragged. */
const logDimEndRef=React.useRef<{id:string;end:"a"|"b"}|null>(null);

/** A polygon being scaled: what it looked like when the drag started, so
 *  every frame scales the original rather than compounding the last one. */
/** A bezier handle being pulled: which vertex, and which side of it. */
const logBezierRef=React.useRef<{kind:"line"|"area";id:string;index:number;
  which:"in"|"out";mirror:boolean}|null>(null);

const logScaleRef=React.useRef<{kind:"line"|"area";id:string;
  origin:{x:number;y:number};startDist:number;points:{x:number;y:number}[]}|null>(null);

/**
 * Grabs a corner of a selected shape's bounding box to scale it.
 *
 * The corner opposite the one being dragged stays where it is, which is how
 * every drawing tool does this and the only version that lets you size a
 * shape against something already on the sheet. Uniform: the diagonal is
 * what is being pulled, so both axes follow it and the shape does not
 * squash.
 */
const handleScaleMouseDown=(e:React.MouseEvent,kind:"line"|"area",id:string,corner:Corner)=>{
  e.stopPropagation(); e.preventDefault();
  if(tool!=="select")return;
  if(lockedById(siteLogistics,id))return;
  const shape:any=kind==="line"
    ?siteLogistics.lines.find(l=>l.id===id)
    :siteLogistics.areas.find(a=>a.id===id);
  const b=shape?.points?.length?boundsOf(shape.points):null;
  if(!b)return;
  const origin=cornerPoint(b,oppositeCorner(corner));
  const startDist=pctDistance(origin,cornerPoint(b,corner),liveCanvas);
  // A shape with no size in one direction has no diagonal to pull.
  if(startDist<=0.3)return;
  setSelectedLogId(id);
  logisticsSnapshot();
  logScaleRef.current={kind,id,origin,startDist,points:shape.points.map((p:any)=>({...p}))};
};
/**
 * Where the sheet blocks sit when nobody has moved them.
 *
 * Along the bottom, where a title block belongs, and placed from the block's
 * own height rather than at a fixed percentage: a legend with twelve entries
 * in one column is taller than the space a constant left for it, and it hung
 * off the bottom of the sheet where it could not even be grabbed.
 */
const blockHome=(kind:BlockKind,hPx:number,canvasH:number)=>{
  const hPct=canvasH>0?(hPx/canvasH)*100:0;
  const bottom=Math.max(2,98-hPct);
  return kind==="title"?{x:62,y:bottom}:{x:1.5,y:kind==="scale"?bottom:Math.max(2,bottom-1)};
};
/** Two columns by default: one tall strip down the side of a drawing is the
 *  shape nobody wants, and the corner is there to change it. */
const LEGEND_DEFAULT_COLS=2;

/** Which block is being dragged, and from where inside it. */
const logBlockRef=React.useRef<{kind:BlockKind;ox:number;oy:number}|null>(null);
/** Which block is being reshaped. */
const logBlockResizeRef=React.useRef<{kind:BlockKind;corner:"nw"|"ne"|"sw"|"se";
  startX:number;startY:number}|null>(null);

/** Where each block actually ended up. Its home depends on how tall it
 *  turned out, which only the renderer knows, so the renderer records it
 *  and the drag handlers read it rather than recomputing a guess. */
const blockPosRef=React.useRef<Partial<Record<BlockKind,{x:number;y:number}>>>({});

/**
 * Rulers down the edges of the sheet.
 *
 * A drawing on screen has no size: you can see that a trailer clears a
 * property line, but not whether this is a letter-size print or an ARCH D
 * sheet -- and that is what decides whether the scale in the title block
 * survives a printer. Kept per browser rather than per project, because it
 * is how someone wants to look at a drawing, not a fact about the job.
 */
const [rulers,setRulers]=React.useState<boolean>(()=>{
  try{ return localStorage.getItem("oe_log_ruler_v1")==="1"; }catch{ return false; }
});
const toggleRulers=React.useCallback(()=>setRulers(prev=>{
  const next=!prev;
  try{ localStorage.setItem("oe_log_ruler_v1",next?"1":"0"); }catch{ /* private mode */ }
  return next;
}),[]);
React.useEffect(()=>{
  const h=(e:KeyboardEvent)=>{
    const tag=(document.activeElement as HTMLElement)?.tagName;
    if(tag==="INPUT"||tag==="TEXTAREA")return;
    if((e.ctrlKey||e.metaKey)&&(e.key==="r"||e.key==="R")){
      // Ctrl+R is reload everywhere else, so this only takes it on the tab
      // that has rulers to show.
      e.preventDefault();
      toggleRulers();
    }
  };
  window.addEventListener("keydown",h);
  return()=>window.removeEventListener("keydown",h);
},[toggleRulers]);

/** Where the sheet actually is inside its box, measured rather than worked
 *  out: it is centred, scaled to fit, and then zoomed and panned. */
const [sheetRect,setSheetRect]=React.useState<{x:number;y:number;w:number;h:number}|null>(null);

/** How wide the ruler bands are. */
const RULER_W=18;

/** Two points picked for calibration, waiting on a real-world distance. */
const [calibAsk,setCalibAsk]=React.useState<{a:{x:number;y:number};b:{x:number;y:number}}|null>(null);
/** The shape being offset, while the distance is being typed. */
const [offsetAsk,setOffsetAsk]=React.useState<{kind:"line"|"area";id:string;name:string}|null>(null);
/** The area just drawn, waiting to be told what it is. */
const [nameAsk,setNameAsk]=React.useState<{id:string}|null>(null);
const [nameInput,setNameInput]=React.useState("");
const [offsetInput,setOffsetInput]=React.useState("");
/** Which way: inward on a closed shape, one side or the other on a line. */
const [offsetSide,setOffsetSide]=React.useState<-1|1>(-1);
const [exporting,setExporting]=React.useState(false);
const [shiftHeld,setShiftHeld]=React.useState(false);
React.useEffect(()=>{
  const down=(e:KeyboardEvent)=>{if(e.key==="Shift")setShiftHeld(true);};
  const up=(e:KeyboardEvent)=>{if(e.key==="Shift")setShiftHeld(false);};
  // Releasing Shift outside the window never reaches keyup, so the focus
  // change clears it too.
  const clear=()=>setShiftHeld(false);
  window.addEventListener("keydown",down);
  window.addEventListener("keyup",up);
  window.addEventListener("blur",clear);
  return()=>{window.removeEventListener("keydown",down);
    window.removeEventListener("keyup",up);window.removeEventListener("blur",clear);};
},[]);

/**
 * How the sheet is being looked at: a scale and an offset applied to the
 * drawing and its marks together, so the two stay registered however far in
 * you are. It changes nothing about what anything measures -- the scale of
 * the plan belongs to the drawing, zoom belongs to looking at it.
 */
const [view,setView]=React.useState<ViewTransform>(FIT);
const panRef=React.useRef<{x:number;y:number}|null>(null);
const planSheetRef=React.useRef<HTMLDivElement>(null);
/** The last middle press, so the next one can be told it is a double. */
const midPressRef=React.useRef<Press|null>(null);

/**
 * Whether a zoom or a pan is happening this instant.
 *
 * It decides one thing: whether the sheet gets `will-change: transform`.
 * That hint hands the sheet to the compositor, which rasterises it once and
 * then stretches that one bitmap for every later scale -- smooth to drag,
 * and at 300% a drawing of soft grey smudges where the type used to be. The
 * sheet was carrying the hint the whole time it was zoomed in, which is
 * exactly when it hurts: the ruler beside it stayed crisp while the drawing
 * inside it did not.
 *
 * So the hint is worn only for the moment of the gesture, when cheap frames
 * are worth a soft picture, and dropped a quarter of a second after the last
 * one -- at which point the browser rasterises again at the scale you are
 * actually looking at, and the sheet snaps sharp.
 */
const [gesturing,setGesturing]=React.useState(false);
const gestureTimer=React.useRef<number|null>(null);
const markGesture=React.useCallback(()=>{
  setGesturing(true);
  if(gestureTimer.current!==null)window.clearTimeout(gestureTimer.current);
  gestureTimer.current=window.setTimeout(()=>{gestureTimer.current=null;setGesturing(false);},250);
},[]);
React.useEffect(()=>()=>{if(gestureTimer.current!==null)window.clearTimeout(gestureTimer.current);},[]);

/** Ctrl and the wheel zooms about the pointer; the wheel alone is left to
 *  the page, which is what people expect of a document. Bound by hand
 *  because a passive listener may not refuse the browser's own zoom. */
React.useEffect(()=>{
  const el=planBoxRef.current;
  if(!el)return;
  const onWheel=(e:WheelEvent)=>{
    if(!e.ctrlKey&&!e.metaKey)return;
    e.preventDefault();
    markGesture();
    // The cursor has to be expressed in the sheet's own untransformed frame,
    // because that is the frame the transform is written in. offsetLeft and
    // offsetTop are layout values and ignore the transform; the bounding rect
    // would already have it applied and the anchor would creep on every notch.
    const r=el.getBoundingClientRect();
    const sheet=planSheetRef.current;
    const ox=sheet?sheet.offsetLeft:0, oy=sheet?sheet.offsetTop:0;
    setView(v=>zoomAt(v,wheelFactor(e.deltaY),
      e.clientX-r.left-ox, e.clientY-r.top-oy));
  };
  el.addEventListener("wheel",onWheel,{passive:false});
  return()=>el.removeEventListener("wheel",onWheel);
},[markGesture]);

/**
 * A sheet you have just arrived at is shown whole.
 *
 * The pan and the zoom belong to the drawing you were looking at. Carrying
 * them across to the live map and back meant pressing Sheet and landing on
 * whatever corner of the drawing you had last magnified -- and if you had
 * panned away from it, on nothing at all, which looks exactly like a button
 * that does not work.
 */
React.useEffect(()=>{setView(FIT);},[logisticsView]);

/** The middle button drags the sheet, and a double click of it puts the whole
 *  sheet back -- the two things a wheel is for besides scrolling. */
const onPlanPointerDown=(e:React.PointerEvent)=>{
  if(e.button!==1)return;
  e.preventDefault();
  // The double is counted here rather than read off the event. A pointer
  // event carries no click count -- detail is zero on pointerdown by
  // specification -- so testing it looks right and never fires for a mouse.
  const press:Press={t:Date.now(),x:e.clientX,y:e.clientY};
  if(isDoublePress(midPressRef.current,press)){
    midPressRef.current=null;
    panRef.current=null;
    setView(FIT);
    return;
  }
  midPressRef.current=press;
  panRef.current={x:e.clientX,y:e.clientY};
  try{(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);}catch{/* drag still works */}
};
const onPlanPointerMove=(e:React.PointerEvent)=>{
  const p=panRef.current;
  if(!p)return;
  markGesture();
  setView(v=>panBy(v,e.clientX-p.x,e.clientY-p.y));
  panRef.current={x:e.clientX,y:e.clientY};
};
React.useEffect(()=>{
  const stop=()=>{panRef.current=null;};
  window.addEventListener("pointerup",stop);
  window.addEventListener("pointercancel",stop);
  return()=>{window.removeEventListener("pointerup",stop);
    window.removeEventListener("pointercancel",stop);};
},[]);

/** Padding around the plan inside its box, in px. Shared by the style and
 *  the measurement so the two cannot drift apart. */
const PLAN_PAD=12;
/** The frame around the plan. Counted against the space the image may take,
 *  or the frame overflows and the overlay is clipped narrower than the sheet
 *  it is drawn on top of. */
const PLAN_BORDER=1;

/**
 * How much room the plan actually has.
 *
 * Measured rather than expressed as a percentage. The image sits in a wrapper
 * that shrinks to fit it, so a percentage height on the image has no definite
 * parent to resolve against and is dropped -- the plan then sized itself to
 * the full width and had its bottom clipped. The box it has to fit in does
 * not depend on the image, so measuring it is not circular.
 */
const planBoxRef=React.useRef<HTMLDivElement>(null);
const [planBox,setPlanBox]=React.useState({w:0,h:0});
React.useEffect(()=>{
  const el=planBoxRef.current;
  if(!el||typeof ResizeObserver==="undefined")return;
  const read=()=>{
    const chrome=(PLAN_PAD+PLAN_BORDER)*2;
    const w=Math.max(0,el.clientWidth-chrome);
    const h=Math.max(0,el.clientHeight-chrome);
    setPlanBox(prev=>(Math.abs(prev.w-w)<1&&Math.abs(prev.h-h)<1)?prev:{w,h});
  };
  read();
  const ro=new ResizeObserver(read);
  ro.observe(el);
  return()=>ro.disconnect();
},[]);

/** The text box currently open for typing, if any. */
const [editingTextId,setEditingTextId]=React.useState<string|null>(null);
/**
 * The label currently open for renaming.
 *
 * The side panel said "Double-click any label to rename it" and only a text
 * box did. Everything else had to be renamed through the properties panel,
 * which means finding it, which means knowing it is there.
 */
const [editingLabel,setEditingLabel]=React.useState<{kind:"line"|"area"|"icon"|"circle";id:string}|null>(null);
/**
 * What the next custom line is called.
 *
 * A custom line is whatever this job has that the palette does not -- and a
 * job has more than one of those. Fencing and a sleeve route are two
 * different lines, drawn more than once each, and naming every one of them
 * by hand after the fact is how people stop bothering.
 */
const [customName,setCustomName]=React.useState("");

/** The names already in use on this plan, so a second one is a click. */
const customNames=React.useMemo(()=>{
  const seen=new Map<string,{color?:string;dash?:string;width?:number}>();
  for(const ln of siteLogistics.lines){
    if(ln.lineType!=="custom")continue;
    const name=String(ln.label||"").trim();
    if(!name||seen.has(name))continue;
    seen.set(name,{color:ln.strokeColor,dash:ln.strokeDash,width:ln.strokeWidth});
  }
  return [...seen.entries()].map(([name,style])=>({name,...style}));
},[siteLogistics.lines]);

/** A label, changed on whichever list the thing lives in. */
const renameElement=(kind:"line"|"area"|"icon"|"circle",id:string,label:string)=>{
  logisticsSnapshot();
  const put=<S extends {id:string}>(list:S[])=>list.map(x=>x.id!==id?x:{...x,label});
  setSiteLogistics(prev=>
    kind==="line"?{...prev,lines:put(prev.lines)}
    :kind==="area"?{...prev,areas:put(prev.areas)}
    :kind==="icon"?{...prev,icons:put(prev.icons)}
    :{...prev,circles:put(prev.circles)});
};
/**
 * Feet per pixel of the live map, as the map itself reports it.
 *
 * The aerial has no Setup menu and no calibrate tool, and it does not need
 * either: a web map's ground scale follows from its projection, its zoom and
 * its latitude. Before this, nothing drawn on the map could be measured --
 * the dimension tool drew a line and had nothing to write on it.
 */
const [mapFpp,setMapFpp]=React.useState<number|null>(null);
const handleMapScale=React.useCallback((f:number|null)=>{
  setMapFpp(prev=>(prev===f?prev:(f&&Number.isFinite(f)&&f>0?f:null)));
},[]);

/** The uploaded sheet's own pixel width, which is what a named scale like
 *  1"=20' is actually about. */
const [imgNaturalW,setImgNaturalW]=React.useState(0);
/** And its height, so an export is the sheet's own shape rather than the
 *  on-screen box's rounded to the nearest pixel. */
const [imgNaturalH,setImgNaturalH]=React.useState(0);

/**
 * What is being brought in right now, said in words, or null when nothing is.
 *
 * It stays set until the drawing is genuinely on the screen rather than
 * merely decoded. The complaint was about the gap between picking a file and
 * the sheet appearing -- on a large PDF, several seconds of nothing -- and
 * that gap ends at the image's own load event, so that is where it is cleared.
 */
const [importing,setImporting]=React.useState<string|null>(null);

/**
 * A notice that never comes down is worse than no notice at all.
 *
 * Every path clears it, but every path assumes an event that might not
 * arrive -- a sheet swapped out mid-read takes its load event with it. Thirty
 * seconds past the last thing that happened, the notice goes anyway.
 */
React.useEffect(()=>{
  if(!importing)return;
  const id=window.setTimeout(()=>setImporting(null),30000);
  return()=>window.clearTimeout(id);
},[importing]);

/**
 * The sheet's size in real inches.
 *
 * A named scale fixes it exactly -- 1" = 20' over a drawing whose width is
 * known in feet gives the paper's width in inches. Without one the image's
 * own pixels at 96 dpi are the best anyone can say.
 */
/**
 * How many pixels of the drawing make an inch of paper.
 *
 * A PDF page is measured in points and we render it at 2x, so it arrives at
 * 144 pixels to the inch. Assuming 96 made the app read a 36" sheet as 54"
 * wide, and then every named scale taken off it, every annotation sized in
 * points of it, and the graphic scale bar itself were half as big again as
 * they should have been.
 *
 * Declared here, above the first thing that reads it. It was two hundred
 * lines below, and the only reason that was survivable in testing is that
 * the reader below is the right-hand side of a ?? -- so it was reached only
 * on a plan that has a drawing and no named scale, which the demo is not.
 * On a calibrated plan it threw before the tab could render at all.
 */
const sheetDpi=siteLogistics.imageDpi||96;

const sheetInches=React.useMemo(()=>{
  // The map is not a sheet. Sizing annotations in points of a 36" sheet that
  // is not on the screen made every label on the aerial about three pixels
  // tall; over the map they are sized in screen points instead.
  if(logisticsView==="map")return null;
  const preset=SCALE_PRESETS.find(sp=>sp.id===siteLogistics.scale?.preset)?.feetPerPx;
  const perPct=normalizeScale(siteLogistics.scale as any,liveCanvas).feetPerPct;
  const w=sheetInchesFor(perPct,preset?preset*96:null)
    ??(imgNaturalW?imgNaturalW/sheetDpi:null);
  if(!w)return null;
  const ratio=imgNaturalW&&imgNaturalH?imgNaturalH/imgNaturalW
    :(liveCanvas.h&&liveCanvas.w?liveCanvas.h/liveCanvas.w:null);
  return ratio?{w,h:w*ratio}:null;
},[siteLogistics.scale,liveCanvas.w,liveCanvas.h,imgNaturalW,imgNaturalH,logisticsView]);

React.useEffect(()=>{
  if(!rulers)return;
  const read=()=>{
    const box=planBoxRef.current, sheet=planSheetRef.current;
    if(!box||!sheet)return;
    const bb=box.getBoundingClientRect(), sb=sheet.getBoundingClientRect();
    const next={x:sb.left-bb.left,y:sb.top-bb.top,w:sb.width,h:sb.height};
    setSheetRect(prev=>(prev&&Math.abs(prev.x-next.x)<0.5&&Math.abs(prev.y-next.y)<0.5
      &&Math.abs(prev.w-next.w)<0.5&&Math.abs(prev.h-next.h)<0.5)?prev:next);
  };
  read();
  // The sheet is laid out, then transformed; a frame later is when both have
  // settled.
  const id=requestAnimationFrame(read);
  return()=>cancelAnimationFrame(id);
});


/** The right-click menu, if one is open. Positioned in page coordinates so it
 *  can sit over the drawing without being clipped by it. */
const [ctxMenu,setCtxMenu]=React.useState<{x:number;y:number;
  items:Array<{label:string;onClick:()=>void;disabled?:boolean;danger?:boolean}>}|null>(null);
const closeCtxMenu=React.useCallback(()=>setCtxMenu(null),[]);

/**
 * The sheet bar's menus.
 *
 * Anchored under whatever opened them rather than at the pointer: these hang
 * off a named control, and a panel that appears wherever the mouse happened
 * to be reads as a context menu for something else.
 */
const [popover,setPopover]=React.useState<{kind:"setup"|"export"|"phase"|"add";x:number;y:number}|null>(null);

const openPop=(kind:"setup"|"export"|"phase"|"add",e:React.MouseEvent)=>{
  e.stopPropagation();
  const r=(e.currentTarget as HTMLElement).getBoundingClientRect();
  setPopover(prev=>prev&&prev.kind===kind?null:{kind,x:r.left,y:r.bottom+4});
};
React.useEffect(()=>{
  if(!popover)return;
  const away=()=>setPopover(null);
  const onKey=(ev:KeyboardEvent)=>{if(ev.key==="Escape")setPopover(null);};
  window.addEventListener("mousedown",away);
  window.addEventListener("keydown",onKey);
  return()=>{window.removeEventListener("mousedown",away);window.removeEventListener("keydown",onKey);};
},[popover]);
React.useEffect(()=>{
  if(!ctxMenu)return;
  const onAway=()=>closeCtxMenu();
  const onKey=(e:KeyboardEvent)=>{if(e.key==="Escape")closeCtxMenu();};
  // Captured on the window so the menu closes for any click anywhere,
  // including one that lands on another shape and opens its own menu.
  window.addEventListener("mousedown",onAway);
  window.addEventListener("keydown",onKey);
  return()=>{window.removeEventListener("mousedown",onAway);window.removeEventListener("keydown",onKey);};
},[ctxMenu,closeCtxMenu]);

const goPlan=(id:string|null)=>{if(id)setSiteLogisticsProp(prev=>({...prev,activePlanId:id}));};
const mutatePlans=(fn:(sl:any)=>any)=>{logisticsSnapshot();setSiteLogisticsProp(prev=>fn(prev));};

/** Feet per 1% of width: a property of the drawing, not of the window. */
const planScale=normalizeScale(siteLogistics.scale as any,liveCanvas);
/**
 * Feet per pixel of whatever is actually behind the markups.
 *
 * On the sheet that is the scale the drawing was calibrated at; on the map it
 * is the map's. They are kept apart on purpose: the plan's calibration
 * describes an uploaded drawing and must survive a trip to the aerial and
 * back, and the map's changes every time somebody zooms.
 */
const fpp=logisticsView==="map"?mapFpp:feetPerPixel(planScale,liveCanvas);
/** How big this sheet wants the app's own marks. One is true paper size. */
const markup=(()=>{
  const v=siteLogistics.markupScale;
  if(Number.isFinite(v)&&(v as number)>0)return Math.min(6,Math.max(.4,v as number));
  // Unset: scaled to the sheet, because that is what the problem scales with.
  //
  // A mark sized in points of the sheet is right on paper and unreadable on
  // a screen, and how unreadable depends entirely on how much the sheet had
  // to shrink to fit the window. A letter page shown at 1000px is near its
  // own size and needs nothing; a 36" sheet in the same window is at a
  // quarter scale, so its 8pt legend key lands at about two pixels. Measured
  // on the demo plan at 1x, every piece of sheet text came out between 2 and
  // 4 pixels.
  //
  // So the default compensates: roughly one step of scaling per foot of
  // paper. Letter 1x, ARCH C 2x, ARCH D 3x, ARCH E 4x. Capped, because past
  // that the marks start covering the drawing they annotate, and 1x is still
  // on the slider for anyone who wants true paper size.
  const inches=sheetInches?.w;
  if(!inches||!Number.isFinite(inches))return 1;
  return Math.min(4,Math.max(1,inches/12));
})();

/** The scale in words, for the sheet and for the panel that switches it on. */
const scaleTxt=(()=>{
  if(!fpp)return "";
  if(logisticsView==="map")return mapScaleNote(fpp).toUpperCase();
  const pre=SCALE_PRESETS.find(sp=>sp.id===siteLogistics.scale?.preset);
  return siteLogistics.scale?.preset==="custom"?"CALIBRATED":(pre?.label||"");
})();
const fmtLen=(logPx:number)=>{if(!fpp)return null;const ft=logPx*fpp;return ft>=1?`${ft.toFixed(0)}'`:`${(ft*12).toFixed(1)}"`; };
const fmtArea=(logPx2:number)=>{if(!fpp)return null;const sqft=logPx2*fpp*fpp;return sqft>=43560?`${(sqft/43560).toFixed(2)} ac`:sqft>=100?`${Math.round(sqft).toLocaleString()} sf`:`${sqft.toFixed(0)} sf`;};

const handleAreaClick=(e:React.MouseEvent<SVGSVGElement>)=>{
  const pos=svgToPercent(e);
  setAreaInProgress(prev=>[...prev,pos]);
};
const handleAreaDblClick=(e:React.MouseEvent<SVGSVGElement>)=>{
  if(areaInProgress.length>=3&&drawingAreaType){
    e.preventDefault();
    const cfg=SITE_AREA_CONFIG[drawingAreaType]||SITE_AREA_CONFIG["custom-area"];
    // A named type is its own name. A custom one is not anything yet, so it
    // lands with no label and is asked, rather than arriving called "Custom
    // Area" and waiting to be corrected.
    const custom=drawingAreaType==="custom-area";
    const id=uid();
    const newArea:SiteArea={id,areaType:drawingAreaType,points:[...areaInProgress],
      label:custom?"":cfg.label,color:cfg.color};
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,areas:[...prev.areas,newArea]}));
    setAreaInProgress([]);setAreaPreview(null);
    if(custom){setNameAsk({id});setNameInput("");}
  }
};
const handleCircleClick=(e:React.MouseEvent<SVGSVGElement>,pos:{x:number;y:number})=>{
  if(!circleCenter){setCircleCenter(pos);}
  else{
    const r=logDist(circleCenter,pos);
    const newCircle:SiteCircle={id:uid(),cx:circleCenter.x,cy:circleCenter.y,
      r,rPct:pctDistance(circleCenter,pos,liveCanvas),label:"Radius",color:"#fc8181"};
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,circles:[...prev.circles,newCircle]}));
    setCircleCenter(null);setCirclePreviewR(0);
  }
};
/**
 * Two clicks make a dimension.
 *
 * The same two-click shape as calibration, and deliberately so: measuring a
 * printed dimension and setting the scale from one are the same gesture, and
 * the second is offered from the first once you can see what it reads.
 */
const handleDimClick=(pos:{x:number;y:number})=>{
  const next=[...calibPoints,pos];
  if(next.length===2){
    const id=uid();
    logisticsSnapshot();
    setSiteLogistics(prev=>({...prev,dimensions:[...(prev.dimensions||[]),
      {id,a:next[0],b:next[1],offset:0}]}));
    setCalibPoints([]);
    setAreaPreview(null);
    setSelectedLogId(id);
    setLogisticsTool("select");
  }else{
    setCalibPoints(next);
  }
};

/**
 * Calibrating: pick two points, then say how far apart they really are.
 *
 * In that order. It wanted the distance typed before the second click, so
 * picking two points with the field empty did nothing at all and said nothing
 * about why, which is indistinguishable from a broken tool. You measure
 * first and are asked second, the way every markup tool does it.
 */
const handleCalibClick=(pos:{x:number;y:number})=>{
  const next=[...calibPoints,pos];
  setCalibPoints(next);
  if(next.length===2){
    setCalibAsk({a:next[0],b:next[1]});
    setAreaPreview(null);
  }
};

/** Puts the measured distance to work, or says why it cannot. */
const applyCalibration=(typed:string)=>{
  if(!calibAsk)return;
  const feet=parseFeet(typed);
  if(feet===null){ say(`Could not read that \u2014 try 100 or 100'-6"`); return; }
  const perPct=calibrate(calibAsk.a,calibAsk.b,feet,liveCanvas);
  if(!perPct){ say("Those two points are too close together to measure"); return; }
  logisticsSnapshot();
  setSiteLogistics(prev=>({...prev,scale:{preset:"custom",feetPerPct:perPct,feetPerPx:null}}));
  endCalibration();
  say(`Scale set \u2014 that distance is ${(+feet.toFixed(2))}'`);
};

/**
 * The same shape, a measured distance away from itself.
 *
 * Half the questions a logistics plan answers are clearance questions -- the
 * buildable area inside the lot line, how far the crane can pick, what a pump
 * truck reaches from where it can stand, how far a temporary service can run
 * from the meter -- and all of them are this.
 *
 * What comes out is a new shape, not a link back to the old one. A setback is
 * a statement about where the lot line was when somebody measured it; quietly
 * dragging it along when the line moves would make it a statement about
 * nothing. It is named for what it came from, so the sheet says so.
 */
/** Give the area just drawn the name it was asked for, or leave it unnamed. */
const applyName=()=>{
  const nm=nameInput.trim();
  const id=nameAsk?.id;
  setNameAsk(null); setNameInput("");
  if(!id||!nm)return;
  setSiteLogistics(prev=>({...prev,
    areas:prev.areas.map(a=>a.id===id?{...a,label:nm}:a)}));
  setSelectedLogId(id);
};

const applyOffset=()=>{
  const ask=offsetAsk;
  if(!ask)return;
  const feet=parseFeet(offsetInput);
  if(feet===null){ say(`Could not read that — try 30 or 30'-6"`); return; }
  if(!fpp){ say("Set a drawing scale before offsetting — there is nothing to measure against"); return; }
  const closed=ask.kind==="area";
  const shape:any=closed
    ?siteLogistics.areas.find(a=>a.id===ask.id)
    :siteLogistics.lines.find(l=>l.id===ask.id);
  if(!shape){ setOffsetAsk(null); return; }

  // Percent coordinates are percent of width and percent of height
  // separately, so the same number is a different distance across as it is
  // down. An offset has to be measured in one space: pixels, and back again.
  const W=liveCanvas.w||1, H=liveCanvas.h||1;
  const out=offsetPath(
    shape.points.map((pt:any)=>({x:pt.x/100*W,y:pt.y/100*H})),
    (feet/fpp)*offsetSide, closed);
  if(!out){
    say(`${feet}' is more than that shape has to give — the offset folds through itself`);
    return;
  }
  logisticsSnapshot();
  const pts=out.map(pt=>({x:(pt.x/W)*100,y:(pt.y/H)*100}));
  const label=offsetLabel(shape.label,feet*offsetSide);
  const id=uid();
  if(closed){
    // Unfilled: what matters is the boundary, and a second wash of colour
    // over the one it came from hides the drawing underneath both.
    const cfg=SITE_AREA_CONFIG[shape.areaType]||SITE_AREA_CONFIG["custom-area"];
    setSiteLogistics(prev=>({...prev,areas:[...prev.areas,{
      id,areaType:"custom-area",points:pts,label,
      color:shape.fillColor||shape.color||cfg.color,
      fillColor:shape.fillColor||shape.color||cfg.color,
      fillOpacity:0,strokeWidth:shape.strokeWidth??1.5}]}));
  }else{
    // A custom line rather than another one of whatever it came from: a
    // setback is not a second property line, and the key should not say
    // "Property Line x2".
    setSiteLogistics(prev=>({...prev,lines:[...prev.lines,{
      id,lineType:"custom",points:pts,label,
      strokeColor:shape.strokeColor||SITE_LINE_CONFIG[shape.lineType as SiteLineType].color,
      strokeWidth:shape.strokeWidth,strokeDash:"6,4"}]}));
  }
  setSelectedLogId(id);
  setOffsetAsk(null); setOffsetInput("");
  say(`${label} drawn`);
};

const endCalibration=()=>{
  setCalibAsk(null);
  setCalibPoints([]);
  setCalibInput("");
  setAreaPreview(null);
  setLogisticsTool("select");
};

const handleLineClick=(e:React.MouseEvent,ln:SiteLine)=>{
  e.stopPropagation();
  if(tool==="select")setSelectedLogId(ln.id);
};

/**
 * A site drawing, from the file somebody picked.
 *
 * Every step says so while it runs, because reading and rendering a sheet is
 * slow enough that silence reads as failure -- people uploaded the same file
 * twice. Failures say so too, in a toast rather than a browser alert, and the
 * input is emptied either way so the same file can be picked again after one.
 */
const handleImageUpload=async(e:React.ChangeEvent<HTMLInputElement>)=>{
  const input=e.target;
  const file=input.files?.[0];
  // Cleared before anything else: a second pick of the same file fires no
  // change event otherwise, so a failed upload could not be retried.
  input.value="";
  if(!file)return;

  /** Hand the sheet over -- or stand down, if it is the one already up. */
  const show=(dataUrl:string,dpi:number)=>{
    // Re-uploading the identical drawing changes no src, so no load event
    // would ever arrive to take the notice down.
    if(siteLogistics.imageDataUrl===dataUrl){setImporting(null);return;}
    setImporting("Drawing the sheet\u2026");
    setSiteLogistics(prev=>({...prev,imageDataUrl:dataUrl,imageDpi:dpi}));
  };
  /** Give up, with a reason. */
  const give=(msg:string)=>{setImporting(null);say(msg);};

  if(file.type==="application/pdf"||file.name.toLowerCase().endsWith(".pdf")){
    // Render first page of PDF to canvas, then use as image
    setImporting("Reading the PDF\u2026");
    try{
      const pdfjs=await import("pdfjs-dist");
      const workerMod=await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
      pdfjs.GlobalWorkerOptions.workerSrc=workerMod.default;
      const buf=await file.arrayBuffer();
      const pdf=await pdfjs.getDocument({data:buf}).promise;
      setImporting("Rendering page 1\u2026");
      const page=await pdf.getPage(1);
      const scale=2; // 2x for sharpness
      const vp=page.getViewport({scale});
      const canvas=document.createElement("canvas");
      canvas.width=vp.width; canvas.height=vp.height;
      const ctx=canvas.getContext("2d")!;
      ctx.fillStyle="#ffffff";
      ctx.fillRect(0,0,vp.width,vp.height);
      await page.render({canvasContext:ctx,viewport:vp,canvas}).promise;
      show(canvas.toDataURL("image/png"),72*scale);   // a PDF point is 1/72"
    }catch(err){
      console.error("PDF render error:",err);
      give("Could not render that PDF \u2014 try exporting it as a PNG or JPG and uploading that instead");
    }
  } else {
    setImporting("Reading the file\u2026");
    const reader=new FileReader();
    reader.onload=ev=>{
      const url=ev.target?.result;
      if(typeof url==="string")show(url,96);   // a screen image: 96 is the convention
      else give(`Could not read ${file.name}`);
    };
    reader.onerror=()=>give(`Could not read ${file.name}`);
    reader.readAsDataURL(file);
  }
};

/**
 * The plan as a file: the drawing, its marks, and a key underneath.
 *
 * A sheet leaves the screen constantly -- printed for the trailer wall, sent
 * to a sub, attached to a permit -- and the legend lives in a side panel that
 * does not go with it. TRL and DMP on a page with nothing to decode them is a
 * sheet somebody has to explain in person.
 */
/** Says what happened, falling back to an alert where no toast exists. */
const say=(m:string,undoable=false)=>{
  if(showToast)showToast(m,undoable?6000:2200,undoable); else window.alert(m);
};

/**
 * This plan, drawn onto a canvas: the sheet, its marks and a key underneath.
 *
 * Separated from saving because a phased job is exported as a set, and the
 * pages of that set are made exactly the way a single sheet is.
 */
/** A rendered sheet, and how big it is on paper when that is knowable. */
interface RenderedSheet{canvas:HTMLCanvasElement;pageW?:number;pageH?:number}

const renderPlanToCanvas=async(plan:SitePlan):Promise<RenderedSheet|null>=>{
  const svg=logSvgRef.current; if(!svg)return null;
  const container=svg.parentElement as HTMLElement;
  const w=container.clientWidth, h=container.clientHeight;
  if(!w||!h){say("Nothing to export yet");return null;}

  // Everything about the sheet comes from the plan handed in, not from the
  // active one. Reading `current` here meant a set exported while stepping
  // through the plans captured whichever one the closure was built around --
  // every page the same sheet, with the same name on it.
  const rows=legendRows(plan as any);
  const legendW=(width:number)=>Math.max(1,Math.min(4,Math.floor(width/190)));
  // Every sheet says which plan it is and when it is in effect. A page pulled
  // out of a set and pinned to a wall has to be able to answer that alone.
  const caption=[plan.name,phaseRange(phaseOf(plan),true)].filter(Boolean).join("  ·  ");
  const planFpp=feetPerPixel(normalizeScale(plan.scale as any,liveCanvas),liveCanvas);
  const preset=SCALE_PRESETS.find(p=>p.id===plan.scale?.preset);
  const scaleText=!planFpp?"":plan.scale?.preset==="custom"?"Calibrated":(preset?.label||"");

  // Nothing is appended under the drawing any more. The legend, the title
  // block and the scale bar are on the sheet, drawn into the same SVG as
  // every other mark, so the exported page is exactly the shape the sheet
  // was -- which is what lets it be printed at the size it was drawn for.

  // Rendered at the uploaded sheet's own pixel size rather than at whatever
  // the window happened to be. A sheet exported at screen size prints at a
  // scale that depends on how wide the browser was, which is no scale at all.
  const outW=imgNaturalW||w;
  const outH=imgNaturalW?(imgNaturalH||Math.round(h*(imgNaturalW/w))):h;
  const SCALE=imgNaturalW?1:2;
  const canvas=document.createElement("canvas");
  canvas.width=outW*SCALE; canvas.height=outH*SCALE;
  const ctx=canvas.getContext("2d");
  if(!ctx){say("Could not prepare the image");return null;}
  ctx.scale(SCALE,SCALE);

  /** An image drawn, or an honest failure. Without the error leg a source
   *  that will not load leaves the export waiting for an onload that never
   *  comes, with nothing on screen to say so. */
  const draw=(src:string,dw:number,dh:number)=>new Promise<void>((res,rej)=>{
    const img=new Image();
    const done=()=>{clearTimeout(timer);};
    const timer=setTimeout(()=>{rej(new Error("Timed out preparing the drawing"));},15000);
    img.onload=()=>{done();ctx.drawImage(img,0,0,dw,dh);res();};
    img.onerror=()=>{done();rej(new Error("Could not read the drawing"));};
    img.src=src;
  });

  try{
    ctx.fillStyle=plan.imageDataUrl?"#ffffff":T.bgPanel;
    ctx.fillRect(0,0,outW,outH);
    if(plan.imageDataUrl)await draw(plan.imageDataUrl,outW,outH);

    // Serialised with its size written in. An SVG with no width, height or
    // viewBox has no intrinsic dimensions, and a browser asked to rasterise
    // one either guesses or refuses -- which is how an export comes out with
    // the plan on it and none of the marks.
    const clone=svg.cloneNode(true) as SVGSVGElement;
    // The marks were authored in the on-screen coordinate space; the viewBox
    // says so and the browser scales them up to the sheet's real size.
    clone.setAttribute("width",String(outW));
    clone.setAttribute("height",String(outH));
    clone.setAttribute("viewBox",`0 0 ${w} ${h}`);
    clone.setAttribute("xmlns","http://www.w3.org/2000/svg");
    // A text box open for editing is a <foreignObject> holding a live
    // textarea; it cannot be rasterised and is not part of the drawing.
    clone.querySelectorAll("foreignObject").forEach(n=>n.remove());
    const svgUrl=URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)],
      {type:"image/svg+xml;charset=utf-8"}));
    try{ await draw(svgUrl,outW,outH); } finally { URL.revokeObjectURL(svgUrl); }

  }catch(err:any){
    say(err?.message||"Could not prepare the drawing");
    return null;
  }
  // What this is on paper. A named scale fixes it exactly; a calibrated one
  // does not, because nothing in it says how big the paper was meant to be,
  // so the page falls back to the image's pixels.
  const presetFt=SCALE_PRESETS.find(sp=>sp.id===plan.scale?.preset)?.feetPerPx;
  const inches=sheetInchesFor(
    normalizeScale(plan.scale as any,liveCanvas).feetPerPct,
    presetFt?presetFt*96:null)
    ??(imgNaturalW?imgNaturalW/sheetDpi:null);
  const pageW=inches?Math.round(inches*72):undefined;
  const pageH=pageW?Math.round(pageW*(outH/outW)):undefined;
  return {canvas,pageW,pageH};
};

const handleExport=async()=>{
  const sheet=await withoutSelection(()=>renderPlanToCanvas(current));
  if(!sheet)return;
  const {canvas}=sheet;
  // The format is chosen in the save dialog, where the dropdown already lives,
  // rather than being decided by the name of a button.
  const stem=`${current.name||"Site Logistics"}`.replace(/[\/:*?"<>|]/g,"-");
  const target=await pickSaveTarget(stem,"pdf");
  if(!target)return;                       // dismissed
  try{
    await target.write(target.format==="pdf"
      ?new Blob([jpegsToPdf([{jpeg:new Uint8Array(await (await canvasToBlob(canvas,"jpeg")).arrayBuffer()),
          width:canvas.width,height:canvas.height,pageW:sheet.pageW,pageH:sheet.pageH}]).buffer.slice(0) as ArrayBuffer],
        {type:"application/pdf"})
      :await canvasToBlob(canvas,target.format));
    say(`Saved ${target.filename}`);
  }catch(e:any){
    if(e?.name!=="AbortError")say(e?.message||"Could not save the file.");
  }
};

/**
 * Runs something with nothing selected, then puts the selection back.
 *
 * The export serialises the live SVG, selection chrome and all, so a sheet
 * saved while something was selected came out with blue grips, a dashed
 * bounding box and vertex handles printed on the drawing -- on the copy that
 * goes to the owner. Handles are not part of the drawing and must not be in
 * the file.
 */
const withoutSelection=async<T,>(fn:()=>Promise<T>):Promise<T>=>{
  const was=selectedLogId;
  if(was!==null){ setSelectedLogId(null); await nextPaint(); }
  try{ return await fn(); }
  finally{ if(was!==null) setSelectedLogId(was); }
};

/** Two frames, which is what it takes for a switched plan to be on screen. */
const nextPaint=()=>new Promise<void>(res=>
  requestAnimationFrame(()=>requestAnimationFrame(()=>res())));

/**
 * The whole sequence as one PDF, a page per plan, in the order of the tabs.
 *
 * Handed over as separate files, a sequence is a sequence somebody prints out
 * of order. One document in tab order is the thing an owner can read end to
 * end, and each page carries its own name and dates.
 *
 * Each plan is made current for a moment and rendered the way the live sheet
 * is, which is why the tabs flick past while it runs: the drawing exists in
 * the DOM, and rendering one that is not on screen would mean a second
 * renderer quietly drifting from the first.
 */
const handleExportAll=async()=>{
  const plans=allPlans;
  if(plans.length<2){await handleExport();return;}
  const wasActive=current.id;
  const pages:Array<{jpeg:Uint8Array;width:number;height:number;pageW?:number;pageH?:number}>=[];
  setExporting(true);
  try{
    for(const pl of plans){
      goPlan(pl.id);
      await nextPaint();
      const sheet=await withoutSelection(()=>renderPlanToCanvas(pl));
      if(!sheet)return;
      const blob=await canvasToBlob(sheet.canvas,"jpeg");
      pages.push({jpeg:new Uint8Array(await blob.arrayBuffer()),
        width:sheet.canvas.width,height:sheet.canvas.height,
        pageW:sheet.pageW,pageH:sheet.pageH});
    }
  }catch(e:any){
    say(e?.message||"Could not prepare the drawings");
    return;
  }finally{
    goPlan(wasActive);
    setExporting(false);
  }

  const site=(address||"").split(",")[0].trim();
  const stem=(site?`${site} — Site Logistics`:"Site Logistics").replace(/[\/:*?"<>|]/g,"-");
  const target=await pickSaveTarget(stem,"pdf");
  if(!target)return;
  try{
    const pdf=jpegsToPdf(pages);
    await target.write(new Blob([pdf.buffer.slice(0) as ArrayBuffer],{type:"application/pdf"}));
    say(`Saved ${target.filename} — ${pages.length} sheets`);
  }catch(e:any){
    if(e?.name!=="AbortError")say(e?.message||"Could not save the file.");
  }
};


/**
 * The colour of a handle.
 *
 * Handles are not part of the drawing, so they do not take the element's
 * colour the way a label or a leader does: one blue for everything you grab
 * means a grip is recognisable before it is read, whatever it is sitting on.
 */
const HANDLE_BLUE="#3d87d6";

/** How much room the graphic scale takes at the foot of the sheet. */
const BAR_H=30;






const iconCategories=["Facilities","Equipment","Site","Utilities"] as const;
const tbBtnS:React.CSSProperties={background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,padding:"5px 10px",fontSize:11,fontFamily:FONT,cursor:"pointer",whiteSpace:"nowrap"};

const togglePanel=(k:string,def=true)=>setLogPanelCollapsed(p=>({...p,[k]:!(p[k]==null?def:p[k])}));
const panelOpen=(k:string,def=true)=>logPanelCollapsed[k]==null?def:logPanelCollapsed[k];
const SectionHeader=({id,label,def=true}:{id:string;label:string;def?:boolean})=>(
  <div onClick={()=>togglePanel(id,def)}
    style={{display:"flex",alignItems:"center",justifyContent:"space-between",
      padding:"5px 10px 5px 12px",cursor:"pointer",userSelect:"none" as const,
      borderTop:`1px solid ${T.borderSecondary}`,
      background:panelOpen(id,def)?T.bgAccentFaint:"none"}}>
    <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as const}}>{label}</span>
    <span style={{fontSize:10,color:T.textWeak,opacity:.7}}>{panelOpen(id,def)?"\u25be":"\u25b8"}</span>
  </div>
);

/**
 * The grip that turns a shape, on a stalk above it.
 *
 * Drawn in screen units, where both axes mean the same thing, so the arm is
 * the same length whichever way the shape faces. The angle itself is worked
 * out in plan percentages, which is where the canvas proportions matter.
 */
/**
 * A padlock, drawn on whatever is selected and locked.
 *
 * Without it a locked element is indistinguishable from a broken one: the
 * drag does nothing and nothing on screen says why.
 */
const LockBadge=({cx,cy,color}:{cx:number;cy:number;color:string})=>(
  <g transform={`translate(${cx},${cy})`} style={{pointerEvents:"none"}}>
    <title>Locked — right-click to unlock</title>
    <rect x={-5.5} y={-1.5} width={11} height={8.5} rx={1.8} fill={color} opacity={.95}/>
    <path d="M -3 -1.5 V -3.6 A 3 3 0 0 1 3 -3.6 V -1.5" fill="none"
      stroke={color} strokeWidth={1.5} strokeOpacity={.95}/>
  </g>
);

const RotateGrip=({cx,cy,deg,color,onDown}:{cx:number;cy:number;deg:number;color:string;
  onDown:(e:React.MouseEvent)=>void})=>{
  const arm=26;
  const g=handleAt({x:cx,y:cy},arm,deg,1);
  return(
    <g style={{cursor:"grab"}} onMouseDown={onDown}>
      <title>Drag to rotate · hold Shift for 15° steps</title>
      <line x1={cx} y1={cy} x2={g.x} y2={g.y} stroke={chromeOn(color)} strokeWidth={1} strokeOpacity={.6} strokeDasharray="3,2"/>
      <circle cx={g.x} cy={g.y} r={11} fill="transparent"/>
      <circle cx={g.x} cy={g.y} r={6} fill={HANDLE_BLUE} stroke="#ffffff" strokeWidth={1.75}/>
      {/* A arc with a tick: the universal "this turns" mark. */}
      <path d={`M ${g.x-3} ${g.y-1.4} A 3.2 3.2 0 1 1 ${g.x+1.1} ${g.y+2.9}`}
        fill="none" stroke="#ffffff" strokeWidth={1.3} strokeLinecap="round"/>
      <path d={`M ${g.x-4.4} ${g.y-2.9} L ${g.x-2.9} ${g.y-0.6} L ${g.x-0.9} ${g.y-2.2}`}
        fill="none" stroke="#ffffff" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  );
};

const svgInner=(()=>{
            // Always read live SVG dimensions so map-mode and plan-mode coordinates align correctly.
            // logSvgDims can lag when logisticsView changes; reading clientWidth/Height from the ref
            // is synchronous and always reflects the current rendered size.
            const W=(logSvgRef.current?.clientWidth||0)||logSvgDims.w;
            const H=(logSvgRef.current?.clientHeight||0)||logSvgDims.h;
            const px=(p:{x:number;y:number})=>({x:(p.x/100)*W, y:(p.y/100)*H});
            // Paper, not pixels. A label is printed, so it is sized in points
            // of the sheet it will be printed on: 10pt reads as 10pt whether
            // the drawing is letter or ARCH D, instead of taking the same
            // share of a sheet that got three times bigger.
            // Paper size, times whatever this sheet needs its marks scaled
            // by. Applied here rather than to the type alone, so a label, the
            // box it sits in and the key down the side all grow together --
            // scaling the words inside a fixed box only overflows it.
            const IN=unitsPerInch(W,sheetInches?.w)*markup;
            const PT=pt(IN);
            const TXT=(points:number)=>points*PT;
            const ptsStr=(pts:{x:number;y:number}[])=>pts.map(p=>`${(p.x/100)*W},${(p.y/100)*H}`).join(" ");
            // The per-vertex smooth flag has to survive the trip into drawn
            // units, or every corner comes out sharp.
            const pxPts=(pts:any[])=>pts.map(p=>({...px(p),curve:p.curve,
              ...(p.hIn?{hIn:{x:(p.hIn.x/100)*W,y:(p.hIn.y/100)*H}}:{}),
              ...(p.hOut?{hOut:{x:(p.hOut.x/100)*W,y:(p.hOut.y/100)*H}}:{})}));
            const dOf=(pts:any[],closed:boolean,legacyCurved?:boolean)=>
              pathFromPoints(pxPts(withLegacyCurve(pts,legacyCurved)),closed);

            /**
             * The selection box and its corner grips.
             *
             * A shape is a list of vertices, but the thing people reach for
             * to make the whole of it bigger is the box around it. Holding
             * Shift turns the corners into diagonal arrows and scales from
             * the opposite one -- the same gesture, and the same cursor, as
             * every markup tool.
             */
            /**
             * The two bezier handles on a curved vertex.
             *
             * Drawn where the curve's control points actually are, whether
             * those were dragged or are still implied by the neighbours --
             * so grabbing one starts from the shape on screen rather than
             * snapping it somewhere first.
             */
            const bezierHandles=(kind:"line"|"area",id:string,pts:any[],closed:boolean)=>(
              <g>
                {pts.map((v:any,i:number)=>{
                  if(!v.curve)return null;
                  const h=handlesOf(pts,i,closed);
                  if(!h)return null;
                  const at=px(v);
                  return(["out","in"] as const).map(which=>{
                    const off=h[which];
                    const hp=px({x:v.x+off.x,y:v.y+off.y});
                    return(
                      <g key={`${i}-${which}`}>
                        <line x1={at.x} y1={at.y} x2={hp.x} y2={hp.y}
                          stroke={HANDLE_BLUE} strokeOpacity={.55} strokeWidth={1}
                          strokeDasharray="3,2" style={{pointerEvents:"none"}}/>
                        <g style={{cursor:"grab"}}
                          onMouseDown={(e)=>handleBezierMouseDown(e,kind,id,i,which)}>
                          <title>Drag to reshape the curve · Alt to move this side alone</title>
                          <circle cx={hp.x} cy={hp.y} r={8} fill="transparent"/>
                          <circle cx={hp.x} cy={hp.y} r={3.5}
                            fill={HANDLE_BLUE} stroke="#ffffff" strokeWidth={1.2}/>
                        </g>
                      </g>
                    );
                  });
                })}
              </g>
            );

            const scaleBox=(kind:"line"|"area",id:string,pts:{x:number;y:number}[])=>{
              const b=boundsOf(pts);
              if(!b)return null;
              const tl=px({x:b.x,y:b.y}), br=px({x:b.x+b.w,y:b.y+b.h});
              const corners:Corner[]=["nw","ne","se","sw"];
              return(
                <g>
                  <rect x={tl.x} y={tl.y} width={br.x-tl.x} height={br.y-tl.y}
                    fill="none" stroke={chromeInk} strokeOpacity={.35}
                    strokeWidth={0.8} strokeDasharray="4,3"
                    style={{pointerEvents:"none"}}/>
                  {corners.map(c=>{
                    const p=px(cornerPoint(b,c));
                    const diag=c==="nw"||c==="se"?"nwse-resize":"nesw-resize";
                    return(
                      <g key={c} style={{cursor:shiftHeld?diag:"default"}}
                        onMouseDown={(e)=>{if(shiftHeld)handleScaleMouseDown(e,kind,id,c);}}>
                        <title>Hold Shift and drag to resize the whole shape</title>
                        <rect x={p.x-7} y={p.y-7} width={14} height={14} fill="transparent"/>
                        <rect x={p.x-4} y={p.y-4} width={8} height={8}
                          fill={HANDLE_BLUE} stroke="#ffffff" strokeWidth={1.4}
                          fillOpacity={shiftHeld?1:.75}/>
                      </g>
                    );
                  })}
                </g>
              );
            };

            /**
             * A label, wherever it has been pulled to, with a line back.
             *
             * Everything inside is positioned relative to the label's home, so
             * the callers draw what they always drew and this moves it. The
             * leader appears only once the label is far enough away to need
             * one -- a label sitting on its anchor does not need a line to
             * itself.
             */
            const Labelled=({id,kind,anchor,offset,color,halfW,halfH,rotate,tip,tipHalf,children}:{
              id:string;
              /** Which list the thing lives in, so its name can be changed. */
              kind:"line"|"area"|"icon"|"circle";
              anchor:{x:number;y:number};offset?:{x:number;y:number};
              color:string;halfW:number;halfH:number;rotate?:number;
              /** What the leader points at, when that is not where the label
               *  lives. An item's name sits under its box, so a leader drawn
               *  to the label's home ends in mid-air below the item instead
               *  of on it. */
              tip?:{x:number;y:number};tipHalf?:{w:number;h:number};
              children:React.ReactNode;
            })=>{
              const off=offset||{x:0,y:0};
              // The drag is measured on screen. An item's name is drawn inside
              // the group that turns with the item, so the offset has to be
              // turned back by the same angle or the label sets off in
              // whatever direction the item happens to be facing.
              const{x:dx,y:dy}=unrotateOffset({x:off.x/100*W,y:off.y/100*H},rotate||0);
              // Everything here is measured from the label's home. The leader
              // starts at the edge of whatever it points at, so it meets the
              // item's outline rather than running under it to the middle.
              const t=tip?{x:tip.x-anchor.x,y:tip.y-anchor.y}:{x:0,y:0};
              const from=tipHalf?edgePoint({x:dx,y:dy},t,tipHalf.w,tipHalf.h):t;
              const lead=leaderLine(from,{x:dx,y:dy},halfW,halfH,3);
              return(
                <g>
                  {lead&&<line x1={anchor.x+lead.from.x} y1={anchor.y+lead.from.y}
                    x2={anchor.x+lead.to.x} y2={anchor.y+lead.to.y}
                    stroke={chromeOn(color)} strokeWidth={1} strokeOpacity={.75}/>}
                  {lead&&<circle cx={anchor.x+from.x} cy={anchor.y+from.y} r={1.75} fill={chromeOn(color)}/>}
                  <g transform={`translate(${dx},${dy})`}>
                    {children}
                    {/* Something to actually hit.
                        A <g> has no geometry of its own, so pointer-events on
                        it does nothing: hits land on its children, and the
                        label's own text and chips set pointer-events to none
                        so they never steal a click meant for the shape they
                        sit on. This rectangle is what the pointer finds:
                        click to select what the label belongs to, double
                        click to rename it, and Shift-drag to move it clear
                        of whatever it is sitting on. */}
                    {tool==="select"&&editingLabel?.id!==id&&(
                      <rect x={anchor.x-halfW} y={anchor.y-halfH}
                        width={halfW*2} height={halfH*2}
                        fill="transparent" style={{cursor:shiftHeld?"move":"text"}}
                        onMouseDown={(e)=>{
                          if(shiftHeld){handleLabelMouseDown(e,id,off);return;}
                          e.stopPropagation();
                          setSelectedLogId(id);
                        }}
                        onDoubleClick={(e)=>{
                          e.stopPropagation(); e.preventDefault();
                          if(lockedById(siteLogistics,id)){say("That one is locked");return;}
                          setEditingLabel({kind,id});
                        }}>
                        <title>Double-click to rename{" "}Shift-drag to move</title>
                      </rect>
                    )}

                    {/* Renaming, in place.
                        Sized for the screen rather than for the paper: the
                        label itself may be two points tall on a D sheet, and
                        a two-point input is not a thing anyone can type in. */}
                    {editingLabel?.id===id&&(()=>{
                      const w=Math.max(halfW*2,150), h=26;
                      const start=(()=>{
                        const el:any=kind==="line"?siteLogistics.lines.find(l=>l.id===id)
                          :kind==="area"?siteLogistics.areas.find(a=>a.id===id)
                          :kind==="icon"?siteLogistics.icons.find(i=>i.id===id)
                          :siteLogistics.circles.find(c=>c.id===id);
                        return String(el?.label??"");
                      })();
                      const close=()=>setEditingLabel(null);
                      return(
                        <foreignObject x={anchor.x-w/2} y={anchor.y-h/2} width={w} height={h}>
                          <input autoFocus defaultValue={start}
                            onMouseDown={e=>e.stopPropagation()}
                            onBlur={e=>{renameElement(kind,id,e.currentTarget.value.trim());close();}}
                            onKeyDown={e=>{
                              if(e.key==="Enter"){e.preventDefault();renameElement(kind,id,e.currentTarget.value.trim());close();}
                              if(e.key==="Escape"){e.preventDefault();e.currentTarget.value=start;close();}
                            }}
                            style={{width:"100%",height:"100%",boxSizing:"border-box",fontSize:12,
                              fontFamily:FONT,padding:"3px 6px",borderRadius:3,outline:"none",
                              background:T.bgInput,color:T.textPrimary,
                              border:`1px solid ${T.borderAccent}`}}/>
                        </foreignObject>
                      );
                    })()}
                  </g>
                </g>
              );
            };

            return(<>
              {/* Rendered lines */}
              {siteLogistics.lines.map(ln=>{
                const cfg=SITE_LINE_CONFIG[ln.lineType];
                const lineColor=ln.strokeColor||cfg.color;
                const lineWidth=ln.strokeWidth??cfg.width;
                const lineDash=ln.strokeDash!==undefined?ln.strokeDash:(cfg.dash==="none"?"":cfg.dash);
                const lnPts=withLegacyCurve(ln.points,ln.curved);
                const d=dOf(ln.points,false,ln.curved);
                const isSelected=selectedLogId===ln.id&&tool==="select";
                const mid=ln.points[Math.floor(ln.points.length/2)];
                const midPx=mid?px(mid):null;
                return(
                  <g key={ln.id}>
                    {/* Wider invisible hit area for easier clicking */}
                    <path d={d} fill="none" stroke="transparent" strokeWidth={Math.max(lineWidth+8,12)}
                      style={{cursor:tool==="select"?"move":"pointer"}}
                      onMouseDown={(e)=>handleShapeMouseDown(e,"line",ln.id,ln.points[0])}
                      onClick={(e)=>handleLineClick(e,ln)}
                      onContextMenu={(e)=>openShapeMenu(e,"line",ln.id)}/>
                    {/* The clearance either side: the width a haul route
                        really takes, the easement a service needs. Drawn
                        under the line itself. */}
                    {(()=>{
                      if(!fpp||!ln.offsetFt||ln.offsetHidden)return null;
                      const dist=ln.offsetFt/fpp;
                      const pts=ln.points.map(pt=>({x:(pt.x/100)*W,y:(pt.y/100)*H}));
                      const which=ln.offsetSide||"both";
                      const sides=[
                        which==="right"?null:offsetPath(pts,dist,false),
                        which==="left"?null:offsetPath(pts,-dist,false),
                      ];
                      return sides.map((side,i)=>side&&(
                        <polyline key={`off${i}`} points={side.map(pt=>`${pt.x},${pt.y}`).join(" ")}
                          fill="none" stroke={lineColor} strokeWidth={1} strokeDasharray="7,5"
                          strokeOpacity={.7} style={{pointerEvents:"none"}}/>
                      ));
                    })()}
                    {isSelected&&<path d={d} fill="none" stroke={chromeInk} strokeWidth={lineWidth+3} strokeOpacity={0.3}/>}
                    <path d={d} fill="none" stroke={lineColor}
                      strokeWidth={lineWidth} strokeDasharray={lineDash}
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{pointerEvents:"none"}}/>
                    {midPx&&(showLabels||fmtLen(polyLen(ln.points)))&&(
                      <Labelled id={ln.id} kind="line" anchor={midPx} offset={ln.labelOffset} color={lineColor}
                        halfW={Math.max((ln.label?.length||4),6)*3} halfH={16}>
                        {showLabels&&<MapLabel x={midPx.x} y={midPx.y} dy={-TXT(9)} text={ln.label} color={lineColor} variant="halo" size={TXT(10)} surface={labelSurface}/>}
                        {fmtLen(polyLen(ln.points))&&<MapLabel x={midPx.x} y={midPx.y} dy={TXT(7)}
                          text={fmtLen(polyLen(ln.points))} color={lineColor} variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                      </Labelled>
                    )}
                    {isSelected&&!ln.locked&&scaleBox("line",ln.id,ln.points)}
                    {isSelected&&!ln.locked&&bezierHandles("line",ln.id,lnPts,false)}
                    {/* Vertex handles — only when this line is selected in select mode */}
                    {isSelected&&!ln.locked&&ln.points.map((p,i)=>{
                      const pp=px(p);
                      const isDraggingThis=logVertexDragRef.current?.id===ln.id&&logVertexDragRef.current?.vertIdx===i;
                      return(
                        <g key={i} style={{cursor:"crosshair"}}
                          onMouseDown={(e)=>handleVertexMouseDown(e,ln.id,i)}
                          onContextMenu={(e)=>openVertexMenu(e,"line",ln.id,i)}>
                          <title>Drag to move · right-click for options</title>
                          <circle cx={pp.x} cy={pp.y} r={10} fill="transparent"/>
                          {/* Round, always: a square means the bounding box
                              now, and one shape cannot mean two things on
                              the same selection. Smoothed and sharp are told
                              apart by the middle -- filled where the run
                              curves through, open where it turns a corner. */}
                          <circle cx={pp.x} cy={pp.y} r={6}
                            fill={isDraggingThis?chromeInk:handleFill}
                            stroke={chromeOn(lineColor)} strokeWidth={2}/>
                          {lnPts[i]?.curve&&<circle cx={pp.x} cy={pp.y} r={2.5} fill={lineColor}/>}
                        </g>
                      );
                    })}
                                      </g>
                );
              })}

              {/* In-progress line preview */}
              {isLineTool&&lineInProgress.length>0&&drawingLineType&&(()=>{
                const cfg=SITE_LINE_CONFIG[drawingLineType];
                const previewColor=logDrawStyle.strokeColor||cfg.color;
                const previewWidth=logDrawStyle.strokeWidth??cfg.width;
                const previewDash=logDrawStyle.strokeDash!==undefined?logDrawStyle.strokeDash:(cfg.dash==="none"?"":cfg.dash);
                const allPts=[...lineInProgress,...(linePreview?[linePreview]:[])];
                const pts=ptsStr(allPts);
                return(
                  <g style={{pointerEvents:"none"}}>
                    <polyline points={pts} fill="none" stroke={previewColor}
                      strokeWidth={previewWidth} strokeDasharray={previewDash}
                      strokeLinecap="round" strokeOpacity={0.75}/>
                    {lineInProgress.map((p,i)=>{const pp=px(p);return(
                      <circle key={i} cx={pp.x} cy={pp.y} r={4} fill={previewColor}/>
                    );})}
                  </g>
                );
              })()}

              {/* Placed icons */}
              {siteLogistics.icons.map(ic=>{
                const cfg=SITE_ICON_CONFIG[ic.type];
                const pp=px({x:ic.x,y:ic.y});
                const isSelected=selectedLogId===ic.id&&tool==="select";
                const ft=footprintFt(ic);
                // With a scale the item is drawn at the ground it takes; without
                // one there is no honest size, so it stays the symbol it was.
                const foot=footprintPx(ft,fpp);
                // Its own colour if it has been given one, the type's otherwise.
                const col=ic.color||cfg.color;
                // How much shows through, and the edge that holds it together.
                //
                // These used to be one `opacity` on the whole rect, which
                // faded the outline along with the fill -- and the outline was
                // the same colour as the fill, so a footprint at 45% had no
                // boundary at all and disappeared into a busy survey. The fill
                // is translucent, the edge is not.
                // Darkened only as far as it has to be to read on this sheet,
                // which keeps the colour saying which item it is.
                const edge=chromeOn(col);
                // Below a certain size on paper an item drawn true to its
                // footprint is a coloured dot. A four-foot porta-potty at
                // 1" = 20' is a fifth of an inch; on a D sheet of a four-acre
                // site nobody can tell it from the generator beside it. Under
                // the floor it is drawn as the symbol it already is on the
                // key, at a size that can be read -- and its real footprint
                // is drawn inside it, so the sheet never claims the symbol is
                // the ground it takes.
                const S=sheetInches?Math.max(.2*IN,10):28;
                const tiny=!!foot&&Math.max(foot.w,foot.h)<S;
                const toScale=!!foot&&!tiny;
                const fillOp=ic.fillOpacity??(toScale?0.45:0.92);
                const edgeW=ic.strokeWidth??(toScale?1.5:1);
                const bw=(foot&&!tiny)?Math.max(foot.w,3):S;
                const bh=(foot&&!tiny)?Math.max(foot.h,3):S;
                // The abbreviation is dropped once the box is too small to hold
                // it — three clipped letters read as a glitch, not a label.
                const abbrSize=Math.min(10,Math.max(0,Math.min(bw/(cfg.abbr.length*0.62),bh*0.7)));
                const showAbbr=abbrSize>=6;
                const rect={x:-bw/2,y:-bh/2,w:bw,h:bh};
                // Past a quarter turn either way the type would be read upside
                // down, so it flips. The box still faces where it faces.
                const upsideDown=(()=>{const d=normalizeAngle(ic.rotation||0);return d>90&&d<270;})();
                const ringPx=(fpp&&ic.offsetFt&&!ic.offsetHidden)?ic.offsetFt/fpp:0;
                return(
                  <g key={ic.id} transform={`translate(${pp.x},${pp.y}) rotate(${normalizeAngle(ic.rotation||0)})`}
                    style={{cursor:tool==="select"?"move":"default"}}
                    onMouseDown={(e)=>handleIconMouseDown(e,ic)}
                    onContextMenu={(e)=>openElementMenu(e,"icon",ic.id)}>
                    {/* What it reaches, or has to be kept clear of. Drawn
                        before the item so the item stays on top of its own
                        ring, and outside the label so it never turns with
                        the item -- a radius has no bearing. */}
                    {ringPx>0&&(
                      <g style={{pointerEvents:"none"}} transform={`rotate(${-normalizeAngle(ic.rotation||0)})`}>
                        <circle cx={0} cy={0} r={ringPx} fill={col} fillOpacity={.06}
                          stroke={edge} strokeWidth={1} strokeDasharray="7,5" strokeOpacity={.8}/>
                        <MapLabel x={0} y={-ringPx} text={`${formatFt(ic.offsetFt||0)} R`}
                          color={col} variant="quiet" size={TXT(8)} surface={labelSurface}/>
                      </g>
                    )}
                    {isSelected&&<rect x={-bw/2-3} y={-bh/2-3} width={bw+6} height={bh+6} rx={5}
                      fill="none" stroke={chromeInk} strokeWidth={1.5} strokeOpacity={0.45}/>}
                    <rect x={-bw/2} y={-bh/2} width={bw} height={bh} rx={toScale?2:4}
                      fill={col} fillOpacity={fillOp}
                      stroke={edgeW>0?edge:"none"} strokeWidth={edgeW}/>
                    {/* What it really takes, inside the symbol that stands
                        for it: the drawing says both, and says which is which. */}
                    {tiny&&foot&&(
                      <rect x={-foot.w/2} y={-foot.h/2} width={Math.max(foot.w,1)} height={Math.max(foot.h,1)}
                        fill="none" stroke={edge} strokeWidth={.6} strokeOpacity={.6}
                        style={{pointerEvents:"none"}}/>
                    )}
                    {showAbbr&&<text textAnchor="middle" dominantBaseline="central" fontSize={abbrSize}
                      fontWeight={800} fill={toScale?edge:readableOn(col)}
                      transform={upsideDown?"rotate(180)":undefined}
                      style={{pointerEvents:"none",userSelect:"none",fontFamily:FONT}}>
                      {cfg.abbr}
                    </text>}
                    {/* The name turns with the item, which is how a long
                        object is labelled on a real sheet -- but it flips
                        rather than ever being read upside down. */}
                    {showLabels&&(()=>{
                      const y=bh/2+11;
                      return(
                        <Labelled id={ic.id} kind="icon" anchor={{x:0,y}} offset={ic.labelOffset}
                          color={col} halfW={Math.max(ic.label?.length||4,4)*3} halfH={9}
                          rotate={normalizeAngle(ic.rotation||0)}
                          tip={{x:0,y:0}} tipHalf={{w:bw/2,h:bh/2}}>
                          <g transform={upsideDown?`rotate(180) translate(0,${-y*2})`:undefined}>
                            <MapLabel x={0} y={y} text={ic.label} color={col} size={TXT(9)}
                              variant="halo" surface={labelSurface}/>
                          </g>
                        </Labelled>
                      );
                    })()}
                    {isSelected&&!ic.locked&&foot&&HANDLES.map(h=>{
                      const hp=handlePoint(rect,h);
                      return(
                        <g key={h} style={{cursor:handleCursor(h)}}
                          onMouseDown={(e)=>handleResizeMouseDown(e,ic.id,h)}>
                          <circle cx={hp.x} cy={hp.y} r={9} fill="transparent"/>
                          <rect x={hp.x-3.5} y={hp.y-3.5} width={7} height={7} rx={1.5}
                            fill={handleFill} stroke={edge} strokeWidth={1.75}/>
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {/* Rendered dimensions.

                  Drawn after the shapes and before the labels: a dimension is
                  a statement about the drawing underneath it, so it sits on
                  top of what it measures. */}
              {(siteLogistics.dimensions||[]).map(dm=>{
                const pa=px(dm.a), pb=px(dm.b);
                const g=dimGeometry(pa,pb,(dm.offset||0)/100*W);
                if(!g)return null;
                const isSelected=selectedLogId===dm.id&&tool==="select";
                const col=dm.color||chromeInk;
                const text=dimensionText(g.lengthPx,fpp);
                const tint=col;
                return(
                  <g key={dm.id}>
                    {/* The hit area, wide enough to grab without covering the
                        drawing it is measuring. */}
                    <line x1={g.da.x} y1={g.da.y} x2={g.db.x} y2={g.db.y}
                      stroke="transparent" strokeWidth={14}
                      style={{cursor:tool==="select"?"move":"crosshair"}}
                      onMouseDown={(e)=>handleShapeMouseDown(e,"dimension",dm.id,dm.a)}
                      onContextMenu={(e)=>openElementMenu(e,"dimension",dm.id)}/>
                    {extensionLines(g).map((ln,i)=>(
                      <line key={i} x1={ln.from.x} y1={ln.from.y} x2={ln.to.x} y2={ln.to.y}
                        stroke={tint} strokeWidth={0.8} strokeOpacity={.75}
                        style={{pointerEvents:"none"}}/>
                    ))}
                    <line x1={g.da.x} y1={g.da.y} x2={g.db.x} y2={g.db.y}
                      stroke={tint} strokeWidth={isSelected?1.6:1.1}
                      style={{pointerEvents:"none"}}/>
                    {/* Ticks rather than arrowheads: a 45-degree slash is what
                        a surveyed drawing uses, and it stays legible at the
                        size these are read at. */}
                    {[g.da,g.db].map((p,i)=>(
                      <line key={i} x1={p.x-4} y1={p.y+4} x2={p.x+4} y2={p.y-4}
                        transform={`rotate(${g.angleDeg} ${p.x} ${p.y})`}
                        stroke={tint} strokeWidth={1.4} style={{pointerEvents:"none"}}/>
                    ))}
                    {text&&(
                      <g transform={`rotate(${g.angleDeg} ${g.mid.x} ${g.mid.y})`}
                        style={{pointerEvents:"none"}}>
                        <MapLabel x={g.mid.x} y={g.mid.y} dy={-TXT(4)} text={text}
                          color={tint} variant="halo" size={TXT(10)} surface={labelSurface}/>
                      </g>
                    )}
                    {/* Both ends, draggable, so a dimension can be snapped
                        onto the corners it is checking. */}
                    {isSelected&&!dm.locked&&([["a",pa],["b",pb]] as const).map(([end,p])=>(
                      <g key={end} style={{cursor:"crosshair"}}
                        onMouseDown={(e)=>handleDimEndMouseDown(e,dm.id,end)}>
                        <title>Drag to move this end</title>
                        <circle cx={p.x} cy={p.y} r={9} fill="transparent"/>
                        <circle cx={p.x} cy={p.y} r={3.5} fill={handleFill}
                          stroke={chromeOn(col)} strokeWidth={1.75}/>
                      </g>
                    ))}
                  </g>
                );
              })}

              {/* Rotation grips for items, drawn outside the rotated group so
                  the grip itself stays upright and easy to grab. */}
              {siteLogistics.icons.map(ic=>{
                if(selectedLogId!==ic.id||tool!=="select"||ic.locked)return null;
                const cfg=SITE_ICON_CONFIG[ic.type];
                const pp=px({x:ic.x,y:ic.y});
                const foot=footprintPx(footprintFt(ic),fpp);
                const bh=foot?Math.max(foot.h,3):28;
                const deg=normalizeAngle(ic.rotation||0);
                const stalk=handleAt({x:0,y:0},bh/2+4,deg,1);
                // The readouts stay level whatever the item is doing. They
                // report on the item rather than label the drawing, and a
                // dimension read upside down is worse than none.
                const ft=footprintFt(ic);
                const reach=Math.hypot(foot?foot.w:28,bh)/2;
                return(
                  <g key={`rot-${ic.id}`}>
                    {foot&&<MapLabel x={pp.x} y={pp.y-reach-TXT(9)} text={describeFootprint(ft)}
                      color={cfg.color} variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                    {isRotated(ic.rotation)&&<MapLabel x={pp.x} y={pp.y-reach-TXT(foot?21:11)}
                      text={formatAngle(deg)} color={cfg.color} variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                    <RotateGrip cx={pp.x+stalk.x} cy={pp.y+stalk.y}
                      deg={deg} color={cfg.color}
                      onDown={(e)=>handleRotateMouseDown(e,"icon",ic.id,{x:ic.x,y:ic.y},deg)}/>
                  </g>
                );
              })}

              {/* Rendered areas */}
              {siteLogistics.areas.map(ar=>{
                const cfg=SITE_AREA_CONFIG[ar.areaType]||SITE_AREA_CONFIG["custom-area"];
                // Falls back to the area type's own colour. An area saved
                // without one was drawn with no fill attribute at all, which
                // SVG renders as black -- so a laydown zone came out a muddy
                // grey instead of yellow, and nothing said why.
                const fillColor=ar.fillColor||ar.color||cfg.color;
                const fillOpacity=ar.fillOpacity??cfg.fillOpacity;
                const arPts=withLegacyCurve(ar.points,ar.curved);
                const d=dOf(ar.points,true,ar.curved);
                const isSelected=selectedLogId===ar.id&&tool==="select";
                const cx=ar.points.reduce((s,p)=>s+p.x,0)/ar.points.length;
                const cy=ar.points.reduce((s,p)=>s+p.y,0)/ar.points.length;
                const cpx=px({x:cx,y:cy});
                const areaPx=polyArea(ar.points);
                return(
                  <g key={ar.id} style={{cursor:tool==="select"?"move":"default"}}
                    onMouseDown={(e)=>handleShapeMouseDown(e,"area",ar.id,ar.points[0])}>
                    {isSelected&&<path d={d} fill="none" stroke={chromeInk} strokeWidth={2} strokeOpacity={0.4} strokeDasharray="6,3"/>}
                    <path d={d} fill={fillColor} fillOpacity={fillOpacity}
                      stroke={fillColor} strokeWidth={ar.strokeWidth??1.5} strokeOpacity={0.7}
                      strokeLinejoin="round"
                      onContextMenu={(e)=>openShapeMenu(e,"area",ar.id)}/>
                    {isSelected&&!ar.locked&&scaleBox("area",ar.id,ar.points)}
                    {isSelected&&!ar.locked&&bezierHandles("area",ar.id,arPts,true)}
                    {isSelected&&!ar.locked&&ar.points.map((vp,i)=>{
                      const h=px(vp);
                      return(
                        <g key={i} style={{cursor:"grab"}}
                          onMouseDown={(e)=>handleVertexMouseDown(e,ar.id,i,"area")}
                          onContextMenu={(e)=>openVertexMenu(e,"area",ar.id,i)}>
                          <circle cx={h.x} cy={h.y} r={5.5} fill={handleFill}
                            stroke={chromeOn(fillColor)} strokeWidth={2}/>
                          {arPts[i]?.curve&&<circle cx={h.x} cy={h.y} r={2.3} fill={fillColor}/>}
                        </g>
                      );
                    })}
                    <Labelled id={ar.id} kind="area" anchor={cpx} offset={ar.labelOffset} color={fillColor}
                      halfW={Math.max((ar.label?.length||4),6)*3} halfH={20}>
                      <MapLabel x={cpx.x} y={cpx.y} text={ar.label} color={fillColor} variant="halo" size={TXT(10)} surface={labelSurface}/>
                      {fmtArea(areaPx)&&<MapLabel x={cpx.x} y={cpx.y} dy={TXT(15)} text={fmtArea(areaPx)}
                        color={fillColor} variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                    </Labelled>
                  </g>
                );
              })}

              {/* Area in-progress preview */}
              {isAreaTool&&areaInProgress.length>0&&(()=>{
                const cfg=SITE_AREA_CONFIG[drawingAreaType||"custom-area"]||SITE_AREA_CONFIG["custom-area"];
                const allPts=[...areaInProgress,...(areaPreview?[areaPreview]:[])];
                const pts=ptsStr(allPts);
                return(
                  <g style={{pointerEvents:"none"}}>
                    <polygon points={pts} fill={cfg.color} fillOpacity={cfg.fillOpacity*0.6}
                      stroke={cfg.color} strokeWidth={1.5} strokeDasharray="5,3" strokeOpacity={0.8}/>
                    {areaInProgress.map((p,i)=>{const pp=px(p);return(
                      <circle key={i} cx={pp.x} cy={pp.y} r={4} fill={cfg.color}/>
                    );})}
                  </g>
                );
              })()}

              {/* Rendered circles */}
              {siteLogistics.circles.map(ci=>{
                const cpx=px({x:ci.cx,y:ci.cy});
                const isSelected=selectedLogId===ci.id&&tool==="select";
                // A radius saved in screen pixels meant a different distance at
                // every window size; one saved in percent does not.
                const rpx=ci.rPct!=null?ci.rPct/100*W:ci.r;
                const areaPx2=Math.PI*rpx*rpx;
                return(
                  <g key={ci.id} style={{cursor:tool==="select"?"move":"default"}}
                    onMouseDown={(e)=>handleShapeMouseDown(e,"circle",ci.id,{x:ci.cx,y:ci.cy})}
                    onContextMenu={(e)=>openElementMenu(e,"circle",ci.id)}>
                    {isSelected&&<circle cx={cpx.x} cy={cpx.y} r={rpx+3}
                      fill="none" stroke={chromeInk} strokeWidth={1.5} strokeOpacity={0.4} strokeDasharray="6,3"/>}
                    <circle cx={cpx.x} cy={cpx.y} r={rpx}
                      fill={ci.color} fillOpacity={0.18}
                      stroke={ci.color} strokeWidth={ci.strokeWidth??2} strokeDasharray={ci.strokeDash||""}/>
                    <circle cx={cpx.x} cy={cpx.y} r={3} fill={ci.color}/>
                    <Labelled id={ci.id} kind="circle" anchor={{x:cpx.x,y:cpx.y-rpx-9}} offset={ci.labelOffset}
                      color={ci.color} halfW={Math.max((ci.label?.length||4),6)*3} halfH={20}>
                      <MapLabel x={cpx.x} y={cpx.y-rpx-9} text={ci.label} color={ci.color} variant="halo" size={TXT(10)} surface={labelSurface}/>
                    {fmtLen(rpx)&&<MapLabel x={cpx.x} y={cpx.y-rpx-TXT(22)}
                      text={`r=${fmtLen(rpx)}${fmtArea(areaPx2)?` · ${fmtArea(areaPx2)}`:""}`}
                      color={ci.color} variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                    </Labelled>
                  </g>
                );
              })}

              {/* Free text */}
              {(siteLogistics.texts||[]).map(tx=>{
                const tp=px({x:tx.x,y:tx.y});
                const isSelected=selectedLogId===tx.id&&tool==="select";
                const editing=editingTextId===tx.id;
                const size=tx.size??13;
                const color=tx.color||T.textPrimary;
                const lines=(tx.text||"").split("\n");
                const anchor=tx.align==="center"?"middle":tx.align==="right"?"end":"start";
                // Measured off the glyph box rather than the DOM: the box has to
                // be laid out in the same pass that draws the text.
                const wide=Math.max(1,...lines.map(l=>l.length))*size*0.56;
                const tall=lines.length*size*1.35;
                const padX=6,padY=4;
                const boxX=anchor==="middle"?tp.x-wide/2-padX:anchor==="end"?tp.x-wide-padX:tp.x-padX;
                if(editing){
                  const boxW=Math.max(wide+padX*2+40,160);
                  const boxH=Math.max(tall+padY*2+8,46);
                  const barH=12;
                  return(
                    // The bar across the top is what makes an open box
                    // movable. Without it the box is a textarea and nothing
                    // else, so the obvious thing to do after typing a note --
                    // drag it where it belongs -- silently does nothing.
                    <foreignObject key={tx.id} x={boxX} y={tp.y-padY-size*0.95-barH}
                      width={boxW} height={boxH+barH}>
                      <div style={{width:"100%",height:"100%",boxSizing:"border-box"}}>
                        <div
                          onMouseDown={(e)=>{
                            e.stopPropagation();
                            setEditingTextId(null);
                            handleShapeMouseDown(e as any,"text",tx.id,{x:tx.x,y:tx.y});
                          }}
                          title="Drag to move"
                          style={{height:barH,cursor:"move",borderRadius:"3px 3px 0 0",
                            background:T.borderAccent,opacity:.85,display:"flex",
                            alignItems:"center",justifyContent:"center",gap:3}}>
                          {[0,1,2].map(i=>(
                            <span key={i} style={{width:3,height:3,borderRadius:"50%",
                              background:T.textOnGold,opacity:.7}}/>
                          ))}
                        </div>
                        <textarea autoFocus value={tx.text}
                          onChange={e=>setSiteLogistics(prev=>({...prev,
                            texts:(prev.texts||[]).map(t=>t.id===tx.id?{...t,text:e.target.value}:t)}))}
                          onBlur={()=>{
                            setEditingTextId(null);
                            // An empty box left behind is litter nobody can see
                            // to select and delete, so it removes itself.
                            setSiteLogistics(prev=>({...prev,
                              texts:(prev.texts||[]).filter(t=>t.id!==tx.id||t.text.trim()!=="")}));
                          }}
                          onKeyDown={e=>{
                            e.stopPropagation();
                            if(e.key==="Escape")(e.target as HTMLTextAreaElement).blur();
                          }}
                          placeholder="Note…"
                          style={{userSelect:"text",WebkitUserSelect:"text",
                            width:"100%",height:boxH,resize:"none",fontFamily:FONT,fontSize:size,
                            lineHeight:1.35,color,background:T.bgPanel,
                            border:`1px solid ${T.borderAccent}`,borderTop:"none",
                            borderRadius:"0 0 3px 3px",padding:`${padY}px ${padX}px`,
                            outline:"none",boxSizing:"border-box"}}/>
                      </div>
                    </foreignObject>
                  );
                }
                return(
                  <g key={tx.id} style={{cursor:tool==="select"?"move":"default"}}
                    transform={isRotated(tx.rotation)
                      ?`rotate(${normalizeAngle(tx.rotation||0)} ${tp.x} ${tp.y})`:undefined}
                    onMouseDown={(e)=>handleShapeMouseDown(e,"text",tx.id,{x:tx.x,y:tx.y})}
                    onContextMenu={(e)=>openElementMenu(e,"text",tx.id)}
                    onDoubleClick={(e)=>{e.stopPropagation();if(tool==="select")setEditingTextId(tx.id);}}>
                    <title>Double-click to edit</title>
                    {(tx.boxed!==false)&&<rect x={boxX} y={tp.y-padY-size*0.95} width={wide+padX*2} height={tall+padY*2}
                      rx={3} fill={T.bgPanel} fillOpacity={0.88} stroke={isSelected?T.borderAccent:T.borderSecondary}
                      strokeWidth={isSelected?1.5:1}/>}
                    {isSelected&&tx.boxed===false&&<rect x={boxX} y={tp.y-padY-size*0.95} width={wide+padX*2} height={tall+padY*2}
                      rx={3} fill="none" stroke={T.borderAccent} strokeWidth={1} strokeDasharray="4,3"/>}
                    {lines.map((ln,i)=>(
                      <text key={i} x={tp.x} y={tp.y+i*size*1.35} textAnchor={anchor}
                        fontSize={size} fill={color}
                        style={{userSelect:"none",fontFamily:FONT,pointerEvents:"none"}}>{ln}</text>
                    ))}
                  </g>
                );
              })}

              {/* ── SHEET BLOCKS ──
                  The legend, the title block and the graphic scale, drawn on
                  the sheet rather than on a strip bolted underneath it.

                  A strip changed the proportions of the page. A CAD sheet is
                  a standard size — ARCH D, ARCH E — and a drawing plus a band
                  of extra height is none of them, so printing it to the size
                  it was drawn for had to shrink it and the scale was gone.
                  These sit inside the drawing's own border, go where they are
                  dragged, and leave the sheet exactly the shape it was. */}
              {(()=>{
                const blocks=siteLogistics.blocks||{};
                const ink=chromeInk;
                const where=(kind:BlockKind,hPx:number)=>{
                  const b=blocks[kind];
                  const home=blockHome(kind,hPx,H);
                  const at={x:b?.x??home.x,y:b?.y??home.y};
                  blockPosRef.current[kind]=at;
                  return {...at,k:b?.scale||1};
                };
                const panel=(x:number,y:number,w:number,h:number,kind:BlockKind)=>(
                  <rect x={x} y={y} width={w} height={h} rx={2}
                    fill={labelSurface} fillOpacity={.93}
                    stroke={ink} strokeOpacity={.4} strokeWidth={.8}
                    style={{cursor:tool==="select"?"move":"default"}}
                    onMouseDown={(e)=>handleBlockMouseDown(e,kind)}
                    onContextMenu={(e)=>openBlockMenu(e,kind)}>
                    <title>Drag to move · corners to resize · Alt-click to reach what is under it</title>
                  </rect>
                );

                /** The corners you pull to reshape a block. The same square
                 *  handles a selected shape has, so they read as handles
                 *  without anyone having to be told. */
                const grips=(x:number,y:number,w:number,h:number,kind:BlockKind)=>(
                  <g>
                    {([["nw",x,y],["ne",x+w,y],["sw",x,y+h],["se",x+w,y+h]] as const).map(([c,gx,gy])=>(
                      <g key={c} style={{cursor:c==="nw"||c==="se"?"nwse-resize":"nesw-resize"}}
                        onMouseDown={(e)=>handleBlockResizeMouseDown(e,kind,c)}>
                        <title>Drag to reshape — wider makes a legend shorter</title>
                        <circle cx={gx} cy={gy} r={7} fill="transparent"/>
                        <rect x={gx-3} y={gy-3} width={6} height={6} rx={1}
                          fill={handleFill} stroke={ink} strokeOpacity={.8} strokeWidth={1.4}/>
                      </g>
                    ))}
                  </g>
                );

                const out:React.ReactNode[]=[];

                // ── Legend: only what is actually on this drawing ──
                const rows=legendRows(siteLogistics as any);
                if(rows.length&&blocks.legend?.hidden!==true){
                  const k0=blocks.legend?.scale||1;
                  const L=legendLayout(rows.length,
                    blocks.legend?.w??LEGEND_COL_IN*IN*LEGEND_DEFAULT_COLS,k0,IN);
                  const pos=where("legend",L.h);
                  const p=px(pos);
                  const padX=.07*IN*pos.k, padY=.06*IN*pos.k;
                  out.push(
                    <g key="legend" data-block="legend">
                      {panel(p.x,p.y,L.w,L.h,"legend")}
                      <text x={p.x+padX} y={p.y+padY+SHEET_TYPE.head*PT*pos.k} fontSize={SHEET_TYPE.head*PT*pos.k}
                        fontWeight={700} fill={ink} fillOpacity={.7} letterSpacing=".1em"
                        style={{pointerEvents:"none",userSelect:"none",fontFamily:FONT}}>LEGEND</text>
                      {rows.map((row,i)=>{
                        const slot=legendSlot(i,L.perCol);
                        const cx=p.x+padX+slot.col*L.colW;
                        const cy=p.y+padY+.17*IN*pos.k+slot.row*L.rowH+L.rowH/2;
                        return(
                          <g key={i} style={{pointerEvents:"none"}}>
                            {row.kind==="line"?(
                              <line x1={cx} y1={cy} x2={cx+.17*IN*pos.k} y2={cy}
                                stroke={row.color} strokeWidth={1.2*PT*pos.k}
                                strokeDasharray={row.dash||undefined}/>
                            ):(
                              <>
                                <rect x={cx} y={cy-.047*IN*pos.k} width={.094*IN*pos.k} height={.094*IN*pos.k} rx={1}
                                  fill={row.color} fillOpacity={row.kind==="area"?.45:1}
                                  stroke={row.color} strokeWidth={.8}/>
                                {row.abbr&&(
                                  <text x={cx+.047*IN*pos.k} y={cy+SHEET_TYPE.swatch*PT*.36*pos.k} textAnchor="middle"
                                    fontSize={SHEET_TYPE.swatch*PT*pos.k} fontWeight={800} fill="#0f1117"
                                    style={{userSelect:"none",fontFamily:FONT}}>{row.abbr}</text>
                                )}
                              </>
                            )}
                            <text x={cx+.22*IN*pos.k} y={cy+SHEET_TYPE.label*PT*.36*pos.k} fontSize={SHEET_TYPE.label*PT*pos.k}
                              fill={ink} style={{userSelect:"none",fontFamily:FONT}}>{rowText(row)}</text>
                          </g>
                        );
                      })}
                      {selectedLogId===blockSelId("legend")&&grips(p.x,p.y,L.w,L.h,"legend")}
                    </g>
                  );
                }

                // ── Title block: who drew this, what it is, which issue ──
                const tb=siteLogisticsProp.titleBlock;
                const atIndex=Math.max(0,allPlans.findIndex(pl=>pl.id===current.id));
                const tbRows=blockRows({tb,project:projectName,address,
                  sheetTitle:[current.name,phaseRange(phaseOf(current))].filter(Boolean).join(" · "),
                  date:current.issueDate,scale:scaleTxt});
                const showCompany=!!tb?.company&&isShown(tb,"company");
                if((tbRows.length||showCompany)&&blocks.title?.hidden!==true){
                  const k=blocks.title?.scale||1;
                  const wBox=clampSize(blocks.title?.w,1.3*IN*k,3.6*IN*k,2.0*IN*k);
                  const padX=.08*IN*k, padY=.07*IN*k, lineH=SHEET_TYPE.value*PT*1.35*k;
                  const labelW=.58*IN*k, valueW=wBox-padX*2-labelW;
                  const body:React.ReactNode[]=[];
                  let cy=padY;

                  if(showCompany){
                    for(const ln of wrapText((tb?.company||"").toUpperCase(),wBox-padX*2,SHEET_TYPE.head*PT*k,2)){
                      body.push(<text key={`co${cy}`} x={padX} y={cy+SHEET_TYPE.head*PT*k} fontSize={SHEET_TYPE.head*PT*k}
                        fontWeight={700} fill={ink}
                        style={{userSelect:"none",fontFamily:FONT}}>{ln}</text>);
                      cy+=SHEET_TYPE.head*PT*1.25*k;
                    }
                    if(tb?.companyLine){
                      for(const ln of wrapText(tb.companyLine,wBox-padX*2,SHEET_TYPE.small*PT*k,2)){
                        body.push(<text key={`cl${cy}`} x={padX} y={cy+SHEET_TYPE.small*PT*k} fontSize={SHEET_TYPE.small*PT*k}
                          fill={ink} fillOpacity={.65}
                          style={{userSelect:"none",fontFamily:FONT}}>{ln}</text>);
                        cy+=SHEET_TYPE.small*PT*1.3*k;
                      }
                    }
                    cy+=.03*IN*k;
                    body.push(<line key="r1" x1={padX} y1={cy-.02*IN*k} x2={wBox-padX} y2={cy-.02*IN*k}
                      stroke={ink} strokeOpacity={.25} strokeWidth={.7}/>);
                  }

                  // Every value wraps to the column it is in. A long project
                  // name used to run out of the box and across the drawing.
                  tbRows.forEach((r,i)=>{
                    const lines=wrapText(r.value,valueW,SHEET_TYPE.value*PT*k,2);
                    body.push(<text key={`l${i}`} x={padX} y={cy+SHEET_TYPE.value*PT*k} fontSize={SHEET_TYPE.label*PT*k}
                      fontWeight={700} fill={ink} fillOpacity={.6}
                      style={{userSelect:"none",fontFamily:FONT}}>{r.label}</text>);
                    lines.forEach((ln,j)=>{
                      body.push(<text key={`v${i}-${j}`} x={padX+labelW} y={cy+SHEET_TYPE.value*PT*k+j*lineH}
                        fontSize={SHEET_TYPE.value*PT*k} fill={ink}
                        style={{userSelect:"none",fontFamily:FONT}}>{ln}</text>);
                    });
                    cy+=Math.max(1,lines.length)*lineH+.02*IN*k;
                  });

                  const hBox=cy+padY;
                  const pos=where("title",hBox);
                  const at=px(pos);
                  out.push(
                    <g key="title" data-block="title" transform={`translate(${at.x},${at.y})`}>
                      {panel(0,0,wBox,hBox,"title")}
                      <g style={{pointerEvents:"none"}}>{body}</g>
                      {selectedLogId===blockSelId("title")&&grips(0,0,wBox,hBox,"title")}
                    </g>
                  );
                }

                // ── Graphic scale: the only scale that survives a printer ──
                if(fpp&&blocks.scale?.hidden!==true){
                  const k=blocks.scale?.scale||1;
                  const bar=niceBar(fpp,clampSize(blocks.scale?.w,.7*IN*k,3*IN*k,1.7*IN*k));
                  if(bar){
                    // Two rows, chequered, with the first division cut into
                    // fine ticks: the bar every drawing office prints, and
                    // the reason is not decoration -- the chequer is what
                    // lets the eye find a division at printed size, and the
                    // fine end is what measures something shorter than one.
                    const cells=barCells(bar), ticks=barTicks(bar);
                    const rowH=.022*IN*k;
                    const numSize=SHEET_TYPE.small*PT*k;
                    const pos=where("scale",.3*IN*k);
                    const p=px(pos);
                    const padX=.1*IN*k, padY=.06*IN*k;
                    const wBox=bar.px+padX*2, hBox=padY*2+rowH*2+numSize*2.6;
                    const bx=p.x+padX, by=p.y+padY;
                    const numY=by+rowH*2+numSize*1.1;
                    out.push(
                      <g key="scale" data-block="scale">
                        {panel(p.x,p.y,wBox,hBox,"scale")}
                        <g style={{pointerEvents:"none"}}>
                          {cells.map((c,i)=>(
                            <g key={i}>
                              <rect x={bx+c.x0} y={by} width={c.x1-c.x0} height={rowH}
                                fill={i%2?labelSurface:ink}/>
                              <rect x={bx+c.x0} y={by+rowH} width={c.x1-c.x0} height={rowH}
                                fill={i%2?ink:labelSurface}/>
                            </g>
                          ))}
                          <rect x={bx} y={by} width={bar.px} height={rowH*2}
                            fill="none" stroke={ink} strokeWidth={.5}/>
                          {ticks.map((t,i)=>(
                            <text key={i} x={bx+t.px} y={numY} textAnchor="middle" fontSize={numSize}
                              fill={ink} style={{userSelect:"none",fontFamily:FONT}}>
                              {i===ticks.length-1?barLabel(bar):String(Math.round(t.feet))}
                            </text>
                          ))}
                          {scaleTxt&&(
                            <text x={bx} y={numY+numSize*1.4} fontSize={numSize}
                              fontWeight={700} fill={ink} fillOpacity={.7}
                              style={{userSelect:"none",fontFamily:FONT}}>SCALE: {scaleTxt}</text>
                          )}
                        </g>
                        {selectedLogId===blockSelId("scale")&&grips(p.x,p.y,wBox,hBox,"scale")}
                      </g>
                    );
                  }
                }

                return out;
              })()}

              {/* The padlock on whatever is selected and locked. Drawn in
                  one pass, outside any rotated group, so it reads upright
                  whatever the thing it belongs to is doing. */}
              {(()=>{
                if(!selectedLogId||tool!=="select")return null;
                const id=selectedLogId;
                const ic=siteLogistics.icons.find(i=>i.id===id&&i.locked);
                if(ic){
                  const foot=footprintPx(footprintFt(ic),fpp);
                  const p=px({x:ic.x,y:ic.y});
                  const reach=Math.hypot(foot?foot.w:28,foot?foot.h:28)/2;
                  return<LockBadge cx={p.x+reach+8} cy={p.y-reach-6} color={SITE_ICON_CONFIG[ic.type].color}/>;
                }
                const ci=siteLogistics.circles.find(c=>c.id===id&&c.locked);
                if(ci){
                  const p=px({x:ci.cx,y:ci.cy});
                  const r=ci.rPct!=null?ci.rPct/100*W:ci.r;
                  return<LockBadge cx={p.x+r*0.71+8} cy={p.y-r*0.71-6} color={ci.color}/>;
                }
                const tx=(siteLogistics.texts||[]).find(t=>t.id===id&&t.locked);
                if(tx){
                  const p=px({x:tx.x,y:tx.y});
                  return<LockBadge cx={p.x+10} cy={p.y-14} color={tx.color||T.borderAccent}/>;
                }
                const sh=siteLogistics.areas.find(a=>a.id===id&&a.locked)
                  ||siteLogistics.lines.find(l=>l.id===id&&l.locked);
                if(sh){
                  const pts=sh.points.map(px);
                  const top=Math.min(...pts.map(q=>q.y));
                  const right=Math.max(...pts.map(q=>q.x));
                  const col=(sh as any).fillColor||(sh as any).color
                    ||(sh as any).strokeColor||chromeInk;
                  return<LockBadge cx={right+9} cy={top-8} color={col}/>;
                }
                return null;
              })()}

              {/* Rotation grips for text, areas and lines. Shapes with
                  vertices have no angle of their own -- the turn is baked into
                  the points -- so the grip always reads upright on them. */}
              {(siteLogistics.texts||[]).map(tx=>{
                if(selectedLogId!==tx.id||tool!=="select"||tx.locked||editingTextId===tx.id)return null;
                const tp=px({x:tx.x,y:tx.y});
                const deg=normalizeAngle(tx.rotation||0);
                const stalk=handleAt({x:0,y:0},16,deg,1);
                return(
                  <RotateGrip key={`rot-${tx.id}`} cx={tp.x+stalk.x} cy={tp.y+stalk.y}
                    deg={deg} color={tx.color||T.borderAccent}
                    onDown={(e)=>handleRotateMouseDown(e,"text",tx.id,{x:tx.x,y:tx.y},deg)}/>
                );
              })}
              {siteLogistics.areas.map(ar=>{
                if(selectedLogId!==ar.id||tool!=="select"||ar.locked)return null;
                const c=centroid(ar.points); const cp=px(c);
                const top=Math.min(...ar.points.map(p=>px(p).y));
                return(
                  <RotateGrip key={`rot-${ar.id}`} cx={cp.x} cy={top-6}
                    deg={normalizeAngle(ar.rotation||0)}
                    color={ar.fillColor||ar.color}
                    onDown={(e)=>handleRotateMouseDown(e,"area",ar.id,c,ar.rotation||0,ar.points)}/>
                );
              })}
              {siteLogistics.lines.map(ln=>{
                if(selectedLogId!==ln.id||tool!=="select"||ln.locked)return null;
                const c=centroid(ln.points); const cp=px(c);
                const top=Math.min(...ln.points.map(p=>px(p).y));
                return(
                  <RotateGrip key={`rot-${ln.id}`} cx={cp.x} cy={top-6}
                    deg={normalizeAngle(ln.rotation||0)}
                    color={ln.strokeColor||SITE_LINE_CONFIG[ln.lineType].color}
                    onDown={(e)=>handleRotateMouseDown(e,"line",ln.id,c,ln.rotation||0,ln.points)}/>
                );
              })}

              {/* Circle drawing preview */}
              {isCircleTool&&circleCenter&&circlePreviewR>0&&(()=>{
                const cpx=px(circleCenter);
                return(
                  <g style={{pointerEvents:"none"}}>
                    <circle cx={cpx.x} cy={cpx.y} r={circlePreviewR}
                      fill="#fc8181" fillOpacity={0.12}
                      stroke="#fc8181" strokeWidth={2} strokeDasharray="5,3" strokeOpacity={0.7}/>
                    <circle cx={cpx.x} cy={cpx.y} r={3} fill="#fc8181"/>
                    {fmtLen(circlePreviewR)&&<MapLabel x={cpx.x} y={cpx.y-circlePreviewR-TXT(8)}
                      text={`r=${fmtLen(circlePreviewR)}`} color="#fc8181" variant="quiet" size={TXT(8)} surface={labelSurface}/>}
                  </g>
                );
              })()}

              {/* Calibration line preview */}
              {/* A dimension being drawn, following the cursor.
                  After the first click there was nothing on screen at all,
                  so the only way to tell the tool had taken the point was to
                  click again and see what appeared. */}
              {isDimTool&&calibPoints.length>0&&(()=>{
                const a=px(calibPoints[0]);
                const b=areaPreview?px(areaPreview):a;
                const g=dimGeometry(a,b,0);
                const text=g?dimensionText(g.lengthPx,fpp):"";
                return(
                  <g style={{pointerEvents:"none"}}>
                    <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={chromeInk} strokeWidth={1.1} strokeDasharray="5,4" strokeOpacity={.85}/>
                    {g&&[g.da,g.db].map((pt,i)=>(
                      <line key={i} x1={pt.x-4} y1={pt.y+4} x2={pt.x+4} y2={pt.y-4}
                        transform={`rotate(${g.angleDeg} ${pt.x} ${pt.y})`}
                        stroke={chromeInk} strokeWidth={1.4} strokeOpacity={.85}/>
                    ))}
                    <circle cx={a.x} cy={a.y} r={3.5} fill={chromeInk} fillOpacity={.9}/>
                    {g&&text&&(
                      <g transform={`rotate(${g.angleDeg} ${g.mid.x} ${g.mid.y})`}>
                        <MapLabel x={g.mid.x} y={g.mid.y} dy={-TXT(4)} text={text}
                          color={chromeInk} variant="halo" size={TXT(10)} surface={labelSurface}/>
                      </g>
                    )}
                  </g>
                );
              })()}

              {isCalibTool&&calibPoints.length>0&&(()=>{
                const allPts=[...calibPoints,...(areaPreview?[areaPreview]:[])];
                const pts=ptsStr(allPts);
                return(
                  <g style={{pointerEvents:"none"}}>
                    <polyline points={pts} fill="none" stroke="#ecc94b"
                      strokeWidth={2} strokeDasharray="6,3" strokeOpacity={0.9}/>
                    {calibPoints.map((p,i)=>{const pp=px(p);return(
                      <circle key={i} cx={pp.x} cy={pp.y} r={5}
                        fill="#ecc94b" fillOpacity={0.9}/>
                    );})}
                  </g>
                );
              })()}
            </>);
            })();

return(
<div style={{display:"flex",flex:1,minHeight:0,overflow:"hidden",fontFamily:FONT}}>
  {/* LEFT PALETTE */}
  <div style={{width:180,background:T.bgPanel,borderRight:`1px solid ${T.borderPrimary}`,overflowY:"auto",flexShrink:0,padding:"6px 0"}}>
    {/* Tools (always visible) */}
    <div style={{padding:"4px 12px 4px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as const}}>Tools</div>
    {[
      {id:"select",label:"\u2196 Select / Move"},
      {id:"text",  label:"\u270e Text Box"},
      {id:"dimension",label:"\u2194 Dimension"},
    ].map(t=>(
      <div key={t.id} onClick={()=>{setLogisticsTool(t.id);setLineInProgress([]);}}
        style={{padding:"5px 12px",cursor:"pointer",fontSize:11,margin:"0 6px 1px",borderRadius:3,
          borderLeft:`2px solid ${logisticsTool===t.id?T.borderAccent:"transparent"}`,
          background:logisticsTool===t.id?T.bgAccentFaint:"none",
          color:logisticsTool===t.id?T.borderAccent:T.textSecondary}}>
        {t.label}
      </div>
    ))}

    {/* Draw Lines section */}
    <SectionHeader id="lines" label="Draw Lines"/>
    {panelOpen("lines")&&<>
      {(Object.keys(SITE_LINE_CONFIG) as SiteLineType[]).map(lt=>{
        const cfg=SITE_LINE_CONFIG[lt];
        const tid=`line:${lt}`;
        return(
          <div key={lt} onClick={()=>{setLogisticsTool(tid);setLineInProgress([]);setLogDrawStyle({});
            if(lt==="custom")setCustomName("");}}
            style={{padding:"4px 12px",cursor:"pointer",fontSize:10,margin:"0 6px",borderRadius:3,display:"flex",alignItems:"center",gap:6,
              borderLeft:`2px solid ${logisticsTool===tid?cfg.color:"transparent"}`,
              background:logisticsTool===tid?`${cfg.color}18`:"none",
              color:logisticsTool===tid?cfg.color:T.textSecondary}}>
            <span style={{display:"inline-block",width:22,height:2,background:cfg.color,borderRadius:3,flexShrink:0,
              borderBottom:`2px ${cfg.dash!=="none"?"dashed":"solid"} ${cfg.color}`}}/>
            {cfg.label}
          </div>
        );
      })}
      {/* The custom lines this plan already has, each one a thing you can
          draw another of. Without this, "custom" means one unnamed line type
          and every repeat has to be renamed by hand afterwards. */}
      {customNames.map(c=>{
        const col=c.color||SITE_LINE_CONFIG["custom"].color;
        const on=logisticsTool==="line:custom"&&customName===c.name;
        return(
          <div key={c.name} title={`Draw another ${c.name}`}
            onClick={()=>{
              setLogisticsTool("line:custom");setLineInProgress([]);
              setCustomName(c.name);
              // Drawn the way the first one was, or two lines that say the
              // same thing look like two different things.
              setLogDrawStyle({strokeColor:c.color,strokeDash:c.dash,strokeWidth:c.width});
            }}
            style={{padding:"4px 12px 4px 20px",cursor:"pointer",fontSize:10,margin:"0 6px",borderRadius:3,
              display:"flex",alignItems:"center",gap:6,
              borderLeft:`2px solid ${on?col:"transparent"}`,
              background:on?`${col}18`:"none",
              color:on?col:T.textSecondary,
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as const}}>
            <span style={{display:"inline-block",width:22,height:2,background:col,borderRadius:3,flexShrink:0,
              borderBottom:`2px ${c.dash?"dashed":"solid"} ${col}`}}/>
            {c.name}
          </div>
        );
      })}

      {isLineTool&&drawingLineType==="custom"&&(
        <div style={{margin:"4px 12px",display:"flex",alignItems:"center",gap:5}}>
          <span style={{fontSize:10,color:T.textWeak,flexShrink:0}}>Name</span>
          <input value={customName} placeholder="Fencing, sleeve route…"
            onChange={e=>setCustomName(e.target.value)}
            style={{flex:1,minWidth:0,fontSize:10,background:T.bgInput,
              border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,
              padding:"3px 5px",fontFamily:FONT,outline:"none"}}/>
        </div>
      )}

      {isLineTool&&lineInProgress.length>0&&(
        <div style={{margin:"4px 12px",padding:"4px 8px",background:`${T.borderAccent}22`,borderRadius:3,fontSize:10,color:T.borderAccent}}>
          {lineInProgress.length} pts {'\u2014'} dbl-click to finish
          <button onClick={()=>{setLineInProgress([]);setLinePreview(null);}}
            style={{marginLeft:6,background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,fontFamily:FONT}}>cancel</button>
        </div>
      )}
    </>}

    {/* Draw Areas section */}
    <SectionHeader id="areas" label="Draw Areas" def={false}/>
    {panelOpen("areas",false)&&<>
      {(Object.entries(SITE_AREA_CONFIG)).map(([type,cfg])=>{
        const tid=`area:${type}`;
        return(
          <div key={type} onClick={()=>{setLogisticsTool(tid);setAreaInProgress([]);}}
            style={{padding:"4px 12px",cursor:"pointer",fontSize:10,margin:"1px 6px",borderRadius:3,display:"flex",alignItems:"center",gap:6,
              borderLeft:`2px solid ${logisticsTool===tid?cfg.color:"transparent"}`,
              background:logisticsTool===tid?`${cfg.color}18`:"none",
              color:logisticsTool===tid?cfg.color:T.textSecondary}}>
            <span style={{display:"inline-block",width:14,height:10,borderRadius:3,
              background:`${cfg.color}55`,border:`1.5px solid ${cfg.color}`,flexShrink:0}}/>
            {cfg.label}
          </div>
        );
      })}
      <div onClick={()=>{setLogisticsTool("circle");setCircleCenter(null);}}
        style={{padding:"4px 12px",cursor:"pointer",fontSize:10,margin:"1px 6px",borderRadius:3,display:"flex",alignItems:"center",gap:6,
          borderLeft:`2px solid ${logisticsTool==="circle"?"#fc8181":"transparent"}`,
          background:logisticsTool==="circle"?"#fc818118":"none",
          color:logisticsTool==="circle"?"#fc8181":T.textSecondary}}>
        <span style={{display:"inline-flex",width:14,height:14,borderRadius:"50%",
          background:"#fc818133",border:"1.5px solid #fc8181",flexShrink:0,alignItems:"center",justifyContent:"center"}}>
          <span style={{width:2,height:2,background:"#fc8181",borderRadius:"50%"}}/>
        </span>
        Radius / Circle
      </div>
      {isAreaTool&&areaInProgress.length>0&&(
        <div style={{margin:"4px 12px",padding:"4px 8px",background:`${T.borderAccent}22`,borderRadius:3,fontSize:10,color:T.borderAccent}}>
          {areaInProgress.length} pts {'\u2014'} dbl-click to close
          <button onClick={()=>{setAreaInProgress([]);setAreaPreview(null);}}
            style={{marginLeft:6,background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,fontFamily:FONT}}>cancel</button>
        </div>
      )}
      {isCircleTool&&(
        <div style={{margin:"4px 12px",padding:"4px 8px",background:`${T.borderAccent}22`,borderRadius:3,fontSize:10,color:T.borderAccent}}>
          {circleCenter?"Click edge for radius":"Click center point"}
        </div>
      )}
    </>}

    {/* Icon category sections */}
    {iconCategories.map(cat=>{
      const icons=Object.entries(SITE_ICON_CONFIG).filter(([,c])=>c.category===cat);
      const sectionId=`cat_${cat}`;
      return(
        <div key={cat}>
          <SectionHeader id={sectionId} label={cat} def={false}/>
          {panelOpen(sectionId,false)&&icons.map(([type,cfg])=>{
            const tid=`place:${type}`;
            return(
              <div key={type} onClick={()=>setLogisticsTool(tid)}
                style={{padding:"3px 12px",cursor:"pointer",fontSize:10,margin:"1px 6px",borderRadius:3,display:"flex",alignItems:"center",gap:7,
                  borderLeft:`2px solid ${logisticsTool===tid?cfg.color:"transparent"}`,
                  background:logisticsTool===tid?`${cfg.color}18`:"none",
                  color:logisticsTool===tid?cfg.color:T.textSecondary}}>
                <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:18,height:18,
                  borderRadius:3,background:cfg.color,color:"#0f1117",fontSize:10,fontWeight:800,flexShrink:0}}>
                  {cfg.abbr.slice(0,2)}
                </span>
                {cfg.label}
              </div>
            );
          })}
        </div>
      );
    })}
  </div>

  {/* MAIN CANVAS AREA */}
  <div style={{flex:1,display:"flex",flexDirection:"column" as const,overflow:"hidden"}}>
    {/* ── SHEET BAR ──
        One row, because the drawing is the point.

        Three rows of chrome used to sit above the sheet: a toolbar, the plan
        tabs and the phase dates. Most of what was in them is setup — the
        site drawing, the scale, the view — settled once per job and then read
        hundreds of times by nobody. What is used while drawing stays out
        here; the rest is one click away under Setup, and a plan's dates open
        from the tab they belong to. */}
    <div style={{padding:"5px 10px",background:T.bgPanel,borderBottom:`1px solid ${T.borderPrimary}`,
      display:"flex",alignItems:"center",gap:6,flexShrink:0,minHeight:30}}>

      {logisticsView==="plan"&&<>
        {/* The plan tabs, which are the navigation and the sequence at once. */}
        {allPlans.map((pl,i)=>{
          const on=pl.id===current.id;
          const renaming=renamingId===pl.id;
          const dropLeft=dragOverIdx===i&&dragIdx!==null&&dragIdx>i;
          const dropRight=dragOverIdx===i&&dragIdx!==null&&dragIdx<i;
          const range=phaseRange(phaseOf(pl));
          return (
            <div key={pl.id}
              draggable={!renaming}
              onDragStart={e=>{setDragIdx(i);e.dataTransfer.effectAllowed="move";
                // Firefox will not start a drag without data on the transfer.
                e.dataTransfer.setData("text/plain",pl.id);}}
              onDragOver={e=>{e.preventDefault();e.dataTransfer.dropEffect="move";setDragOverIdx(i);}}
              onDragEnd={()=>{setDragIdx(null);setDragOverIdx(null);}}
              onDrop={e=>{
                e.preventDefault();
                if(dragIdx!==null&&dragIdx!==i)mutatePlans(prev=>reorderPlans(prev,dragIdx,i));
                setDragIdx(null);setDragOverIdx(null);
              }}
              style={{display:"inline-flex",alignItems:"center",gap:4,borderRadius:3,
                background:on?T.borderAccent:"transparent",
                border:`1px solid ${on?T.borderAccent:T.borderSecondary}`,
                // The insertion point, so a drag shows where the tab lands.
                boxShadow:dropLeft?`-2px 0 0 ${T.borderAccent}`:dropRight?`2px 0 0 ${T.borderAccent}`:"none",
                opacity:dragIdx===i?.4:1,
                padding:"1px 3px 1px 8px",cursor:renaming?"text":"grab",flexShrink:0}}>
              {renaming?(
                <input autoFocus defaultValue={pl.name}
                  onBlur={e=>{const v=e.target.value.trim();if(v)mutatePlans(prev=>renamePlan(prev,pl.id,v));setRenamingId(null);}}
                  onKeyDown={e=>{
                    if(e.key==="Enter")(e.target as HTMLInputElement).blur();
                    if(e.key==="Escape"){setRenamingId(null);}
                  }}
                  style={{background:T.bgInput,border:`1px solid ${T.borderAccent}`,color:T.textSecondary,
                    borderRadius:3,padding:"1px 5px",fontSize:10,fontFamily:FONT,outline:"none",width:110}}/>
              ):(<>
                <span onClick={()=>goPlan(pl.id)} onDoubleClick={()=>setRenamingId(pl.id)}
                  title={`${pl.name}${phaseRange(phaseOf(pl),true)?` · ${phaseRange(phaseOf(pl),true)}`:""}`
                    +" — double-click to rename, drag to reorder"}
                  style={{fontSize:10,fontFamily:FONT,fontWeight:on?700:400,
                    color:on?T.textOnGold:T.textMuted,userSelect:"none",whiteSpace:"nowrap" as const,padding:"1px 0"}}>
                  {pl.name}
                </span>
                {/* The dates sit on the tab they describe and open the editor
                    there. A permanent row for two fields nobody touches after
                    the first week is a row of drawing given away. */}
                <button onClick={e=>{e.stopPropagation();goPlan(pl.id);openPop("phase",e);}}
                  title={range?`In effect ${phaseRange(phaseOf(pl),true)} — click to change`
                    :"Set when this plan is in effect"}
                  style={{background:"none",border:"none",cursor:"pointer",fontFamily:FONT,
                    fontSize:10,padding:"1px 3px",lineHeight:1.4,
                    color:on?T.textOnGold:T.textWeak,opacity:range?.85:.5}}>
                  {range||"🗓"}
                </button>
              </>)}
              {allPlans.length>1&&!renaming&&(
                <button onClick={e=>{e.stopPropagation();mutatePlans(prev=>removePlan(prev,pl.id));}}
                  title="Remove this plan"
                  style={{background:"none",border:"none",cursor:"pointer",lineHeight:1,padding:"0 3px",
                    fontSize:12,fontFamily:FONT,color:on?T.textOnGold:T.textWeak,opacity:.7}}>×</button>
              )}
            </div>
          );
        })}
        <button onClick={e=>openPop("add",e)} title="Add another plan"
          style={{background:"none",border:`1px dashed ${T.borderSecondary}`,color:T.textMuted,borderRadius:3,
            padding:"2px 8px",fontSize:11,fontFamily:FONT,cursor:"pointer",flexShrink:0}}>+</button>

        {/* Calibrating is a mode: you are clicking on the drawing and need to
            be told why, so it says so out here rather than behind a menu. */}
        {isCalibTool&&(
          <span style={{fontSize:10,color:"#ecc94b",display:"flex",alignItems:"center",gap:6,marginLeft:4}}>
            {calibAsk?"How far is that, really?"
              :calibPoints.length===0?"Click one end of a known distance"
              :"Now click the other end"}
            <button onClick={endCalibration}
              style={{background:"none",border:"none",color:"#a0aec0",cursor:"pointer",fontSize:10,fontFamily:FONT}}>cancel</button>
          </span>
        )}

        <div style={{flex:1,minWidth:8}}/>

        <span style={{fontSize:10,color:T.textWeak,whiteSpace:"nowrap" as const}}>
          {(()=>{
            const parts:string[]=[];
            const add=(n:number,one:string,many=one+"s")=>{if(n>0)parts.push(`${n} ${n===1?one:many}`);};
            add(siteLogistics.icons.length,"item");
            add(siteLogistics.lines.length,"line");
            add(siteLogistics.areas.length,"area");
            add(siteLogistics.circles.length,"circle");
            add((siteLogistics.texts||[]).length,"text","texts");
            return parts.length?parts.join(" · "):"Empty plan";
          })()}
        </span>

        {/* A switch, not a button: it reports a state rather than performing an
            action. Named areas keep their text regardless — this is about the
            typed things the legend already decodes. */}
        <button onClick={()=>setSiteLogisticsProp(prev=>({...prev,showLabels:!showLabels}))}
          role="switch" aria-checked={showLabels}
          title={showLabels?"Hide item and line names — the legend still decodes them":"Show item and line names on the plan"}
          style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",border:"none",
            cursor:"pointer",padding:"2px",fontFamily:FONT,flexShrink:0}}>
          <span style={{width:24,height:13,borderRadius:999,flexShrink:0,position:"relative" as const,
            background:showLabels?T.borderAccent:T.borderSecondary,transition:"background .15s"}}>
            <span style={{position:"absolute" as const,top:2,left:showLabels?13:2,width:9,height:9,
              borderRadius:999,background:showLabels?T.textOnGold:T.textMuted,transition:"left .15s"}}/>
          </span>
          <span style={{fontSize:10,color:showLabels?T.textSecondary:T.textMuted,whiteSpace:"nowrap" as const}}>Labels</span>
        </button>

        {allPlans.length>1?(
          <button onClick={e=>openPop("export",e)} style={tbBtnS} disabled={exporting}
            title="Save this sheet, or the whole set as one PDF">
            {exporting?"Exporting…":"Export ▾"}
          </button>
        ):(
          <button onClick={()=>{void handleExport();}} style={tbBtnS} disabled={exporting}
            title="Save this sheet as a PDF">
            {exporting?"Exporting…":"Export"}
          </button>
        )}
        <button onClick={()=>setLogisticsView("map")} style={tbBtnS}
          title="Draw over the aerial instead">🗺 Map</button>
        <button onClick={e=>openPop("setup",e)} style={tbBtnS}
          title="The site drawing, the scale and the view — set once, then out of the way">
          Setup ▾
        </button>
      </>}

      {logisticsView==="map"&&<>
        <button onClick={()=>setLogisticsView("plan")} style={tbBtnS} title="Back to the drawn sheet">📐 Sheet</button>
        <span style={{fontSize:10,color:T.textMuted,fontFamily:FONT,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:260}}>
          {mapLocLabel?<>📍 {mapLocLabel}</>:<span style={{opacity:.5}}>Enter address in Properties to locate the site</span>}
        </span>
        <div style={{flex:1}}/>
        <div style={{display:"flex",border:`1px solid ${T.borderSecondary}`,borderRadius:6,overflow:"hidden",flexShrink:0}}>
          {([["aerial","🛰 Aerial"],["street","🗺 Map"]] as const).map(([v,lbl])=>(
            <button key={v} onClick={()=>setMapViewMode(v as any)}
              style={{padding:"3px 10px",border:"none",cursor:"pointer",fontSize:10,fontFamily:FONT,
                fontWeight:mapViewMode===v?700:400,
                background:mapViewMode===v?T.borderAccent:"transparent",
                color:mapViewMode===v?"#fff":T.textSecondary}}>
              {lbl}
            </button>
          ))}
        </div>
        {mapLocked?(
          <button onClick={()=>setMapLocked(false)} style={{...tbBtnS,color:"#68d391",borderColor:"#68d391"}}>🔓 Unlock Map</button>
        ):(
          <button onClick={()=>setMapLocked(true)} style={{...tbBtnS}}>🔒 Lock &amp; Draw</button>
        )}
      </>}
    </div>

    {logisticsView==="plan"?(<>
    {/* ── DRAWING STYLE BAR (plan mode only) ── */}
    {isLineTool&&drawingLineType&&(()=>{
      const cfg=SITE_LINE_CONFIG[drawingLineType];
      const curColor=logDrawStyle.strokeColor||cfg.color;
      const curWidth=logDrawStyle.strokeWidth??cfg.width;
      const curDash=logDrawStyle.strokeDash!==undefined?logDrawStyle.strokeDash:(cfg.dash==="none"?"":cfg.dash);
      // Most line types default to a dash pattern of their own that is none of
      // the generic presets, so offer it as the first swatch. Without it the
      // control shows nothing selected until you change something.
      const typeDash=cfg.dash==="none"?"":cfg.dash;
      const styleOpts=[{dash:typeDash,label:`${cfg.label} default`},
        ...LINE_DASH_OPTIONS.filter(o=>o.dash!==typeDash)];
      return(
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"5px 14px",
          background:T.bgApp,borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0,flexWrap:"wrap" as const}}>
          <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as const}}>Draw Style</span>
          {/* Color */}
          <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
            <span style={{fontSize:10,color:T.textWeak}}>Color</span>
            <span style={{width:18,height:18,borderRadius:3,background:curColor,border:`2px solid ${curColor}`,flexShrink:0,
              boxShadow:"0 0 0 1px rgba(255,255,255,.15)",cursor:"pointer",position:"relative" as const}}>
              <input type="color" value={curColor}
                onChange={e=>setLogDrawStyle(p=>({...p,strokeColor:e.target.value}))}
                style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer",padding:0,border:"none"}}/>
            </span>
          </label>
          {/* Weight */}
          <label style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:10,color:T.textWeak}}>Weight</span>
            <input type="range" min={1} max={10} step={0.5} value={curWidth}
              onChange={e=>setLogDrawStyle(p=>({...p,strokeWidth:parseFloat(e.target.value)}))}
              className="oe-range" style={{width:64,["--oe-accent" as any]:curColor}}/>
            <span style={{fontSize:10,color:T.textWeak,minWidth:22,textAlign:"right" as const}}>{curWidth}px</span>
          </label>
          {/* Line style. Drawn rather than named: each swatch is the line you
              will actually get, in the current colour and weight, so the
              selected one doubles as the preview that used to sit alongside. */}
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:10,color:T.textWeak}}>Style</span>
            <DashSwatches options={styleOpts} value={curDash} color={curColor} width={curWidth}
              swatchW={38} T={T} onChange={d=>setLogDrawStyle(p=>({...p,strokeDash:d}))}/>
          </div>
          {/* Reset to type defaults */}
          {(logDrawStyle.strokeColor||logDrawStyle.strokeWidth!==undefined||logDrawStyle.strokeDash!==undefined)&&(
            <button onClick={()=>setLogDrawStyle({})}
              style={{fontSize:10,color:T.textWeak,background:"none",border:"none",cursor:"pointer",fontFamily:FONT}}>↺ Reset</button>
          )}
          <span style={{marginLeft:"auto",fontSize:10,color:T.textMuted,opacity:.7}}>
            Double-click to finish the line
          </span>
        </div>
      );
    })()}

    {/* CANVAS */}
    <div ref={planBoxRef}
      onPointerDown={onPlanPointerDown} onPointerMove={onPlanPointerMove}
      onAuxClick={e=>{if(e.button===1)e.preventDefault();}}
      style={{flex:1,minHeight:0,overflow:"hidden",background:T.bgApp,display:"flex",
        alignItems:"center",justifyContent:"center",
        padding:PLAN_PAD,
        // The bands sit inside the box, so the sheet is inset by them.
        paddingTop:rulers?PLAN_PAD+RULER_W:PLAN_PAD,
        paddingLeft:rulers?PLAN_PAD+RULER_W:PLAN_PAD,
        cursor:panRef.current?"grabbing":undefined,position:"relative"}}>
      {/* ---- IMPORTING ----
          What the upload is doing, while it does it. The sheet itself is the
          only other sign that anything happened, and it arrives last. */}
      {importing&&(
        <div style={{position:"absolute",inset:0,zIndex:8,display:"flex",
          alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
          <div role="status" aria-live="polite"
            style={{display:"flex",alignItems:"center",gap:10,fontFamily:FONT,
              background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:6,
              boxShadow:"0 8px 24px rgba(0,0,0,.45)",padding:"12px 16px"}}>
            <span className="oe-spin"/>
            <span style={{fontSize:12,color:T.textSecondary}}>{importing}</span>
          </div>
        </div>
      )}

      {offsetAsk&&(
        <OffsetPrompt T={T} name={offsetAsk.name} closed={offsetAsk.kind==="area"}
          value={offsetInput} side={offsetSide}
          onValue={setOffsetInput} onSide={setOffsetSide}
          onApply={applyOffset} onCancel={()=>{setOffsetAsk(null);setOffsetInput("");}}/>
      )}

      {nameAsk&&(
        <NamePrompt T={T} what="area" value={nameInput}
          onValue={setNameInput} onApply={applyName}
          onCancel={()=>{setNameAsk(null);setNameInput("");}}/>
      )}

      {/* The distance, asked for once both ends have been picked. */}
      {calibAsk&&(
        <div style={{position:"absolute",left:"50%",top:18,transform:"translateX(-50%)",zIndex:6,
          background:T.bgPanel,border:`1px solid ${T.borderAccent}`,borderRadius:6,
          boxShadow:"0 8px 24px rgba(0,0,0,.45)",padding:"10px 12px",fontFamily:FONT,
          display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,color:T.textSecondary,whiteSpace:"nowrap" as const}}>
            That distance is
          </span>
          <input autoFocus value={calibInput} placeholder={FEET_HINT}
            onChange={e=>setCalibInput(e.target.value)}
            onKeyDown={e=>{
              if(e.key==="Enter"){e.preventDefault();applyCalibration(calibInput);}
              if(e.key==="Escape"){e.preventDefault();endCalibration();}
            }}
            style={{width:150,fontSize:11,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
              color:T.textPrimary,borderRadius:3,padding:"4px 7px",fontFamily:FONT,outline:"none"}}/>
          <button onClick={()=>applyCalibration(calibInput)}
            style={{fontSize:11,padding:"4px 11px",background:T.borderAccent,border:"none",
              color:T.textOnGold,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
            Set scale
          </button>
          <button onClick={endCalibration}
            style={{fontSize:11,padding:"4px 8px",background:"none",border:"none",
              color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
        </div>
      )}

      {/* ── RULERS ──
          Down the edges of the box, counting the sheet's own inches from its
          corner. They follow the paper: zoom in and zero stays on the corner
          of the sheet rather than on the edge of the window. */}
      {rulers&&sheetRect&&sheetInches&&(()=>{
        const R=RULER_W;
        const ppiX=sheetRect.w/sheetInches.w;
        const ppiY=sheetRect.h/sheetInches.h;
        const ink=T.textMuted, faint=T.borderSecondary;
        const band:React.CSSProperties={position:"absolute",background:T.bgPanel,
          zIndex:4,pointerEvents:"none",fontFamily:FONT};
        return(
          <>
            {/* across the top */}
            <svg style={{...band,left:R,top:0,right:0,height:R}}
              width="100%" height={R}>
              <rect x={0} y={0} width="100%" height={R} fill={T.bgPanel}/>
              <line x1={0} y1={R-0.5} x2="100%" y2={R-0.5} stroke={faint} strokeWidth={1}/>
              {ticksFor(sheetInches.w,ppiX,sheetRect.x-R).map((t,i)=>(
                <g key={i}>
                  <line x1={t.px} y1={t.major?R-9:R-4} x2={t.px} y2={R}
                    stroke={ink} strokeOpacity={t.major?.85:.5} strokeWidth={0.8}/>
                  {t.major&&(
                    <text x={t.px+2} y={9} fontSize={10} fill={ink}
                      style={{userSelect:"none"}}>{tickLabel(t.inch)}</text>
                  )}
                </g>
              ))}
            </svg>

            {/* down the side */}
            <svg style={{...band,left:0,top:R,bottom:0,width:R}}
              width={R} height="100%">
              <rect x={0} y={0} width={R} height="100%" fill={T.bgPanel}/>
              <line x1={R-0.5} y1={0} x2={R-0.5} y2="100%" stroke={faint} strokeWidth={1}/>
              {ticksFor(sheetInches.h,ppiY,sheetRect.y-R).map((t,i)=>(
                <g key={i}>
                  <line x1={t.major?R-9:R-4} y1={t.px} x2={R} y2={t.px}
                    stroke={ink} strokeOpacity={t.major?.85:.5} strokeWidth={0.8}/>
                  {t.major&&(
                    <text x={9} y={t.px-3} fontSize={10} fill={ink}
                      transform={`rotate(-90 9 ${t.px-3})`}
                      style={{userSelect:"none"}}>{tickLabel(t.inch)}</text>
                  )}
                </g>
              ))}
            </svg>

            {/* the corner, which says what the sheet is */}
            <div style={{position:"absolute",left:0,top:0,width:R,height:R,zIndex:5,
              background:T.bgPanel,borderRight:`1px solid ${faint}`,
              borderBottom:`1px solid ${faint}`,pointerEvents:"none"}}/>
            <div style={{position:"absolute",left:R+6,top:R+6,zIndex:5,
              fontSize:10,color:T.textWeak,fontFamily:FONT,pointerEvents:"none",
              background:T.bgPanel,padding:"1px 6px",borderRadius:3,opacity:.9}}>
              {describeSheet(sheetInches.w,sheetInches.h)}
            </div>
          </>
        );
      })()}

      {/* Says where you are and takes you back. Zoom with no way out but the
          gesture that got you there is a trap for anyone on a trackpad. */}
      {!isFitted(view)&&(
        <button onClick={()=>setView(FIT)} title="Fit the whole sheet (double-click the wheel)"
          style={{position:"absolute",right:10,top:10,zIndex:5,
            background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:3,
            color:T.textSecondary,fontFamily:FONT,fontSize:10,padding:"3px 9px",
            cursor:"pointer",boxShadow:"0 1px 4px rgba(0,0,0,.18)"}}>
          {formatZoom(view)} {'·'} Fit
        </button>
      )}
      <div ref={planSheetRef} style={{position:"relative",display:"inline-block",maxWidth:"100%",maxHeight:"100%",
        border:`1px solid ${T.borderSecondary}`,borderRadius:6,overflow:"hidden",
        transform:toCss(view),transformOrigin:"0 0",
        willChange:gesturing?"transform":undefined,
        cursor:isPlaceTool?"crosshair":isLineTool?"crosshair":isAreaTool?"crosshair":isCircleTool?"crosshair":isCalibTool||isDimTool?"crosshair":"default"}}>
        {siteLogistics.imageDataUrl?(
          <img src={siteLogistics.imageDataUrl} alt="Site Plan"
            onLoad={e=>{const im=e.target as HTMLImageElement;
              setImgNaturalW(im.naturalWidth||0);setImgNaturalH(im.naturalHeight||0);
              // The sheet is up; whatever brought it here is done.
              setImporting(null);}}
            onError={()=>{
              // Only speak if this was something the user just handed over;
              // a stored plan that fails to decode is a different problem.
              if(importing)say("That drawing could not be displayed");
              setImporting(null);}}
            // Sized by the space it is actually in, not by a guess at the
            // chrome around it. "100vw - 220px" assumed the panels either side
            // came to 220px; they are wider, so the plan overflowed its box and
            // was clipped -- while the annotation layer, which is inset:0 on
            // that box, stayed the box's size. The drawing and the marks on it
            // were different widths, so every icon sat at the wrong point of
            // the plan and every area was measured against the wrong shape.
            style={{display:"block",
              maxWidth:planBox.w||undefined,maxHeight:planBox.h||undefined,
              userSelect:"none",pointerEvents:"none"}}
            draggable={false}/>
        ):(
          <div style={{width:"min(800px,calc(100vw - 220px))",height:"min(550px,calc(100vh - 160px))",background:T.bgPanel,display:"flex",flexDirection:"column" as const,alignItems:"center",justifyContent:"center",gap:12,color:T.textWeak}}>
            <div style={{fontSize:32}}>&#128506;</div>
            <div style={{fontSize:14}}>Upload a site plan to get started</div>
            <div style={{fontSize:11,color:T.textWeak,opacity:.6}}>PNG, JPG, or PDF {'\u2014'} or place items on the blank canvas</div>
            {/* The annotation layer is a later sibling laid over this whole
                card, so a click meant for the button was landing on the layer
                instead and the button did nothing -- while the same control
                in the Setup menu, which is outside that stack, worked fine.
                Only the button is lifted above the layer: the rest of the
                card stays under it, because drawing on the blank canvas is
                done through it. */}
            <label style={{...tbBtnS,cursor:"pointer",marginTop:8,fontSize:12,padding:"8px 16px",
              position:"relative" as const,zIndex:1}}>
              Upload Site Plan
              <input type="file" accept="image/*,.pdf" style={{display:"none"}} onChange={handleImageUpload}/>
            </label>
          </div>
        )}

        <svg ref={logSvgRef}
          style={{position:"absolute",inset:0,width:"100%",height:"100%",overflow:"visible",
            userSelect:"none" as const,WebkitUserSelect:"none" as const,}}
          onClick={handleSvgClick}
          onDoubleClick={handleSvgDblClick}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleMouseUp}>

          {svgInner}
        </svg>

      </div>
    </div>
    </>):(
      /* ── LIVE MAP + drawing overlay ── */
      <div style={{flex:1,position:"relative",overflow:"hidden"}}>
        <SiteMap
          address={address}
          geoQuery={geoQuery}
          viewMode={mapViewMode}
          locked={mapLocked}
          onScale={handleMapScale}
          T={T}
          onLocated={setMapLocLabel}
        />
        {/* SVG drawing overlay — always renders placed items; pointer events active only when locked */}
        <svg ref={logSvgRef}
          style={{
            position:"absolute",inset:0,width:"100%",height:"100%",
            overflow:"visible",userSelect:"none" as const,WebkitUserSelect:"none" as const,
            zIndex:450,          // above all Leaflet panes (tile=200, overlay=400) but below controls (800)
            pointerEvents:mapLocked?"all":"none",
            cursor:mapLocked?(isPlaceTool||isLineTool||isAreaTool||isCircleTool||isCalibTool||isDimTool?"crosshair":"default"):"default",
          }}
          onClick={mapLocked?handleSvgClick:undefined}
          onDoubleClick={mapLocked?handleSvgDblClick:undefined}
          onMouseMove={mapLocked?handleSvgMouseMove:undefined}
          onMouseUp={mapLocked?handleMouseUp:undefined}>
          {svgInner}
        </svg>
        {offsetAsk&&(
          <OffsetPrompt T={T} name={offsetAsk.name} closed={offsetAsk.kind==="area"}
            value={offsetInput} side={offsetSide}
            onValue={setOffsetInput} onSide={setOffsetSide}
            onApply={applyOffset} onCancel={()=>{setOffsetAsk(null);setOffsetInput("");}}/>
        )}

        {nameAsk&&(
          <NamePrompt T={T} what="area" value={nameInput}
            onValue={setNameInput} onApply={applyName}
            onCancel={()=>{setNameAsk(null);setNameInput("");}}/>
        )}

        {/* "Lock to draw" hint when unlocked */}
        {!mapLocked&&(
          <div style={{position:"absolute",bottom:16,left:"50%",transform:"translateX(-50%)",
            background:"rgba(15,17,23,.82)",border:`1px solid ${T.borderSecondary}`,borderRadius:6,
            padding:"7px 16px",fontSize:11,color:T.textMuted,fontFamily:FONT,
            pointerEvents:"none",whiteSpace:"nowrap"}}>
            Pan &amp; zoom to frame the site, then click 🔒 <strong>Lock &amp; Draw</strong> to start placing items
          </div>
        )}
      </div>
    )}
  </div>

  {/* RIGHT PANEL — Properties + Legend + Notes */}
  <div style={{width:210,background:T.bgPanel,borderLeft:`1px solid ${T.borderPrimary}`,overflowY:"auto",flexShrink:0,padding:"8px 0"}}>

    {/* ── PROPERTIES — shown when an object is selected ── */}
    {selectedLogId&&!isBlockSel(selectedLogId)&&tool==="select"&&(()=>{
      const selLine=siteLogistics.lines.find(l=>l.id===selectedLogId);
      const selArea=siteLogistics.areas.find(a=>a.id===selectedLogId);
      const selCircle=siteLogistics.circles.find(c=>c.id===selectedLogId);
      const selIcon=siteLogistics.icons.find(i=>i.id===selectedLogId);
      const selText=(siteLogistics.texts||[]).find(t=>t.id===selectedLogId);
      const locked=lockedById(siteLogistics,selectedLogId);

      const inputS:React.CSSProperties={flex:1,fontSize:10,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
        color:T.textSecondary,borderRadius:3,padding:"3px 6px",fontFamily:FONT,outline:"none",minWidth:0};
      /**
       * The bearing, typed in.
       *
       * Every turnable thing gets the same control, because "what angle is
       * this" is the same question whether it is a trailer or a lay-down area,
       * and reading it off a grip you have to grab first is not an answer.
       */

      const colorBtnS=(col:string):React.CSSProperties=>({
        width:22,height:22,borderRadius:3,background:col,border:`2px solid ${col}`,flexShrink:0,
        boxShadow:"0 0 0 1px rgba(255,255,255,.15)",cursor:"pointer",position:"relative" as const,
      });

      return(
        <div style={{padding:"6px 10px 8px",borderBottom:`1px solid ${T.borderSecondary}`}}>
          <div style={{fontSize:10,fontWeight:700,color:T.borderAccent,letterSpacing:".1em",textTransform:"uppercase" as const,marginBottom:8}}>
            {selLine?"Line":selArea?"Area":selCircle?"Circle":selIcon?"Icon":selText?"Text":""} Properties
          </div>

          {/* Locked: the panel still reads, so you can check what the thing
              is, but nothing in it takes an edit. A disabled panel with no
              way out of it would be a trap, so the way out is right here. */}
          {locked&&(
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,padding:"4px 6px",
              borderRadius:3,background:T.bgInput,border:`1px solid ${T.borderSecondary}`}}>
              <span style={{fontSize:11}}>{"\u{1F512}"}</span>
              <span style={{fontSize:10,color:T.textMuted,flex:1}}>Locked</span>
              <button onClick={()=>{logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,selectedLogId,false));}}
                style={{fontSize:10,padding:"2px 8px",background:"none",border:`1px solid ${T.borderAccent}`,
                  color:T.borderAccent,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Unlock</button>
            </div>
          )}
          <div style={{opacity:locked?.45:1,pointerEvents:locked?"none" as const:undefined}}>


          {/* ── LINE properties ── */}
          {selLine&&(()=>{
            const cfg=SITE_LINE_CONFIG[selLine.lineType];
            const lineColor=selLine.strokeColor||cfg.color;
            const lineWidth=selLine.strokeWidth??cfg.width;
            const lineTypeDash=cfg.dash==="none"?"":cfg.dash;
            const lineDash=selLine.strokeDash!==undefined?selLine.strokeDash:lineTypeDash;
            const upd=(patch:Partial<SiteLine>)=>setSiteLogistics(prev=>({...prev,lines:prev.lines.map(l=>l.id===selLine.id?{...l,...patch}:l)}));
            return(<>
              <LabelRow label="Color" T={T}>
                <span style={colorBtnS(lineColor)}>
                  <input type="color" value={lineColor} onChange={e=>{logisticsSnapshot();upd({strokeColor:e.target.value});}}
                    style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer",padding:0,border:"none"}}/>
                </span>
                <span style={{fontSize:10,color:T.textMuted,flex:1}}>{lineColor}</span>
                {selLine.strokeColor&&<button onClick={()=>upd({strokeColor:undefined})} title="Reset to type default"
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,padding:"0 2px",fontFamily:FONT}}>↺</button>}
              </LabelRow>
              <LabelRow label="Weight" T={T}>
                <input type="range" min={0.5} max={10} step={0.5} value={lineWidth}
                  onChange={e=>{upd({strokeWidth:parseFloat(e.target.value)});}}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:lineColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:22,textAlign:"right" as const}}>{lineWidth}px</span>
              </LabelRow>
              <OffsetRow T={T} inputS={inputS} fpp={fpp}
                label="Clearance" hint="A band either side of the run"
                feet={selLine.offsetFt} hidden={!!selLine.offsetHidden}
                onFeet={v=>upd({offsetFt:v})}
                onToggle={()=>upd({offsetHidden:selLine.offsetHidden?undefined:true})}
                side={selLine.offsetSide||"both"}
                onCycleSide={()=>upd({offsetSide:
                  (selLine.offsetSide||"both")==="both"?"left"
                  :selLine.offsetSide==="left"?"right":undefined})}
                onSnapshot={logisticsSnapshot}/>
              <LabelRow label="Style" T={T}>
                <DashSwatches
                  options={[{dash:lineTypeDash,label:`${cfg.label} default`},
                            ...LINE_DASH_OPTIONS.filter(o=>o.dash!==lineTypeDash)]}
                  value={lineDash} color={lineColor} width={Math.min(lineWidth,6)}
                  swatchW={34} T={T}
                  onChange={d=>{logisticsSnapshot();upd({strokeDash:d});}}/>
              </LabelRow>
              {/* Turning a shape moves its vertices; the stored bearing is
                  only so the panel can show one and take one. */}
              {/* The delta is taken from the state as it is at the moment of
                  the update, not from this render's copy. Dragging the slider
                  fires faster than React re-renders, and two updates computing
                  their turn from the same stale bearing would each apply the
                  whole thing. */}
              <RotationRow T={T} inputS={inputS} onSnapshot={logisticsSnapshot} deg={selLine.rotation||0} tint={lineColor}
                onSet={d=>setSiteLogistics(prev=>({...prev,lines:prev.lines.map(sh=>{
                  if(sh.id!==selLine.id)return sh;
                  const from=sh.rotation||0;
                  return {...sh,points:rotatePoints(sh.points,d-from,logAspect()),rotation:d};
                })}))}/>
              <LabelRow label="Label" T={T}>
                <input value={selLine.label} onChange={e=>upd({label:e.target.value})}
                  onFocus={()=>logisticsSnapshot()} style={inputS}/>
              </LabelRow>
            </>);
          })()}

          {/* ── AREA properties ── */}
          {selArea&&(()=>{
            const areaColor=selArea.fillColor||selArea.color;
            const areaOpacity=selArea.fillOpacity??(SITE_AREA_CONFIG[selArea.areaType]||SITE_AREA_CONFIG["custom-area"]).fillOpacity;
            const areaStroke=selArea.strokeWidth??1.5;
            const upd=(patch:Partial<SiteArea>)=>setSiteLogistics(prev=>({...prev,areas:prev.areas.map(a=>a.id===selArea.id?{...a,...patch}:a)}));
            return(<>
              <LabelRow label="Color" T={T}>
                <span style={colorBtnS(areaColor)}>
                  <input type="color" value={areaColor} onChange={e=>{logisticsSnapshot();upd({fillColor:e.target.value,color:e.target.value});}}
                    style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer",padding:0,border:"none"}}/>
                </span>
                <span style={{fontSize:10,color:T.textMuted,flex:1}}>{areaColor}</span>
              </LabelRow>
              <LabelRow label="Fill %" T={T}>
                <input type="range" min={0} max={1} step={0.05} value={areaOpacity}
                  onChange={e=>{upd({fillOpacity:parseFloat(e.target.value)});}}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:areaColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:26,textAlign:"right" as const}}>{Math.round(areaOpacity*100)}%</span>
              </LabelRow>
              <LabelRow label="Border" T={T}>
                <input type="range" min={0.5} max={6} step={0.5} value={areaStroke}
                  onChange={e=>{upd({strokeWidth:parseFloat(e.target.value)});}}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:areaColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:22,textAlign:"right" as const}}>{areaStroke}px</span>
              </LabelRow>
              {/* Turning a shape moves its vertices; the stored bearing is
                  only so the panel can show one and take one. */}
              {/* The delta is taken from the state as it is at the moment of
                  the update, not from this render's copy. Dragging the slider
                  fires faster than React re-renders, and two updates computing
                  their turn from the same stale bearing would each apply the
                  whole thing. */}
              <RotationRow T={T} inputS={inputS} onSnapshot={logisticsSnapshot} deg={selArea.rotation||0} tint={areaColor}
                onSet={d=>setSiteLogistics(prev=>({...prev,areas:prev.areas.map(sh=>{
                  if(sh.id!==selArea.id)return sh;
                  const from=sh.rotation||0;
                  return {...sh,points:rotatePoints(sh.points,d-from,logAspect()),rotation:d};
                })}))}/>
              <LabelRow label="Label" T={T}>
                <input value={selArea.label} onChange={e=>upd({label:e.target.value})}
                  onFocus={()=>logisticsSnapshot()} style={inputS}/>
              </LabelRow>
            </>);
          })()}


          {/* ── CIRCLE properties ── */}
          {selCircle&&(()=>{
            const circColor=selCircle.color;
            const circStroke=selCircle.strokeWidth??1.5;
            const circDash=selCircle.strokeDash??"";
            const upd=(patch:Partial<SiteCircle>)=>setSiteLogistics(prev=>({...prev,circles:prev.circles.map(c=>c.id===selCircle.id?{...c,...patch}:c)}));
            return(<>
              <LabelRow label="Color" T={T}>
                <span style={colorBtnS(circColor)}>
                  <input type="color" value={circColor} onChange={e=>{logisticsSnapshot();upd({color:e.target.value});}}
                    style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer",padding:0,border:"none"}}/>
                </span>
                <span style={{fontSize:10,color:T.textMuted,flex:1}}>{circColor}</span>
              </LabelRow>
              <LabelRow label="Border" T={T}>
                <input type="range" min={0.5} max={8} step={0.5} value={circStroke}
                  onChange={e=>{upd({strokeWidth:parseFloat(e.target.value)});}}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:circColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:22,textAlign:"right" as const}}>{circStroke}px</span>
              </LabelRow>
              <LabelRow label="Style" T={T}>
                <DashSwatches options={LINE_DASH_OPTIONS} value={circDash}
                  color={circColor} width={Math.min(circStroke,6)} swatchW={34} T={T}
                  onChange={d=>{logisticsSnapshot();upd({strokeDash:d});}}/>
              </LabelRow>
              <LabelRow label="Label" T={T}>
                <input value={selCircle.label} onChange={e=>upd({label:e.target.value})}
                  onFocus={()=>logisticsSnapshot()} style={inputS}/>
              </LabelRow>
            </>);
          })()}

          {/* ── ICON properties ── */}
          {selIcon&&(()=>{
            const ft=footprintFt(selIcon);
            const presets=SIZE_PRESETS[selIcon.type];
            // The same defaults the drawing uses: a sized footprint is
            // translucent with a hairline edge, a bare symbol is nearly solid.
            const hasFoot=!!fpp;
            const iconColor=selIcon.color||SITE_ICON_CONFIG[selIcon.type].color;
            // Colour belongs to the kind of thing, not to the one you clicked.
            // Two dumpsters in different colours read as two different things
            // on a sheet whose key has one row for dumpsters -- so the colour
            // control moves all of them, and the key stays true.
            const sameType=siteLogistics.icons.filter(i=>i.type===selIcon.type);
            const recolour=(c:string|undefined)=>setSiteLogistics(prev=>({...prev,
              icons:prev.icons.map(i=>i.type===selIcon.type?{...i,color:c}:i)}));
            const iconFill=selIcon.fillOpacity??(hasFoot?0.45:0.92);
            const iconEdge=selIcon.strokeWidth??(hasFoot?1.5:1);
            const upd=(patch:Partial<SiteIcon>)=>setSiteLogistics(prev=>({...prev,
              icons:prev.icons.map(i=>i.id===selIcon.id?{...i,...patch}:i)}));
            const setDim=(dim:"w"|"h",raw:string)=>{
              const n=parseFloat(raw);
              if(!Number.isFinite(n))return;
              upd({sizeFt:clampFootprint({...ft,[dim]:n})});
            };
            const numS:React.CSSProperties={...inputS,flex:"none",width:52,textAlign:"right" as const};
            return(
            <>
              <LabelRow label="Label" T={T}>
                <input value={selIcon.label}
                  onChange={e=>upd({label:e.target.value})}
                  onFocus={()=>logisticsSnapshot()}
                  style={inputS}/>
              </LabelRow>
              {/* How it is drawn.
                  A footprint is translucent so the survey under it can still
                  be read, which on a busy sheet can leave it too faint to
                  find. Which way that should go is a judgement about this
                  drawing, so it is made here rather than decided for everyone. */}
              <LabelRow label="Color" T={T}>
                <span style={colorBtnS(iconColor)}
                  title={`Every ${SITE_ICON_CONFIG[selIcon.type].label} on this plan`}>
                  <input type="color" value={iconColor}
                    onChange={e=>{logisticsSnapshot();recolour(e.target.value);}}
                    style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer",padding:0,border:"none"}}/>
                </span>
                <span style={{fontSize:10,color:T.textMuted,flex:1}}>{iconColor}</span>
                {selIcon.color&&<button onClick={()=>{logisticsSnapshot();recolour(undefined);}}
                  title="Back to the colour for this item type"
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,padding:"0 2px",fontFamily:FONT}}>&#8634;</button>}
              </LabelRow>
              {sameType.length>1&&(
                <div style={{display:"flex",alignItems:"center",gap:6,margin:"-4px 0 6px"}}>
                  <span style={{fontSize:10,color:T.textWeak,width:44,flexShrink:0}}/>
                  <span style={{fontSize:10,color:T.textWeak,opacity:.85}}>
                    Applies to all {sameType.length} on this plan
                  </span>
                </div>
              )}
              <LabelRow label="Fill %" T={T}>
                <input type="range" min={0} max={1} step={0.05} value={iconFill}
                  onChange={e=>upd({fillOpacity:parseFloat(e.target.value)})}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:iconColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:26,textAlign:"right" as const}}>{Math.round(iconFill*100)}%</span>
              </LabelRow>
              <LabelRow label="Border" T={T}>
                <input type="range" min={0} max={4} step={0.5} value={iconEdge}
                  onChange={e=>upd({strokeWidth:parseFloat(e.target.value)})}
                  onMouseDown={()=>logisticsSnapshot()}
                  className="oe-range" style={{flex:1,["--oe-accent" as any]:iconColor}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:22,textAlign:"right" as const}}>{iconEdge?`${iconEdge}px`:"none"}</span>
              </LabelRow>
              <OffsetRow T={T} inputS={inputS} fpp={fpp}
                label="Radius" hint="Swing, reach, spacing, or the longest run from here"
                feet={selIcon.offsetFt} hidden={!!selIcon.offsetHidden}
                onFeet={v=>upd({offsetFt:v})}
                onToggle={()=>upd({offsetHidden:selIcon.offsetHidden?undefined:true})}
                onSnapshot={logisticsSnapshot}/>
              <RotationRow T={T} inputS={inputS} onSnapshot={logisticsSnapshot} deg={selIcon.rotation||0} tint={iconColor}
                onSet={d=>upd({rotation:d})}/>

              {/* Size is in feet because that is what the person knows. Without
                  a scale there is nothing to convert through, so the control
                  says so and points at the fix rather than taking a number it
                  cannot honour. */}
              {fpp?(
                <>
                  <LabelRow label="Size" T={T}>
                    <input type="number" min={MIN_FT} max={MAX_FT} step={1} value={+ft.w.toFixed(2)}
                      onChange={e=>setDim("w",e.target.value)} onFocus={()=>logisticsSnapshot()}
                      style={numS} title="Across, in feet"/>
                    <span style={{fontSize:10,color:T.textWeak}}>&times;</span>
                    <input type="number" min={MIN_FT} max={MAX_FT} step={1} value={+ft.h.toFixed(2)}
                      onChange={e=>setDim("h",e.target.value)} onFocus={()=>logisticsSnapshot()}
                      style={numS} title="Down, in feet"/>
                    <span style={{fontSize:10,color:T.textWeak}}>ft</span>
                  </LabelRow>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                    <span style={{fontSize:10,color:T.textWeak,width:44,flexShrink:0,textAlign:"right" as const}}/>
                    <span style={{fontSize:10,color:T.textMuted}}>
                      {describeFootprint(ft)} &middot; {Math.round(ft.w*ft.h).toLocaleString()} sf
                    </span>
                    {selIcon.sizeFt&&<button onClick={()=>{logisticsSnapshot();upd({sizeFt:undefined});}}
                      title="Back to the standard size for this item"
                      style={{marginLeft:"auto",background:"none",border:"none",color:T.textWeak,
                        cursor:"pointer",fontSize:10,fontFamily:FONT,padding:"0 2px"}}>&#8634; Standard</button>}
                  </div>
                  {presets&&(
                    <div style={{display:"flex",flexWrap:"wrap" as const,gap:3,margin:"0 0 6px 50px"}}>
                      {presets.map(ps=>{
                        const on=matchesPreset(ft,ps);
                        return(
                          <button key={ps.label}
                            onClick={()=>{logisticsSnapshot();upd({sizeFt:{w:ps.w,h:ps.h}});}}
                            title={`${ps.w}' × ${ps.h}'`}
                            style={{padding:"2px 7px",fontSize:10,fontFamily:FONT,cursor:"pointer",
                              borderRadius:3,whiteSpace:"nowrap" as const,
                              background:on?T.borderAccent:"transparent",
                              color:on?T.textOnGold:T.textMuted,
                              border:`1px solid ${on?T.borderAccent:T.borderSecondary}`}}>
                            {ps.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ):(
                <div style={{margin:"0 0 6px 50px",fontSize:10,color:T.textWeak,lineHeight:1.5,opacity:.85}}>
                  Set a drawing scale to size items in feet.
                </div>
              )}
            </>
            );
          })()}

          {/* ── TEXT properties ── */}
          {selText&&(()=>{
            const upd=(patch:Partial<SiteText>)=>setSiteLogistics(prev=>({...prev,
              texts:(prev.texts||[]).map(t=>t.id===selText.id?{...t,...patch}:t)}));
            const size=selText.size??13;
            return(<>
              <LabelRow label="Text" T={T}>
                <textarea value={selText.text} rows={3}
                  onChange={e=>upd({text:e.target.value})}
                  onFocus={()=>logisticsSnapshot()}
                  style={{...inputS,resize:"vertical" as const,lineHeight:1.4}}/>
              </LabelRow>
              <LabelRow label="Size" T={T}>
                <input type="range" min={9} max={28} step={1} value={size}
                  onChange={e=>upd({size:+e.target.value})}
                  onMouseDown={()=>logisticsSnapshot()} style={{flex:1}}/>
                <span style={{fontSize:10,color:T.textMuted,minWidth:26,textAlign:"right" as const}}>{size}px</span>
              </LabelRow>
              <LabelRow label="Color" T={T}>
                <input type="color" value={selText.color||"#e2e4ea"}
                  onChange={e=>upd({color:e.target.value})}
                  onFocus={()=>logisticsSnapshot()} style={colorBtnS(selText.color||"#e2e4ea")}/>
                <span style={{fontSize:10,color:T.textMuted,flex:1}}>{selText.color||"default"}</span>
              </LabelRow>
              <RotationRow T={T} inputS={inputS} onSnapshot={logisticsSnapshot} deg={selText.rotation||0} tint={selText.color||T.borderAccent}
                onSet={d=>upd({rotation:d})}/>
              <LabelRow label="Align" T={T}>
                <div style={{display:"flex",border:`1px solid ${T.borderSecondary}`,borderRadius:3,overflow:"hidden"}}>
                  {(["left","center","right"] as const).map(a=>{
                    const on=(selText.align||"left")===a;
                    return(
                      <button key={a} onClick={()=>{logisticsSnapshot();upd({align:a});}}
                        style={{padding:"3px 9px",fontSize:10,fontFamily:FONT,cursor:"pointer",border:"none",
                          textTransform:"capitalize" as const,
                          background:on?T.borderAccent:"transparent",color:on?T.textOnGold:T.textMuted}}>{a}</button>
                    );
                  })}
                </div>
                <button onClick={()=>{logisticsSnapshot();upd({boxed:selText.boxed===false});}}
                  style={{marginLeft:"auto",padding:"3px 9px",fontSize:10,fontFamily:FONT,cursor:"pointer",borderRadius:3,
                    background:selText.boxed!==false?T.borderAccent:"transparent",
                    color:selText.boxed!==false?T.textOnGold:T.textMuted,
                    border:`1px solid ${selText.boxed!==false?T.borderAccent:T.borderSecondary}`}}>
                  Box
                </button>
              </LabelRow>
            </>);
          })()}

          {/* A note belongs to the thing it is about, and is never drawn.
              The plan gets exported and handed to owners and trades, so
              anything rendered on it is published; a note is the place to put
              what should not be. Free text, which is drawn, is the other tool
              and is meant to be read by everyone. */}
          {(selLine||selArea||selCircle||selIcon)&&(()=>{
            const id=selectedLogId!;
            const note=(selLine||selArea||selCircle||selIcon)!.note||"";
            const setNote=(v:string)=>setSiteLogistics(prev=>({...prev,
              lines:prev.lines.map(l=>l.id===id?{...l,note:v}:l),
              areas:prev.areas.map(a=>a.id===id?{...a,note:v}:a),
              circles:prev.circles.map(c=>c.id===id?{...c,note:v}:c),
              icons:prev.icons.map(i=>i.id===id?{...i,note:v}:i)}));
            return(
              <LabelRow label="Note" T={T}>
                <textarea value={note} rows={2} placeholder="Internal note…"
                  onChange={e=>setNote(e.target.value)} onFocus={()=>logisticsSnapshot()}
                  style={{...inputS,resize:"vertical" as const,lineHeight:1.4}}/>
              </LabelRow>
            );
          })()}

          </div>

          <div style={{marginTop:6,display:"flex",gap:4}}>
            <button disabled={locked} onClick={()=>{
              logisticsSnapshot();
              setSiteLogistics(prev=>({...prev,
                icons:prev.icons.filter(x=>x.id!==selectedLogId),
                lines:prev.lines.filter(x=>x.id!==selectedLogId),
                areas:prev.areas.filter(x=>x.id!==selectedLogId),
                circles:prev.circles.filter(x=>x.id!==selectedLogId),
                texts:(prev.texts||[]).filter(x=>x.id!==selectedLogId),
                dimensions:(prev.dimensions||[]).filter(x=>x.id!==selectedLogId),
              }));setSelectedLogId(null);
            }} style={{flex:1,fontSize:10,padding:"3px 0",background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,
              color:T.dangerText,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
              <Icon.X size={10}/> Delete
            </button>
            {!locked&&(
              <button onClick={()=>{logisticsSnapshot();setSiteLogistics(prev=>setLock(prev,selectedLogId,true));}}
                title="Hold this still — it will not move, reshape or delete until unlocked"
                style={{fontSize:10,padding:"3px 8px",background:"none",border:`1px solid ${T.borderSecondary}`,
                  color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
                {"\u{1F512}"} Lock
              </button>
            )}
            <button onClick={()=>setSelectedLogId(null)}
              style={{flex:1,fontSize:10,padding:"3px 0",background:"none",border:`1px solid ${T.borderSecondary}`,
                color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
              Deselect
            </button>
          </div>
        </div>
      );
    })()}

    {/* ── SHEET — the title block and what has changed since last time ──

        A logistics plan goes out: printed for the trailer wall, emailed to
        the owner, attached to a permit, handed to a sub. Without a block
        naming who drew it and a line saying what changed, the second issue
        is indistinguishable from the first and somebody builds off the sheet
        they happened to keep. */}
    {logisticsView==="plan"&&(()=>{
      const open=!logPanelCollapsed["sheet"];
      const tb=siteLogisticsProp.titleBlock||{};
      const setTb=(patch:Partial<TitleBlock>)=>setSiteLogisticsProp(prev=>({
        ...prev,titleBlock:{...(prev.titleBlock||{}),...patch}}));
      /** A whole block, for the switches: they drop a key rather than set one. */
      const setTbWhole=(next:TitleBlock)=>setSiteLogisticsProp(prev=>({...prev,titleBlock:next}));
      const setSheet=(patch:{issueDate?:string})=>
        mutatePlans(prev=>withPlan(prev,current.id,{...activePlan(prev),...patch} as any));
      const fieldS:React.CSSProperties={flex:1,minWidth:0,fontSize:10,background:T.bgInput,
        border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,
        padding:"3px 5px",fontFamily:FONT,outline:"none"};
      return(
        <div style={{borderBottom:`1px solid ${T.borderSecondary}`,paddingBottom:open?8:4}}>
          <button onClick={()=>setLogPanelCollapsed(p=>({...p,sheet:open}))}
            style={{display:"flex",alignItems:"center",gap:4,width:"100%",background:"none",
              border:"none",cursor:"pointer",padding:"6px 12px 4px",fontFamily:FONT,
              fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as const}}>
            <span style={{opacity:.7}}>{open?"▾":"▸"}</span>
            Sheet
          </button>

          {open&&(
            <div style={{padding:"0 12px"}}>
              {/* Who prepared it. One answer per job — it is the same company
                  on every sheet of the set. */}
              <SheetRow T={T} label="By">
                <input value={tb.company||""} placeholder="Your company"
                  onChange={e=>setTb({company:e.target.value})} style={fieldS}/>
                <ShowToggle T={T} on={isShown(tb,"company")} title="Print the company on the sheet"
                  onClick={()=>setTbWhole(toggleField(tb,"company"))}/>
              </SheetRow>
              {(tb.company||tb.companyLine)&&(
                <SheetRow T={T} label="">
                  <input value={tb.companyLine||""} placeholder="Address or phone"
                    onChange={e=>setTb({companyLine:e.target.value})} style={fieldS}/>
                </SheetRow>
              )}
              <SheetRow T={T} label="For">
                <input value={tb.client||""} placeholder="Owner or client"
                  onChange={e=>setTb({client:e.target.value})} style={fieldS}/>
                <ShowToggle T={T} on={isShown(tb,"client")} title="Print the client on the sheet"
                  onClick={()=>setTbWhole(toggleField(tb,"client"))}/>
              </SheetRow>
              <SheetRow T={T} label="Issued">
                <input type="date" value={current.issueDate||""}
                  onChange={e=>setSheet({issueDate:e.target.value})}
                  style={{...fieldS,colorScheme:T.bgInput==="#ffffff"?"light":"dark"}}/>
                <ShowToggle T={T} on={isShown(tb,"date")} title="Print the date on the sheet"
                  onClick={()=>setTbWhole(toggleField(tb,"date"))}/>
              </SheetRow>

              <div style={{height:1,background:T.borderSecondary,margin:"7px 0"}}/>

              {/* The lines the block takes from the project rather than from
                  this panel. There is nothing to type, only whether to print
                  it -- and the value is shown beside the switch so the choice
                  is made against what would actually appear. */}
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",
                textTransform:"uppercase" as const,marginBottom:4}}>From the project</div>
              {([["project",projectName],["address",address],
                 ["sheetTitle",[current.name,phaseRange(phaseOf(current))].filter(Boolean).join(" · ")],
                 ["scale",scaleTxt]] as Array<[BlockField,string]>).map(([key,value])=>{
                const on=isShown(tb,key);
                const label=BLOCK_FIELDS.find(f=>f.key===key)?.label||key;
                return(
                  <div key={key} style={{display:"flex",alignItems:"center",gap:5,marginBottom:4}}>
                    <ShowToggle T={T} on={on} title={`Print the ${label.toLowerCase()} on the sheet`}
                      onClick={()=>setTbWhole(toggleField(tb,key))}/>
                    <span style={{fontSize:10,color:T.textWeak,width:42,flexShrink:0}}>{label}</span>
                    <span style={{fontSize:10,color:on?T.textSecondary:T.textWeak,
                      opacity:on?1:.5,textDecoration:on?"none":"line-through",
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as const}}>
                      {value||"—"}
                    </span>
                  </div>
                );
              })}

            </div>
          )}
        </div>
      );
    })()}

    {/* ── LEGEND ── */}
    <div style={{padding:"6px 12px 4px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Legend</div>

    {/* Items placed */}
    {(()=>{
      const placed=siteLogistics.icons.reduce((acc,ic)=>{acc[ic.type]=(acc[ic.type]||0)+1;return acc;},{} as Record<string,number>);
      const types=Object.keys(placed) as SiteItemType[];
      if(types.length===0)return <div style={{padding:"4px 12px",fontSize:10,color:T.textWeak,fontStyle:"italic"}}>No items placed yet</div>;
      return types.map(t=>{
        const cfg=SITE_ICON_CONFIG[t];
        // The colour it is actually drawn in, as the line rows below already
        // do -- a key that disagrees with the sheet is worse than no key.
        const first=siteLogistics.icons.find(i=>i.type===t);
        const dispColor=first?.color||cfg.color;
        return(
          <div key={t} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 12px",fontSize:10,color:T.textSecondary}}>
            <span style={{width:12,height:12,borderRadius:3,background:dispColor,flexShrink:0}}/>
            {cfg.label}{placed[t]>1?` \u00d7${placed[t]}`:""}
          </div>
        );
      });
    })()}

    {siteLogistics.lines.length>0&&(
      <>
        <div style={{borderTop:`1px solid ${T.borderSecondary}`,margin:"8px 8px 4px"}}/>
        {/* The same rows as the key printed on the sheet, from the same
            function: a custom line named Fencing says Fencing here too, and
            the two keys cannot drift apart into saying different things
            about one drawing. */}
        {legendRows(siteLogistics as any).filter(r=>r.kind==="line").map((r,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 12px",fontSize:10,color:T.textSecondary}}>
            <span style={{width:18,height:2,borderBottom:`2px ${r.dash?"dashed":"solid"} ${r.color}`,flexShrink:0}}/>
            {rowText(r)}
          </div>
        ))}
      </>
    )}

    {siteLogistics.areas.length>0&&(
      <>
        <div style={{borderTop:`1px solid ${T.borderSecondary}`,margin:"8px 8px 4px"}}/>
        {/* Through legendRows, like the lines above: this panel used to tally
            areas by type and print the type's name, so an area called Crane
            Pad was listed here as "Custom Area" while the sheet's own key,
            two inches away, called it Crane Pad. */}
        {legendRows(siteLogistics as any).filter(r=>r.kind==="area").map((r,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 12px",fontSize:10,color:T.textSecondary}}>
            <span style={{width:12,height:10,borderRadius:3,background:`${r.color}55`,border:`1.5px solid ${r.color}`,flexShrink:0}}/>
            {rowText(r)}
          </div>
        ))}
      </>
    )}

    {siteLogistics.circles.length>0&&(
      <>
        <div style={{borderTop:`1px solid ${T.borderSecondary}`,margin:"8px 8px 4px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:7,padding:"3px 12px",fontSize:10,color:T.textSecondary}}>
          <span style={{width:12,height:12,borderRadius:"50%",background:"#fc818133",border:"1.5px solid #fc8181",flexShrink:0}}/>
          Circles {'\u00d7'}{siteLogistics.circles.length}
        </div>
      </>
    )}

    {fpp&&<div style={{padding:"4px 12px 0",fontSize:10,color:"#68d391"}}
      title={logisticsView==="map"?`The map's own scale — ${mapScaleNote(fpp)} on this screen`:undefined}>
      Scale: {logisticsView==="map"
        ?`from the map — ${mapScaleNote(fpp)}`
        :(siteLogistics.scale.preset==="custom"?"Calibrated":SCALE_PRESETS.find(p=>p.id===siteLogistics.scale.preset)?.label||"")}
    </div>}

    <div style={{borderTop:`1px solid ${T.borderSecondary}`,margin:"12px 8px 4px"}}/>
    <div style={{padding:"0 12px 6px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Notes</div>
    <textarea value={siteLogistics.notes}
      onChange={e=>setSiteLogistics(prev=>({...prev,notes:e.target.value}))}
      placeholder="Logistics notes, phasing, restrictions..."
      style={{width:"calc(100% - 24px)",margin:"0 12px",boxSizing:"border-box",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
        color:T.textPrimary,padding:"6px 8px",borderRadius:3,fontSize:10,fontFamily:FONT,
        resize:"vertical",minHeight:90,outline:"none"}}/>

    <div style={{padding:"8px 12px 0",fontSize:10,color:T.textWeak}}>
      Double-click any label to rename it
    </div>
  </div>

  {/* Right-click menu. Fixed to the viewport so it is never clipped by the
      drawing's own scroll box, and dismissed by the next click anywhere. */}
  {/* ── SHEET BAR POPOVERS ──
      What used to be two extra rows. Opened from the control it belongs to,
      anchored under it, and gone the moment you click away. */}
  {popover&&(()=>{
    const panelS:React.CSSProperties={position:"fixed",
      left:Math.max(8,Math.min(popover.x,window.innerWidth-282)),
      top:Math.min(popover.y,window.innerHeight-240),
      zIndex:900,width:270,padding:10,
      background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:6,
      boxShadow:"0 8px 24px rgba(0,0,0,.45)",fontFamily:FONT};
    const rowS:React.CSSProperties={display:"flex",alignItems:"center",gap:6,marginBottom:7};
    const headS:React.CSSProperties={fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",
      textTransform:"uppercase" as const,marginBottom:6};
    const itemS:React.CSSProperties={padding:"6px 8px",fontSize:11,cursor:"pointer",borderRadius:3,
      color:T.textSecondary,whiteSpace:"nowrap" as const};
    const fieldS:React.CSSProperties={fontSize:10,fontFamily:FONT,background:T.bgInput,
      border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,
      padding:"3px 5px",outline:"none"};
    const hover=(on:boolean)=>(e:React.MouseEvent<HTMLDivElement>)=>{
      (e.currentTarget as HTMLDivElement).style.background=on?T.bgAccentFaint:"none";};
    const item=(label:string,onClick:()=>void,danger?:boolean)=>(
      <div key={label} onMouseDown={()=>{onClick();setPopover(null);}}
        style={{...itemS,color:danger?T.dangerText:T.textSecondary}}
        onMouseEnter={hover(true)} onMouseLeave={hover(false)}>{label}</div>
    );

    return(
      <div style={panelS} onMouseDown={e=>e.stopPropagation()}>
        {popover.kind==="add"&&(<>
          {item("Duplicate this plan",()=>mutatePlans(prev=>duplicatePlan(prev,activePlan(prev).id,uid())))}
          {item("Blank plan, same site drawing",()=>mutatePlans(prev=>addPlan(prev,uid())))}
        </>)}

        {popover.kind==="export"&&(<>
          {item("This sheet…",()=>{void handleExport();})}
          {allPlans.length>1&&(
            item(`All ${allPlans.length} sheets as one PDF…`,()=>{void handleExportAll();})
          )}
          {allPlans.length>1&&(
            <div style={{fontSize:10,color:T.textWeak,padding:"2px 8px 0",lineHeight:1.5}}>
              One document, a sheet per plan, in tab order.
            </div>
          )}
        </>)}

        {/* When this plan is the plan. Dates are what turn a pile of drawings
            into a sequence: which one is current, what moves next, and when. */}
        {popover.kind==="phase"&&(()=>{
          const ph=phaseOf(current);
          const issue=phaseIssue(ph);
          const set=(next:{start?:string;end?:string})=>
            mutatePlans(prev=>setPhase(prev,current.id,next));
          const borrowable=(schedule?.phases||[]).filter(p=>p&&p.name);
          return(<>
            <div style={headS}>{current.name} — in effect</div>
            <div style={rowS}>
              <input type="date" value={ph.start||""} max={ph.end||undefined}
                onChange={e=>set({start:e.target.value,end:ph.end})}
                title="First day this plan describes the site"
                style={{...fieldS,flex:1,colorScheme:T.bgInput==="#ffffff"?"light":"dark"}}/>
              <span style={{fontSize:10,color:T.textWeak}}>→</span>
              <input type="date" value={ph.end||""} min={ph.start||undefined}
                onChange={e=>set({start:ph.start,end:e.target.value})}
                title="Last day this plan describes the site"
                style={{...fieldS,flex:1,colorScheme:T.bgInput==="#ffffff"?"light":"dark"}}/>
            </div>
            <div style={{...rowS,marginBottom:issue||phaseLength(ph)?7:0}}>
              {issue
                ?<span style={{fontSize:10,color:T.negative}}>{issue}</span>
                :<span style={{fontSize:10,color:T.textMuted}}>{phaseLength(ph)}</span>}
              <div style={{flex:1}}/>
              {(ph.start||ph.end)&&(
                <button onClick={()=>set({})} title="Clear the dates on this plan"
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",
                    fontSize:10,fontFamily:FONT,padding:"0 2px"}}>Clear</button>
              )}
            </div>
            {/* Borrowed from the gantt rather than typed in again, so the
                drawing and the schedule cannot drift apart silently. Copied,
                not linked: a logistics plan often runs a week either side of
                the bar it belongs to, and a link would make that correction
                impossible to express. */}
            {borrowable.length>0&&schedule?.projectStartDate&&(
              <select value="" title="Copy the dates from a schedule phase"
                onChange={e=>{
                  const found=borrowable.find(p=>p.id===e.target.value);
                  if(!found)return;
                  const span=weeksSpan(found);
                  const got=phaseFromSchedule(schedule.projectStartDate,span.startWeek,span.durationWeeks);
                  if(got)set(got); else say("The schedule has no start date yet");
                }}
                style={{...fieldS,width:"100%",cursor:"pointer"}}>
                <option value="">From a schedule phase…</option>
                {borrowable.map(p=>(
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
            {sequenceSummary(allPlans)&&(
              <div style={{fontSize:10,color:T.textWeak,marginTop:8,paddingTop:7,
                borderTop:`1px solid ${T.borderSecondary}`}}>
                {sequenceSummary(allPlans)}
              </div>
            )}
          </>);
        })()}

        {popover.kind==="setup"&&(<>
          <div style={headS}>Site drawing</div>
          <div style={rowS}>
            <label style={{...tbBtnS,cursor:"pointer",flex:1,textAlign:"center" as const}}>
              {siteLogistics.imageDataUrl?"Replace…":"Upload site plan…"}
              <input type="file" accept="image/*,.pdf" style={{display:"none"}}
                onChange={e=>{setPopover(null);handleImageUpload(e);}}/>
            </label>
            {siteLogistics.imageDataUrl&&(
              <button onClick={()=>{logisticsSnapshot();setSiteLogistics(prev=>({...prev,imageDataUrl:null}));setPopover(null);}}
                style={tbBtnS} title="Draw on a blank canvas instead">Remove</button>
            )}
          </div>

          {/* How big the app's own marks are drawn.
              Annotation is sized in points of the sheet so that it prints
              true, which on a D sheet of a four-acre site is honest and
              unreadable. A markup is written over a drawing to be seen at a
              glance, so how large it should be is a judgement about this
              sheet rather than a constant. */}
          <div style={headS}>Markup size</div>
          <div style={rowS}>
            <input type="range" min={0.5} max={4} step={0.25} value={markup}
              onChange={e=>setSiteLogistics(prev=>({...prev,markupScale:parseFloat(e.target.value)}))}
              onMouseDown={()=>logisticsSnapshot()}
              className="oe-range" style={{flex:1,["--oe-accent" as any]:T.borderAccent}}/>
            <span style={{fontSize:10,color:T.textMuted,minWidth:30,textAlign:"right" as const}}>
              {markup.toFixed(markup<1?2:1)}&times;
            </span>
            {markup!==1&&(
              <button onClick={()=>setSiteLogistics(prev=>({...prev,markupScale:undefined}))}
                title="Back to true paper size"
                style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",
                  fontSize:10,padding:"0 2px",fontFamily:FONT}}>&#8634;</button>
            )}
          </div>
          <div style={{fontSize:10,color:T.textWeak,opacity:.8,padding:"0 2px 7px",lineHeight:1.5}}>
            1&times; is true paper size {"—"} what prints. Larger reads on
            screen and on a big sheet; items too small to draw at their
            footprint become symbols, with the real footprint shown inside.
          </div>

          <div style={headS}>Scale</div>
          <div style={rowS}>
            <select value={siteLogistics.scale.preset}
              onChange={e=>{
                const p=SCALE_PRESETS.find(s=>s.id===e.target.value);
                if(p?.id==="custom"){setLogisticsTool("calibrate");setCalibPoints([]);setPopover(null);}
                // A named scale is a statement about paper, so it is resolved
                // against the sheet's own pixel width rather than the reader's
                // screen. Falls back to the canvas for a blank plan, which has
                // no sheet to measure.
                const feetPerInch=p?.feetPerPx?p.feetPerPx*96:null;
                // Measured against the sheet's own resolution: a scale of 1" = 20' is a
                // statement about paper, and a PDF brought in at 144 dpi is not 96.
                const perPct=presetToFeetPerPct(feetPerInch,imgNaturalW||liveCanvas.w,
                  imgNaturalW?sheetDpi:96);
                setSiteLogistics(prev=>({...prev,scale:{preset:e.target.value,
                  feetPerPct:perPct??(p?.id==="custom"?prev.scale.feetPerPct:null),feetPerPx:null}}));
              }}
              style={{...fieldS,flex:1,cursor:"pointer"}}>
              {SCALE_PRESETS.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            {siteLogistics.scale.preset==="custom"&&planScale.feetPerPct&&(
              <span style={{fontSize:10,color:"#68d391"}}>✓ calibrated</span>
            )}
          </div>

          {/* What the sheet carries. These are drawn on the drawing, not
              under it, so turning one off is the only way to get it off the
              page -- and a size, because an annotation that looks right on
              screen is enormous on a D-size print. */}
          <div style={headS}>On the sheet</div>
          {(["legend","title","scale"] as BlockKind[]).map(kind=>{
            const b=(siteLogistics.blocks||{})[kind];
            // Being switched on is not the same as having anything to draw.
            const missing=kind==="legend"&&legendRows(siteLogistics as any).length===0
              ?"nothing drawn yet"
              :kind==="scale"&&!fpp?"no scale set":null;
            const on=b?.hidden!==true&&!missing;
            const name=kind==="legend"?"Legend":kind==="title"?"Sheet block":"Scale bar";
            const set=(patch:{hidden?:boolean;scale?:number})=>{
              logisticsSnapshot();
              setSiteLogistics(prev=>({...prev,blocks:{...(prev.blocks||{}),
                [kind]:{...((prev.blocks||{})[kind]||{}),...patch}}}));
            };
            return(
              <div key={kind} style={{...rowS,marginBottom:5}}>
                <button onClick={()=>{if(!missing)set({hidden:on});}}
                  role="switch" aria-checked={on} disabled={!!missing}
                  title={missing
                    ?(kind==="legend"
                      ?"The legend lists what is on the drawing — it appears once something is"
                      :"Set the drawing's scale under Scale above, and the bar appears")
                    :on?`Take the ${name.toLowerCase()} off the sheet`:`Put the ${name.toLowerCase()} on the sheet`}
                  style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",
                    border:"none",cursor:"pointer",padding:0,fontFamily:FONT,flex:1}}>
                  <span style={{width:22,height:12,borderRadius:999,flexShrink:0,position:"relative" as const,
                    background:on?T.borderAccent:T.borderSecondary}}>
                    <span style={{position:"absolute" as const,top:2,left:on?12:2,width:8,height:8,
                      borderRadius:999,background:on?T.textOnGold:T.textMuted}}/>
                  </span>
                  <span style={{fontSize:10,color:on?T.textSecondary:T.textMuted}}>{name}</span>
                  {missing&&(
                    <span style={{fontSize:10,color:T.textWeak,opacity:.8}}>{'—'} {missing}</span>
                  )}
                </button>
                {!missing&&<input type="range" min={0.6} max={2} step={0.1} value={b?.scale||1}
                  disabled={!on} title={`How large the ${name.toLowerCase()} is drawn`}
                  onChange={e=>set({scale:parseFloat(e.target.value)})}
                  className="oe-range" style={{width:62,opacity:on?1:.4}}/>}
                {!missing&&<span style={{fontSize:10,color:T.textWeak,width:24,textAlign:"right" as const}}>
                  {Math.round((b?.scale||1)*100)}%
                </span>}
              </div>
            );
          })}

          <div style={headS}>View</div>
          <div style={{...rowS,marginBottom:7}}>
            <button onClick={()=>{toggleRulers();setPopover(null);}}
              role="switch" aria-checked={rulers}
              title="Show the sheet's own inches down the edges (Ctrl+R)"
              style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",
                border:"none",cursor:"pointer",padding:0,fontFamily:FONT,flex:1}}>
              <span style={{width:22,height:12,borderRadius:999,flexShrink:0,position:"relative" as const,
                background:rulers?T.borderAccent:T.borderSecondary}}>
                <span style={{position:"absolute" as const,top:2,left:rulers?12:2,width:8,height:8,
                  borderRadius:999,background:rulers?T.textOnGold:T.textMuted}}/>
              </span>
              <span style={{fontSize:10,color:rulers?T.textSecondary:T.textMuted}}>Rulers</span>
            </button>
            <span style={{fontSize:10,color:T.textWeak}}>Ctrl+R</span>
          </div>
          {sheetInches&&(
            <div style={{fontSize:10,color:T.textWeak,marginBottom:9,paddingLeft:2}}>
              Sheet {describeSheet(sheetInches.w,sheetInches.h)}
            </div>
          )}
          <div style={{display:"flex",border:`1px solid ${T.borderSecondary}`,borderRadius:3,
            overflow:"hidden",marginBottom:9}}>
            {([["plan","📐 Site Plan"],["map","🗺 Live Map"]] as const).map(([v,lbl])=>(
              <button key={v} onClick={()=>{setLogisticsView(v);setPopover(null);}}
                style={{flex:1,padding:"4px 8px",border:"none",cursor:"pointer",fontSize:10,fontFamily:FONT,
                  fontWeight:logisticsView===v?700:400,
                  background:logisticsView===v?T.borderAccent:"transparent",
                  color:logisticsView===v?"#fff":T.textSecondary}}>
                {lbl}
              </button>
            ))}
          </div>

          <div style={{borderTop:`1px solid ${T.borderSecondary}`,paddingTop:5,margin:"0 -8px"}}>
            {/* A plan with locks on it needs a way back that does not depend
                on remembering which ones they were. */}
            {countLocked(siteLogistics)>0&&(
              item(`Unlock all ${countLocked(siteLogistics)} locked`,()=>{
                const n=countLocked(siteLogistics);
                logisticsSnapshot();setSiteLogistics(prev=>unlockAll(prev));
                say(`Unlocked ${n} item${n===1?"":"s"}`,true);
              })
            )}
            {(siteLogistics.icons.length>0||siteLogistics.lines.length>0)&&(
              item("Clear this plan…",()=>{
                // Locked things survive the wipe — that is what locking the
                // survey layer is for — and the count says so rather than
                // leaving it to be noticed.
                const held=countLocked(siteLogistics);
                const ask=held
                  ?`Clear everything except the ${held} locked item${held===1?"":"s"}?`
                  :"Clear all placed items, lines, areas and circles?";
                if(window.confirm(ask)){
                  logisticsSnapshot();
                  setSiteLogistics(prev=>clearUnlocked(prev).sl);
                  if(held)say(`Cleared — ${held} locked item${held===1?"":"s"} kept`);
                }
              },true)
            )}
          </div>
        </>)}
      </div>
    );
  })()}

  {ctxMenu&&(
    <div style={{position:"fixed",left:Math.min(ctxMenu.x,window.innerWidth-190),
      top:Math.min(ctxMenu.y,window.innerHeight-40-ctxMenu.items.length*26),
      zIndex:900,minWidth:176,padding:"4px 0",
      background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:6,
      boxShadow:"0 8px 24px rgba(0,0,0,.45)",fontFamily:FONT}}
      onMouseDown={e=>e.stopPropagation()}
      onContextMenu={e=>e.preventDefault()}>
      {ctxMenu.items.map((it,i)=>(
        <div key={i}
          onMouseDown={()=>{if(it.disabled)return;it.onClick();closeCtxMenu();}}
          style={{padding:"6px 12px",fontSize:11,cursor:it.disabled?"default":"pointer",
            color:it.disabled?T.textWeak:it.danger?T.dangerText:T.textSecondary,
            opacity:it.disabled?.45:1,whiteSpace:"nowrap" as const}}
          onMouseEnter={e=>{if(!it.disabled)(e.currentTarget as HTMLDivElement).style.background=T.bgAccentFaint;}}
          onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.background="none";}}>
          {it.label}
        </div>
      ))}
    </div>
  )}
</div>
);

}
